import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Dropdown,
  Form,
  Modal,
} from 'react-bootstrap';
import {
  PencilSquare,
  Download,
  CloudSun,
  DistributeVertical,
  InfoCircle,
} from 'react-bootstrap-icons';
import JsPDF from 'jspdf';
import html2pdf from 'html2pdf.js';
import { toPng } from 'html-to-image';
import Pluralize from 'pluralize';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

import ValidatedForm from 'components/Form/Inputs';
import Loading from 'components/Loading';
import { formatCurrency } from 'Main/utils';

import html2canvas from 'html2canvas';

import {
  calculateLoan,
  calculateEscalation,
  calculateSolar,
  calculateSolarWithNoShading,
  calculateCosts,
  getProposalState,
  getRates,
} from '@aerialytic/canvas-library';

import { DesignContext, Canvas3D } from '@aerialytic/canvas-library';

import { ios } from 'Leads/Details/DesignTool/utils';
import { LeadContext } from 'Leads/Details/hooks/context';

import TogglePanels from './TogglePanels';
import Controls from './Controls';
import PaybackChart from './PaybackChart';
import SolarPowerChart from './SolarPowerChart';
// import ShadeReport from '../../ShadeReport';
import LoanReport from './LoanReport';
import {
  DiscountModal,
  AddersModal,
  SelectModal,
  EscalationModal,
  ShadingReportModal,
} from './Modals';
import getImages from './ChartsImage';
import { filterDictByKeys } from '@aerialytic/canvas-library';
import IFrameLogo from 'components/IFrameLogo';
import { getSnowLoss, convertStateToAbbr } from 'options';
import axios from 'axios';
import PortalRedirect from '../PortalRedirect';
import { isEmpty } from 'Leads/Details/DesignTool/utils';
import { SystemLosses } from '../../DesignTool/Menu/Shading';
import { client } from 'api/auth';
import { calculateNetMetering, calculateBilling } from './NetMetering';
import {
  calculateAnnualNetMeteringV2,
  AnnualNetMeteringTable,
} from './NetMetering/AnnualNetMetering';
import {
  CURRENT_PROPOSAL_VERSION,
  LEGACY_PROPOSAL_VERSION,
} from 'IFrame/postMessage';
import { MonthlyNetMeteringTable } from './NetMetering/MonthlyNetMetering';
import { calculateMonthlyNetMeteringV2 } from './NetMetering/MonthlyNetMetering';
import {
  calculateNetBillingV2,
  NetBillingTable,
} from './NetMetering/NetBilling';
import {
  calculateLegacySavingsV2,
  LegacySavingsTable,
} from './NetMetering/LegacySavings';
import {
  buildFixedInverterPanelCapacityNotification,
  FIXED_INVERTER_PANEL_CAPACITY_CALLBACK,
  countProductionPayloadPanels,
  FIXED_INVERTER_PANEL_CAPACITY_MESSAGE,
  getInverterGroup,
  resolveMicroInverterQuantity,
} from 'Leads/Details/utils/inverter';
import { waitForCanvas3DReady } from './capture-ready';
import {
  captureRequiredCanvasExportImage,
  captureRequiredExportPng,
} from './export-image';
import { loadPdfImage } from './pdf-image';
import {
  buildLegacyHourlyPanelTopology,
  buildLegacyNoShadingRuntimeSelection,
  buildPersistedLegacyProductionState,
  buildProposalProductionCallbackPayload,
  createLazyLegacyProductionSnapshotFlow,
  createPendingLegacyProductionHydration,
  getLegacyCanvasStringingViewProps,
  getLegacyHydrationPlan,
  getLegacyProductionResponseReason,
  getVerifiedLegacyProductionSnapshot,
  hasUsableLegacyPanelShading,
  isPersistedLegacyProposal,
  LEGACY_CANVAS_UNAVAILABLE_REASON,
  mergeLegacyRuntimeProductionSnapshot,
  normalizeLegacyPanelShadingOrder,
  normalizeLegacyRuntimeProductionSnapshot,
  normalizeLegacySelectedPanels,
  shouldDeferLegacyProductionHydration,
} from './legacy-production';
import {
  addToSelection,
  buildDesignDataWithLiveProduction,
  buildDesignsPayloadWithStringing,
  buildEmptyReportProductionState,
  buildProductionJson as buildSharedProductionJson,
  buildReportInverterAutoModePatch,
  buildReportInverterChangePatch,
  buildReportProductionCallback,
  buildReportProductionPersistence,
  buildRoofOrientationValues,
  buildShadingValuesPerRoof,
  buildSyntheticStringingFromSelectedPanels,
  buildStringingIndexMapsFromPanelData,
  clearReportPanelSelection,
  countSelectedPanels,
  countSelectedPanelFlags,
  countSelectedPanelIndices,
  createDebouncedTask,
  createInitialReportDomainState,
  createProductionRequestGuard,
  createProposalShadingReportSignature,
  getProductionReadiness,
  getAdditionalInverterId,
  getSelectedPanelBreakdown,
  isReusableShadingReportData,
  isUuid,
  mapReportSolarProductionResult,
  normalizeAdditionalInverter as normalizeSharedAdditionalInverter,
  removeFromSelection,
  resolveStringingSelectionIds,
  setAllReportPanelsSelection,
  suggestedNameForLead,
  syncReportSelectedPanelFrom2D,
  syncReportSelectedPanelFrom3D,
  toggleReportPanelSelection,
  toPersistedAdditionalInverters as toSharedPersistedAdditionalInverters,
  UNSAVED_CHANGES_GUARD_MESSAGE,
} from '@aerialytic/designer-states/core';
import { cloneProposalAs } from '@aerialytic/designer-states/host';

// Developer-only toggle for the verification table on the legacy
// (`!billing_type`) savings path. Net Billing, Monthly NM, and Annual NM
// tables are always shown — this flag only controls the LegacySavingsTable.
// Flip to `true` while debugging the no-billing-type calculation, then flip
// back to `false` before shipping.
const SHOW_LEGACY_SAVINGS_DEBUG_TABLE = false;

const SCALE = 0.688;

class Report extends React.Component {
  static contextType = LeadContext;

  constructor(props) {
    super(props);
    this.state = {
      ...createInitialReportDomainState(),
      modal: null,
      scale: SCALE,
      visibility: true,
      showOverlay: false,
      showStringing: false,
      proposalVersion: LEGACY_PROPOSAL_VERSION,
      escalation: 0.03,
      yearlySavings: [],
      paybackYear: 0,
      paybackMonth: 0,
      lifetimeSavings: [],
      systemCost: 0,
      adderCost: 0,
      adderNRCost: 0,
      hiddenAdderCost: 0,
      visibleAdderCost: 0,
      preRebateCost: 0,
      discountSystemCost: 0,
      totalCost: 0,
      discountAmount: 0,
      discountCap: 0,
      rebateAmount: 0,
      credits: [],
      loanSwitch: false,
      loan: {
        loaner: 'DEFAULT',
        total: 0,
        amount: 0,
        downPayment: 0,
        rebated: true,
        monthlyPayments: 0,
        interestRate: 5,
        years: 25,
        fees: 0,
        lifetimeSavings: [],
      },
      loanObj: null,
      exporting: false,
      shadingReportLoading: false,
      proposalWithShadings: true,
      viewerMode: false,
      portalLoading: false,
      showNetMeteringVerification: false,
      netMeteringCalculations: null,
      hourlyCalcIsRunning: false,
      productionDebounceTimer: null,
      proposalCanvasImagery: null,
      proposalDesignName: '',
      proposalDirty: false,
      switchConfirm: null,
    };

    this.proposalVersion = LEGACY_PROPOSAL_VERSION;
    this.productionRequestGuard = createProductionRequestGuard();
    this.schedulePanelSelectionChange = createDebouncedTask(() => {
      if (!this.isUnmounted) {
        this.setState({ productionDebounceTimer: null });
        this.handlePanelSelectionChange();
      }
    }, 800);
    this.isUnmounted = false;
    this.lastRoofIndexMapForPayload = null;
    this.proposalDesignId = null;
    this.legacyProductionHydrationError = null;
    this.loadLazyLegacyProductionSnapshot =
      createLazyLegacyProductionSnapshotFlow();
    this.pendingLegacyProductionHydration =
      createPendingLegacyProductionHydration();

    this.hasEmittedInitialShadingValuesPerArray = false;
    this.emittingInitialShadingValuesPerArray = false;
  }

  setStateAsync = (nextState) =>
    new Promise((resolve) => this.setState(nextState, resolve));

  setStateAsyncIfActive = (isActive, nextState) =>
    new Promise((resolve) => {
      if (typeof isActive === 'function' && !isActive()) {
        resolve(false);
        return;
      }

      this.setState(
        (prevState) => {
          if (typeof isActive === 'function' && !isActive()) return null;
          return typeof nextState === 'function'
            ? nextState(prevState)
            : nextState;
        },
        () =>
          resolve(
            typeof isActive !== 'function' || Boolean(isActive())
          )
      );
    });

  waitForNextPaint = () =>
    new Promise((resolve) => {
      if (typeof requestAnimationFrame !== 'function') {
        resolve();
        return;
      }
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

  getPersistedShadingReportData = () =>
    this.state.designData?.shading_report || null;

  getCurrentShadingReportData = () =>
    this.state.shadingReportData || this.getPersistedShadingReportData();

  hasReusableShadingReportData = (
    report = this.getCurrentShadingReportData()
  ) => isReusableShadingReportData(report, this.state);

  ensureShadingReportReadyForExport = async ({
    force = false,
    showModal = false,
  } = {}) => {
    const persistedReport = this.getPersistedShadingReportData();
    if (!force && this.hasReusableShadingReportData(persistedReport)) {
      if (this.state.shadingReportData !== persistedReport) {
        await this.setStateAsync({ shadingReportData: persistedReport });
      }
      return { report: persistedReport, persisted: true };
    }

    const currentReport = this.state.shadingReportData;
    if (!force && this.hasReusableShadingReportData(currentReport)) {
      return { report: currentReport, persisted: false };
    }

    await this.setStateAsync({ shadingReportLoading: true });
    try {
      const report = await this.handleShadingReportDownload(null, showModal);
      return { report, persisted: false };
    } finally {
      this.setState({ shadingReportLoading: false });
    }
  };

  saveShadingReportForExport = async (report) => {
    const { pid, API } = this.props;
    if (!report) return null;

    const designData = {
      ...(this.state.designData || {}),
      shading_report: report,
    };
    const response = await API.update(
      pid,
      this.withCurrentProposalVersion({ design_data: designData }),
      true
    );
    this.recordCurrentProposalVersion(response);
    await this.setStateAsync({ designData });
    return response;
  };

  prepareProposalForExport = async () => {
    const { report } = await this.ensureShadingReportReadyForExport();
    if (report && this.state.shadingReportData !== report) {
      await this.setStateAsync({ shadingReportData: report });
    }
    await this.updateReport();
    return report;
  };

  getProposalVersion = () =>
    this.proposalVersion ||
    this.state.proposalVersion ||
    LEGACY_PROPOSAL_VERSION;

  withCurrentProposalVersion = (data = {}) => ({
    ...data,
    version: CURRENT_PROPOSAL_VERSION,
  });

  recordCurrentProposalVersion = (response = null) => {
    const proposalVersion =
      response?.version || response?.data?.version || CURRENT_PROPOSAL_VERSION;
    this.proposalVersion = proposalVersion;
    if (this.state.proposalVersion !== proposalVersion) {
      this.setState({ proposalVersion });
    }
  };

  getPanelShadingRequestOptions = (options = {}) => ({
    ...options,
    ...(this.props?.iframe && options.waitForTextures === undefined
      ? { waitForTextures: false }
      : {}),
  });

  getPanelShadingData = async (options = {}) => {
    let panelShadings = Array.isArray(this.state.panelShadings)
      ? this.state.panelShadings
      : [];
    let monthlyPanelShadings =
      this.state.monthlyPanelShadings &&
        typeof this.state.monthlyPanelShadings === 'object'
        ? this.state.monthlyPanelShadings
        : {};

    if (!panelShadings.length) {
      const rawPanelShadings =
        (await this.canvas3DElement?.getAnualPanelsShadings?.(
          this.getPanelShadingRequestOptions(options)
        )) || [];
      const values = Array.isArray(rawPanelShadings)
        ? [...rawPanelShadings]
        : [];
      const lastValue = values[values.length - 1];
      const hasMonthlyData =
        lastValue && typeof lastValue === 'object' && !Array.isArray(lastValue);

      panelShadings = hasMonthlyData ? values.slice(0, -1) : values;
      monthlyPanelShadings = hasMonthlyData ? lastValue : {};
    }

    return {
      panelShadings: Array.isArray(panelShadings) ? panelShadings : [],
      monthlyPanelShadings:
        monthlyPanelShadings && typeof monthlyPanelShadings === 'object'
          ? monthlyPanelShadings
          : {},
    };
  };

  isIframeProposalAutoSaveEnabled = () => {
    if (!this.props?.iframe) return false;
    return this.props?.context?.autoSave !== false;
  };

  handleProposalBeforeUnload = (event) => {
    if (!this.state.proposalDirty) return;
    event.preventDefault();
    event.returnValue = '';
  };

  loadProposalScopeHeading = (proposal) => {
    const clean = (value) =>
      typeof value === 'string' ? value.trim().replace(/\/+$/, '') : '';
    const designId =
      clean(proposal?.design_id) ||
      clean(proposal?.design_data?.design_id) ||
      clean(proposal?.design_data?.canvas_id);
    const leadId = clean(this.props.lead?.uid) || clean(this.context?.uid);
    const endpoint = this.context?.newEndpoint?.('designer/canvas/by_lead');
    if (!designId || !leadId || typeof endpoint?.list !== 'function') return;
    endpoint
      .list(`?lead=${encodeURIComponent(leadId)}`)
      .then((data) => {
        if (this.isUnmounted) return;
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];
        const matched = items
          .flatMap((canvas) =>
            Array.isArray(canvas?.designs) ? canvas.designs : []
          )
          .find((row) => String(row?.id || '') === designId);
        this.setState({
          proposalDesignName: String(matched?.name || '').trim(),
        });
      })
      .catch(() => undefined);
  };

  markProposalDirty = () => {
    this.proposalDirtySeq = (this.proposalDirtySeq || 0) + 1;
    if (!this.state.proposalDirty) this.setState({ proposalDirty: true });
  };

  markProposalClean = () => {
    if (this.state.proposalDirty) this.setState({ proposalDirty: false });
  };

  getSelectedPanelCount = (selectedPanelsInput = null) => {
    const source =
      selectedPanelsInput && typeof selectedPanelsInput === 'object'
        ? selectedPanelsInput
        : this.state?.selectedPanels;
    return countSelectedPanelFlags(source);
  };

  getSelectedPanelBreakdown = (selectedPanelsInput = null) => {
    const source =
      selectedPanelsInput && typeof selectedPanelsInput === 'object'
        ? selectedPanelsInput
        : this.state?.selectedPanels;
    return getSelectedPanelBreakdown(source);
  };

  hasPortalFinancials = () => {
    const {
      yearlySavings,
      lifetimeSavings,
      totalCost,
      systemCost,
      annualUsage,
      paybackYear,
      paybackMonth,
    } = this.state;
    const hasNumber = (value) =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      Number.isFinite(Number(value));

    return (
      hasNumber(yearlySavings?.[0]) &&
      hasNumber(lifetimeSavings?.[24]) &&
      hasNumber(totalCost) &&
      hasNumber(systemCost) &&
      hasNumber(annualUsage) &&
      hasNumber(paybackYear) &&
      hasNumber(paybackMonth)
    );
  };

  handleBlockedPortalOpen = () => {
    if (this.props.displaySnack) {
      this.props.displaySnack({
        variant: 'warning',
        message:
          'Proposal pricing is still incomplete. Save or recalculate before opening the Customer Portal.',
      });
    }
  };

  handlePortalSaveError = () => {
    if (this.props.displaySnack) {
      this.props.displaySnack({
        variant: 'danger',
        message:
          'Proposal save failed. Please save again before opening the Customer Portal.',
      });
    }
  };

  bumpProductionRequestSeq = () => {
    const requestSeq = this.productionRequestGuard.bump();
    this.pendingLegacyProductionHydration?.clear();
    return requestSeq;
  };

  isLatestProductionRequest = (requestSeq) =>
    this.productionRequestGuard.isLatest(requestSeq);

  setStateIfLatest = (requestSeq, nextState, callback) => {
    if (this.isUnmounted) return;

    this.setState(
      (prevState) => {
        if (this.isUnmounted) return null;
        return this.productionRequestGuard.setStateIfLatest(
          requestSeq,
          prevState,
          nextState
        );
      },
      () => {
        if (
          this.isUnmounted ||
          !this.productionRequestGuard.isLatest(requestSeq) ||
          typeof callback !== 'function'
        ) {
          return;
        }
        callback();
      }
    );
  };

  normalizeAdditionalInverter = (entry) =>
    normalizeSharedAdditionalInverter(
      entry,
      this.props?.hardware?.inverters || []
    );

  toPersistedAdditionalInverters = (additionalInvertersInput = []) =>
    toSharedPersistedAdditionalInverters(
      additionalInvertersInput,
      this.props?.hardware?.inverters || []
    );

  emitShadingValuesPerArray = async (isActive = () => true) => {
    if (!this.updateCallback || !isActive()) return false;

    try {
      const panelMetrics = await this.calcPanelMetrics();
      if (!isActive()) return false;
      const yearly = panelMetrics?.panelMetrics;
      const monthly = panelMetrics?.monthlyPanelMetrics;

      if (!Array.isArray(yearly) || !monthly || typeof monthly !== 'object') {
        return false;
      }

      if (!isActive()) return false;
      this.updateCallback('ShadingValuesPerArray', { yearly, monthly });
      return true;
    } catch (err) {
      console.log(`error in calcPanelMetrics: ${err}`);
      return false;
    }
  };

  buildStringingIndexMapsFromCanvas = () =>
    buildStringingIndexMapsFromPanelData(this.canvas3DElement?.panelData);

  retryPendingLegacyProductionHydration = async () => {
    const pending = this.pendingLegacyProductionHydration.claim({
      isLegacyProposal: this.state.isLegacyProposal,
      canvasLoaded: this.canvas3DElement?.isLoaded === true,
      isActive: (context) =>
        !this.isUnmounted &&
        this.isLatestProductionRequest(context.requestSeq),
    });
    if (!pending) return false;

    const isActive = () =>
      !this.isUnmounted &&
      this.isLatestProductionRequest(pending.requestSeq);
    const stateApplied = await this.setStateAsyncIfActive(isActive, {
      pendingLegacyProductionHydration: false,
      productionIsCalculating: true,
    });
    if (!stateApplied) return false;

    try {
      const result = await this.runLegacyProductionLoad(pending);
      if (result.handled) {
        return true;
      }
      if (result.source === 'superseded' || !isActive()) return false;
      if (result.source === 'conflict') {
        this.legacyProductionHydrationError = result.reason;
      }
    } catch (error) {
      if (!isActive()) return false;
      this.legacyProductionHydrationError =
        this.getLegacyProductionFailureReason(error);
    }

    if (isActive()) {
      await this.setStateAsyncIfActive(isActive, {
        needsSolarProductionRecalc: true,
        productionIsCalculating: false,
        pendingLegacyProductionHydration: false,
      });
      if (!isActive()) return false;
      this.emitProductionError(
        'Production data is not available for this legacy proposal. Recalculate production to generate iframe results.',
        this.legacyProductionHydrationError
      );
    }
    return false;
  };

  handleCanvas3DLoaded = async () => {
    try {
      if (
        (!this.state.stringingRoofIndexMap ||
          !this.state.stringingSelectedPanelIndexMap) &&
        this.canvas3DElement?.panelData
      ) {
        const { roofIndexMap, selectedPanelIndexMap } =
          this.buildStringingIndexMapsFromCanvas();
        if (roofIndexMap && selectedPanelIndexMap) {
          this.setState({
            stringingRoofIndexMap: roofIndexMap,
            stringingSelectedPanelIndexMap: selectedPanelIndexMap,
          });
        }
      }

      const strings = this.state.stringingData;
      this.syncSelectedPanelFrom2D();

      const legacyHydrated =
        await this.retryPendingLegacyProductionHydration();
      if (legacyHydrated) return;

      const hasStrings = Array.isArray(strings) && strings.length > 0;
      if (!hasStrings) {
        // Emit shading metrics on initial load for iframe embeds once the canvas is ready.
        this.maybeEmitInitialShadingValuesPerArray();
        return;
      }

      const selectedPanelsSafe =
        this.state.selectedPanels && typeof this.state.selectedPanels === 'object'
          ? this.state.selectedPanels
          : {};
      const totalSelectedFromPersisted = Object.values(selectedPanelsSafe).reduce(
        (sum, arr) =>
          sum +
          (Array.isArray(arr)
            ? arr.reduce((s, v) => s + (v ? 1 : 0), 0)
            : 0),
        0
      );

      if (totalSelectedFromPersisted <= 0) {
        setTimeout(() => {
          this.retryHydrateSelectionFromStringing();
        }, 100);
        this.syncSelectedPanelFrom2D();
        // Don't emit until after selection is hydrated from stringing.
        return;
      }
      this.syncSelectedPanelFrom2D();

      // Emit shading metrics on initial load for iframe embeds once the canvas is ready.
      this.maybeEmitInitialShadingValuesPerArray();
    } catch (e) { }
  };

  maybeEmitInitialShadingValuesPerArray = async () => {
    try {
      if (!this.props.iframe) return;
      if (!this.updateCallback) return;
      if (this.hasEmittedInitialShadingValuesPerArray) return;
      if (this.emittingInitialShadingValuesPerArray) return;
      if (this.canvas3DElement?.isLoaded !== true) return;

      const { design, selectedPanels } = this.state;
      if (!Array.isArray(design) || design.length === 0) return;
      if (!selectedPanels || typeof selectedPanels !== 'object') return;

      this.emittingInitialShadingValuesPerArray = true;
      const emitted = await this.emitShadingValuesPerArray();
      if (emitted) {
        this.hasEmittedInitialShadingValuesPerArray = true;
      }
    } catch (err) {
      console.log(`error emitting initial ShadingValuesPerArray: ${err}`);
    } finally {
      this.emittingInitialShadingValuesPerArray = false;
    }
  };

  applySelectionFromStringingData = (strings = null) => {
    try {
      const data = Array.isArray(strings) ? strings : this.state.stringingData;
      if (!Array.isArray(data) || data.length === 0) return false;

      const fallbackMaps = this.buildStringingIndexMapsFromCanvas();
      const roofIndexMap =
        this.state.stringingRoofIndexMap || fallbackMaps.roofIndexMap || null;
      const selectedPanelIndexMap =
        this.state.stringingSelectedPanelIndexMap ||
        fallbackMaps.selectedPanelIndexMap ||
        null;

      const unique = resolveStringingSelectionIds({
        strings: data,
        roofIndexMap,
        selectedPanelIndexMap,
        selectedObjectsPanels: this.state.selectedObjects?.panels || {},
      });

      if (unique.length === 0) return false;
      this.setState({ hydratingSelectionFromStringing: true }, () => {
        try {
          this.addToSelectedObjectsBulk(unique, 'panels', true);
        } catch (e) { }
        setTimeout(() => {
          try {
            this.syncSelectedPanelFrom3D({ skipRecalc: true });
          } catch (e) { }
          try {
            const annualUsage = Number(this.state.annualUsage || 0);
            const solarProduction = Number(this.state.solarProduction || 0);
            const apiOffset =
              annualUsage > 0 ? (solarProduction / annualUsage) * 100 : 0;
            this.setState(
              {
                offset: Math.round(apiOffset || 0),
                hydratingSelectionFromStringing: false,
              },
              () => {
                try {
                  this.calcSavingsFromAPIProduction();
                } catch (e) { }
              }
            );
          } catch (e) {
            this.setState({ hydratingSelectionFromStringing: false });
          }
        }, 0);
      });
      return true;
    } catch (e) {
      return false;
    }
  };

  clearSelection = (objectType, forceClear = false) => {
    if (objectType !== 'panels') return;
    this.setState(clearReportPanelSelection(this.state, forceClear));
  };

  hydrateSelectionFromStringing = (
    stringsOverride = null,
    selectedPanelsPerRoof = null
  ) => {
    let strings = Array.isArray(stringsOverride)
      ? stringsOverride
      : this.state.stringingData;
    if ((!Array.isArray(strings) || strings.length === 0) && selectedPanelsPerRoof) {
      strings = buildSyntheticStringingFromSelectedPanels(
        selectedPanelsPerRoof
      );
    }
    if (!Array.isArray(strings) || strings.length === 0) return false;
    if (!this.canvas3DElement?.selectPanelsFromStringing) return false;
    if (this.canvas3DElement?.isLoaded !== true) return false;

    try {
      const run = () => {
        this.setState({ hydratingSelectionFromStringing: true }, () => {
          try {
            this.canvas3DElement.selectPanelsFromStringing(strings, 'all');
          } catch (e) { }
          setTimeout(() => {
            try {
              this.syncSelectedPanelFrom3D({ skipRecalc: true });
            } catch (e) { }
            this.setState({ hydratingSelectionFromStringing: false });
          }, 0);
        });
      };

      run();
      return true;
    } catch (e) {
      return false;
    }
  };

  retryHydrateSelectionFromStringing = (triesLeft = 25) => {
    if (triesLeft <= 0) {
      if (this.state.hydratingSelectionFromStringing) {
        this.setState({ hydratingSelectionFromStringing: false });
      }
      return;
    }

    const currentSelected = this.state.selectedObjects?.panels || {};
    const currentTotalSelected = Object.values(currentSelected).reduce(
      (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
      0
    );
    if (currentTotalSelected > 0) {
      if (this.state.hydratingSelectionFromStringing) {
        this.setState({ hydratingSelectionFromStringing: false });
      }
      return;
    }

    const started = this.hydrateSelectionFromStringing();

    setTimeout(() => {
      const selectedByVertex = this.state.selectedObjects?.panels || {};
      const totalSelected = Object.values(selectedByVertex).reduce(
        (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
        0
      );
      if (totalSelected > 0) {
        return;
      }
      if (!started && this.state.hydratingSelectionFromStringing) {
        this.setState({ hydratingSelectionFromStringing: false });
      }
      this.retryHydrateSelectionFromStringing(triesLeft - 1);
    }, 80);
  };

  loadProposalCanvasImagery = (proposal) => {
    const clean = (value) =>
      typeof value === 'string' ? value.trim().replace(/\/+$/, '') : '';
    const designId =
      clean(proposal?.design_id) ||
      clean(proposal?.design_data?.design_id) ||
      clean(proposal?.design_data?.canvas_id);
    const canvasId = clean(proposal?.design_data?.canvas_id) || designId;
    const endpoint = this.context?.newEndpoint?.('designer/canvas');
    if (!canvasId || typeof endpoint?.list !== 'function') return;
    const path =
      designId && designId !== canvasId
        ? `${encodeURIComponent(canvasId)}/?design_id=${encodeURIComponent(
            designId
          )}`
        : `${encodeURIComponent(canvasId)}/`;
    endpoint
      .list(path)
      .then((canvas) => {
        if (this.isUnmounted || !canvas) return;
        this.setState({
          proposalCanvasImagery: {
            rgb: canvas.rgb ?? null,
            dsm: canvas.dsm ?? null,
            dsm_params: canvas.dsm_params ?? canvas.dsmParams ?? null,
            resolution: canvas.resolution ?? null,
          },
        });
      })
      .catch(() => undefined);
  };

  componentDidMount() {
    const {
      pid,
      API,
      lead,
      hardware,
      callback,
      currentUser: advisor,
    } = this.props;

    // console.log('Props from report index.js: ', this.props);

    const { loan } = this.state;

    this.updateCallback = callback;

    API.retrieve(pid).then((data) => {
      if (this.isUnmounted) return;
      this.proposalDesignId = data?.design_id || data?.design_data?.design_id || data?.design_data?.canvas_id || null;
      this.parentDesignId = data?.design_id || data?.design_data?.design_id || null;
      this.loadProposalCanvasImagery(data);
      this.loadProposalScopeHeading(data);
      let state = getProposalState(lead, data, hardware, loan);
      const proposalVersion = data?.version || LEGACY_PROPOSAL_VERSION;
      this.proposalVersion = proposalVersion;
      const persistedProductionPerPanel =
        state?.designData?.productionPerPanel ??
        state?.designData?.production_per_panel ??
        [];
      const persistedProductionPerString =
        state?.designData?.productionPerString ??
        state?.designData?.production_per_string ??
        [];
      state = {
        ...state,
        proposalVersion,
        loadError: null,
        productionPerPanelData: Array.isArray(persistedProductionPerPanel)
          ? persistedProductionPerPanel
          : [],
        productionPerStringData: Array.isArray(persistedProductionPerString)
          ? persistedProductionPerString
          : [],
      };
      // console.log('State from report index.js: ', state);

      // DEBUG: Log design data before setting state
      // console.log('=== DEBUG: componentDidMount Design Data ===');
      // console.log('data.designs from API:', data.designs);
      // console.log('state.design from getProposalState:', state.design);
      // console.log('state.design type:', typeof state.design);
      // console.log('state.design is array:', Array.isArray(state.design));
      // console.log('state.design length:', Array.isArray(state.design) ? state.design.length : 'NOT ARRAY');

      const isLegacyProposal = isPersistedLegacyProposal({
        proposalVersion: data?.version,
        designData: data?.design_data,
        designs: data?.designs,
        legacyVersion: LEGACY_PROPOSAL_VERSION,
      });

      const legacyHydrationPlan = getLegacyHydrationPlan({
        isLegacyProposal,
        design: state.design,
        designData: data?.design_data,
        persistedSelectedPanels: state.selectedPanels,
        proposalVersion: data?.version,
        legacyVersion: LEGACY_PROPOSAL_VERSION,
      });

      if (isLegacyProposal) {
        state = {
          ...state,
          selectedPanels: legacyHydrationPlan.selectedPanels,
          legacyHydrationMode: legacyHydrationPlan.mode,
          legacySelectionSource: legacyHydrationPlan.source,
        };
      }

      this.setState({ ...state, isLegacyProposal }, async () => {
        if (this.isUnmounted) return;

        this.syncSelectedPanelFrom2D();
        this.maybeEmitInitialShadingValuesPerArray();

        // Check if savings arrays have actual values
        const yearlySavingsHasValues = Array.isArray(this.state.yearlySavings) &&
          this.state.yearlySavings.length > 0 &&
          this.state.yearlySavings.some((v) => Number(v) > 0);

        const lifetimeSavingsHasValues = Array.isArray(this.state.lifetimeSavings) &&
          this.state.lifetimeSavings.length > 0 &&
          this.state.lifetimeSavings.some((v) => Number(v) > 0);

        // Check cost values (for backward compatibility with older proposals)
        const hasCostData = Number(this.state.totalCost || 0) > 0 ||
          Number(this.state.systemCost || 0) > 0 ||
          Number(this.state.paybackYear || 0) > 0;

        // Check if savings arrays don't exist at all (null/undefined) - for backward compatibility
        // If arrays exist (even as empty []), we require them to have values > 0
        const savingsArraysDontExist = !Array.isArray(this.state.yearlySavings) &&
          !Array.isArray(this.state.lifetimeSavings);

        // Consider savings persisted if:
        // 1. Savings arrays have values > 0 (primary and most reliable check), OR
        // 2. Cost data exists AND savings arrays don't exist at all (backward compatibility for very old proposals)
        //    If arrays exist (even as empty []), we require them to have actual values to prevent the bug
        // This fixes the bug where proposals with cost data but empty [] savings arrays were skipping calculation
        const hasPersistedSavings = yearlySavingsHasValues || lifetimeSavingsHasValues ||
          (hasCostData && savingsArraysDontExist);

        // Legacy proposals must be hydrated from legacy inputs before modern
        // generated summary/stringing fields can short-circuit the load path.
        if (isLegacyProposal) {
          const exactSelectedPanels =
            legacyHydrationPlan.requiresRuntimeSelection
              ? null
              : legacyHydrationPlan.selectedPanels;
          const pendingHydration = {
            persistedDesigns: data?.designs,
            exactSelectedPanels,
            requestSeq: this.productionRequestGuard.current(),
          };
          const isActive = () =>
            !this.isUnmounted &&
            this.isLatestProductionRequest(pendingHydration.requestSeq);
          let lazySnapshotResult;
          try {
            lazySnapshotResult =
              await this.runLegacyProductionLoad(pendingHydration);
          } catch (error) {
            if (isActive()) {
              this.legacyProductionHydrationError =
                this.getLegacyProductionFailureReason(error);
            }
            lazySnapshotResult = {
              handled: false,
              source: 'calculation',
            };
          }
          if (lazySnapshotResult.handled) {
            return;
          }
          if (
            lazySnapshotResult.source === 'superseded' ||
            !isActive()
          ) {
            return;
          }
          if (lazySnapshotResult.source === 'conflict') {
            this.legacyProductionHydrationError =
              lazySnapshotResult.reason;
          }

          const canvasLoaded = this.canvas3DElement?.isLoaded === true;
          const shouldWaitForCanvas = shouldDeferLegacyProductionHydration({
            isLegacyProposal,
            reason: this.legacyProductionHydrationError,
            canvasLoaded,
          });
          const shouldRetryLoadedCanvas =
            canvasLoaded &&
            this.legacyProductionHydrationError ===
              LEGACY_CANVAS_UNAVAILABLE_REASON;
          if (shouldWaitForCanvas || shouldRetryLoadedCanvas) {
            this.pendingLegacyProductionHydration.defer(pendingHydration);
            const stateApplied = await this.setStateAsyncIfActive(isActive, {
              pendingLegacyProductionHydration: true,
              needsSolarProductionRecalc: false,
              productionIsCalculating: true,
            });
            if (!stateApplied) {
              this.pendingLegacyProductionHydration.clear();
              return;
            }
            if (this.canvas3DElement?.isLoaded === true) {
              await this.retryPendingLegacyProductionHydration();
            }
            return;
          }

          await this.setStateAsyncIfActive(isActive, {
            needsSolarProductionRecalc: true,
            productionIsCalculating: false,
            pendingLegacyProductionHydration: false,
          });
          if (!isActive()) return;
          this.emitProductionError(
            'Production data is not available for this legacy proposal. Recalculate production to generate iframe results.',
            this.legacyProductionHydrationError
          );
          return;
        }

        const productionReadiness = getProductionReadiness({
          solarProduction: this.state.solarProduction,
          totalPanels: this.state.totalPanels,
          stringingData: this.state.stringingData,
          invertersUsed: this.state.invertersUsed,
          isLegacyProposal: false,
        });

        if (productionReadiness.hasPersistedProduction) {
          this.retryHydrateSelectionFromStringing();
          if (!isLegacyProposal && !hasPersistedSavings) {
            await this.calcSavingsFromAPIProduction();
          }
          this.emitProductionCallback();
          return;
        }

        // Handle older leads that have solarProduction but missing other production indicators
        if (productionReadiness.hasSolarProductionOnly) {
          if (!isLegacyProposal && !hasPersistedSavings) {
            await this.calcSavingsFromAPIProduction();
          }
          this.emitProductionCallback();
          return;
        }

        if (productionReadiness.shouldRecalculateModernProduction) {
          const recalculated = await this.recalculateMissingModernProductionTopology();
          if (recalculated) return;
        }

        await this.setStateAsync({
          needsSolarProductionRecalc: true,
          productionIsCalculating: false,
        });
        this.emitProductionError(
          'Production data is not available for this proposal. Recalculate production to generate iframe results.'
        );
      });
    }).catch((err) => {
      if (this.isUnmounted) return;
      console.log(err);
      this.setState({ loadError: 'Proposal not found.' });
    });

    const logoUrl = advisor.company?.logo;
    if (typeof logoUrl === 'string' && logoUrl.trim()) {
      const logoImg = new Image();
      logoImg.onload = () => {
        if (this.isUnmounted) return;
        this.setState({ logoWidth: logoImg.width, logoHeight: logoImg.height });
      };
      logoImg.src = logoUrl;
    }

    const { resolution } = lead.property;
    this.resolution = Number(resolution) / 100.0;
    window.addEventListener('beforeunload', this.handleProposalBeforeUnload);
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    window.removeEventListener('beforeunload', this.handleProposalBeforeUnload);
    this.pendingLegacyProductionHydration.clear();
    this.bumpProductionRequestSeq();
    this.schedulePanelSelectionChange.cancel();
  }

  componentDidUpdate(prevProps, prevState) {
    const prevStrings = prevState?.stringingData;
    const nextStrings = this.state.stringingData;
    const hadStrings = Array.isArray(prevStrings)
      ? prevStrings.length > 0
      : Boolean(prevStrings);
    const hasStrings = Array.isArray(nextStrings)
      ? nextStrings.length > 0
      : Boolean(nextStrings);

    if (!hadStrings && hasStrings && this.canvas3DElement?.isLoaded === true) {
      this.retryHydrateSelectionFromStringing();
    }

    const prev = prevProps.lead.utility;

    const {
      lead: {
        utility: { averageBill, effectiveRate, fixedCharge },
      },
    } = this.props;

    if (
      (prev.averageBill || 0) !== (averageBill || 0) ||
      prev.effectiveRate !== effectiveRate ||
      prev.fixedCharge !== fixedCharge
    )
      this.calcSavings();
  }

  copyProposal = (id = null) => {
    const { pid, API } = this.props;
    const copy = (proposalId) => {
      API.send(
        'POST',
        { version: CURRENT_PROPOSAL_VERSION },
        `${proposalId}/duplicate/`
      ).then((data) => {
        if (this.updateCallback) {
          this.updateCallback(
            'CopyProposal',
            data.id,
            data?.version || CURRENT_PROPOSAL_VERSION
          );
        }
      });
    };

    if (id !== null) {
      copy(id);
      // proposalAPI.update(id, { id, name, copy: true }, true)
    } else {
      copy(pid);
      // proposalAPI.update(id, { id, name, copy: true }, true)
    }
  };

  handleProposalSelect = (id) => {
    if (!id || id === this.props.pid) return;
    if (this.state.proposalDirty) {
      this.setState({ switchConfirm: String(id) });
      return;
    }
    if (this.props.onNavigateProposal) {
      this.props.onNavigateProposal(id);
    }
  };

  confirmProposalSwitch = () => {
    const next = this.state.switchConfirm;
    this.setState({ switchConfirm: null });
    if (next && this.props.onNavigateProposal) {
      this.props.onNavigateProposal(next);
    }
  };

  handleSaveAsProposal = () => {
    const { proposalList } = this.props;
    const existingNames = Array.isArray(proposalList)
      ? proposalList.map((proposal) => String(proposal?.name || ''))
      : [];
    this.setState({
      saveAsOpen: true,
      saveAsName: suggestedNameForLead({ kind: 'proposal', existingNames }),
      proposalActionBusy: false,
    });
  };

  closeSaveAsProposal = () => {
    if (this.state.proposalActionBusy) return;
    this.setState({ saveAsOpen: false });
  };

  designRowOrderKey = (id) => {
    const suffix = Number(String(id || '').split('-').pop());
    return Number.isFinite(suffix) ? suffix : Number.MAX_SAFE_INTEGER;
  };

  remapCloneDesignIds = (stateDesigns, cloneDesigns) => {
    const rows = Array.isArray(stateDesigns) ? stateDesigns : [];
    const clones = Array.isArray(cloneDesigns) ? cloneDesigns : [];
    const idBySuffix = new Map();
    clones.forEach((row) => {
      if (row?.id) idBySuffix.set(this.designRowOrderKey(row.id), row.id);
    });
    return rows.map((row, index) => {
      const cloneId =
        idBySuffix.get(this.designRowOrderKey(row?.id)) || clones[index]?.id;
      return cloneId ? { ...row, id: cloneId } : row;
    });
  };

  settlePanelSelectionForSave = async () => {
    await new Promise((resolve) => this.setState({}, resolve));
    if (this.schedulePanelSelectionChange?.pending?.()) {
      this.schedulePanelSelectionChange.cancel();
      await this.handlePanelSelectionChange();
    }
    const started = Date.now();
    while (this.state.productionIsCalculating && Date.now() - started < 20000) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    await new Promise((resolve) => this.setState({}, resolve));
  };

  submitSaveAsProposal = async () => {
    const { pid, API, onCloneProposal, onNavigateProposal, displaySnack } = this.props;
    const currentName = String(this.currentProposalName() || '').trim();
    let name = String(this.state.saveAsName || '').trim();
    if (name && currentName && name === currentName) {
      name = `${name} copy`;
    }
    if (!name) return;
    this.setState({ proposalActionBusy: true });
    try {
      await this.settlePanelSelectionForSave();
      let data;
      if (typeof this.context?.newEndpoint === 'function') {
        const state = await this.updateReport({ collectOnly: true });
        data = await cloneProposalAs(this.context, pid, name);
        if (data?.id) {
          const nextState = {
            ...state,
            designs: this.remapCloneDesignIds(state?.designs, data.designs),
          };
          await API.update(data.id, nextState, true);
          const cloneDesignId = this.remapCloneDesignIds(
            [{ id: this.parentDesignId }],
            data?.designs
          )[0]?.id;
          if (cloneDesignId && cloneDesignId !== this.parentDesignId) {
            try {
              await this.updateParentDesignThumbnail(cloneDesignId);
            } catch (error) {
              console.error(error);
            }
          }
        }
      } else {
        const state = await this.updateReport({ collectOnly: true });
        data = await API.send(
          'POST',
          { version: CURRENT_PROPOSAL_VERSION, state },
          `${pid}/duplicate/`
        );
      }
      if (onCloneProposal) {
        await onCloneProposal(data);
      } else if (data?.id && onNavigateProposal) {
        onNavigateProposal(data.id);
      }
      this.setState({ saveAsOpen: false, proposalActionBusy: false });
      if (displaySnack) {
        displaySnack({
          variant: 'success',
          message: 'Proposal copied',
        });
      }
    } catch (e) {
      this.setState({ proposalActionBusy: false });
      if (displaySnack) {
        displaySnack({
          variant: 'danger',
          message: 'Failed to copy proposal',
        });
      }
    }
  };

  proposalNameById = (id) => {
    const { pid, proposalList } = this.props;
    const target = String(id || pid || '');
    const found = Array.isArray(proposalList)
      ? proposalList.find((proposal) => String(proposal.id) === String(target))
      : null;
    return found?.name || '';
  };

  currentProposalName = () => this.proposalNameById(this.props.pid);

  proposalTargetId = () => String(this.state.proposalTargetId || this.props.pid || '');

  handleRenameProposal = (id = null) => {
    const target = String(id || this.props.pid || '');
    this.setState({
      renameOpen: true,
      renameName: this.proposalNameById(target),
      proposalTargetId: target,
      proposalActionBusy: false,
    });
  };

  closeRenameProposal = () => {
    if (this.state.proposalActionBusy) return;
    this.setState({ renameOpen: false });
  };

  submitRenameProposal = async () => {
    const { onRenameProposal, API, displaySnack } = this.props;
    const target = this.proposalTargetId();
    const trimmed = String(this.state.renameName || '').trim();
    if (!trimmed) return;
    this.setState({ proposalActionBusy: true });
    try {
      if (onRenameProposal) {
        await onRenameProposal(target, trimmed);
      } else {
        await API.update(target, { name: trimmed }, true);
      }
      this.setState({ renameOpen: false, proposalActionBusy: false });
      if (displaySnack) {
        displaySnack({
          variant: 'success',
          message: 'Proposal renamed',
        });
      }
    } catch (e) {
      this.setState({ proposalActionBusy: false });
      if (displaySnack) {
        displaySnack({
          variant: 'warning',
          message: 'Proposal name could not be updated',
        });
      }
    }
  };

  handleArchiveProposal = (id = null) => {
    const target = String(id || this.props.pid || '');
    this.setState({ archiveOpen: true, proposalTargetId: target, proposalActionBusy: false });
  };

  closeArchiveProposal = () => {
    if (this.state.proposalActionBusy) return;
    this.setState({ archiveOpen: false });
  };

  submitArchiveProposal = async () => {
    const { pid, onArchiveProposal, API, onNavigateProposal, displaySnack } =
      this.props;
    const target = this.proposalTargetId();
    this.setState({ proposalActionBusy: true });
    try {
      if (onArchiveProposal) {
        await onArchiveProposal(target, { stay: String(target) !== String(pid) });
      } else {
        await API.send('POST', {}, `${target}/archive/`);
        if (String(target) === String(pid) && onNavigateProposal) onNavigateProposal();
      }
      this.setState({ archiveOpen: false, proposalActionBusy: false });
      if (displaySnack) {
        displaySnack({
          variant: 'success',
          message: 'Proposal archived',
        });
      }
    } catch (e) {
      this.setState({ proposalActionBusy: false });
      if (displaySnack) {
        displaySnack({
          variant: 'danger',
          message: 'Failed to archive proposal',
        });
      }
    }
  };

  dataUrlToFile = async (dataUrl, filename) => {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return new File([blob], filename, { type: 'image/png' });
  };

  updateParentDesignThumbnail = async (targetDesignId = this.parentDesignId) => {
    const designId =
      typeof targetDesignId === 'string'
        ? targetDesignId.trim().replace(/\/+$/, '')
        : '';
    const endpoint = this.context?.newEndpoint?.('designer/design');
    if (!designId || typeof endpoint?.send !== 'function') return null;
    const canvas3D = await this.waitForCaptureCanvas(['exportThumbnail']);
    const shot = canvas3D.exportDesignSnapshot
      ? await canvas3D.exportDesignSnapshot()
      : await canvas3D.exportThumbnail();
    const base64 = shot?.base64;
    if (!base64) return null;
    const file = await this.dataUrlToFile(
      base64,
      `design_snapshot_${Date.now()}.png`
    );
    const form = new FormData();
    form.append('snapshot', file);
    const saved = await endpoint.send('POST', form, `${designId}/snapshot`);
    if (this.props.onDesignThumbnailUpdated) {
      this.props.onDesignThumbnailUpdated(designId, saved?.snapshot || null);
    }
    return saved;
  };

  saveCurrentProposal = async () => {
    await this.settlePanelSelectionForSave();
    return this.updateReport();
  };

  handleManualSave = async () => {
    await this.saveCurrentProposal();
    try {
      await this.updateParentDesignThumbnail();
    } catch (error) {
      console.error(error);
      if (this.props.displaySnack) {
        this.props.displaySnack({
          variant: 'warning',
          message: 'Proposal saved, but the design thumbnail could not be updated',
        });
      }
      return;
    }
    if (this.props.displaySnack) {
      this.props.displaySnack({
        variant: 'success',
        message: 'Proposal saved',
      });
    }
  };

  recalculateMissingModernProductionTopology = async () => {
    if (countSelectedPanels(this.state.selectedPanels) <= 0) {
      return false;
    }

    const canvas3D = await waitForCanvas3DReady(
      () => this.canvas3DElement,
      {
        timeoutMs: this.props.iframe ? 15000 : 4000,
        pollIntervalMs: this.props.iframe ? 100 : 80,
        requiredMethods: ['getAnualPanelsShadings'],
      }
    );
    if (!canvas3D) return false;

    try {
      this.syncSelectedPanelFrom2D();
      await this.handlePanelSelectionChange();
      return true;
    } catch (e) {
      return false;
    }
  };

  hydratePersistedLegacyProduction = async (persistedDesigns = null) => {
    const hydratedState = buildPersistedLegacyProductionState({
      design: this.state.design,
      panel: this.state.panel,
      selectedPanels: this.state.selectedPanels,
      persistedDesigns,
      annualUsage: this.state.annualUsage,
      fallbackOffset: this.state.offset,
      fallbackSystemSize: this.state.systemSize,
      designData: this.state.designData,
    });
    if (!hydratedState) {
      return false;
    }

    await this.setStateAsync({
      ...hydratedState,
      needsSolarProductionRecalc: false,
      productionIsCalculating: false,
    });
    this.syncSelectedPanelFrom2D();
    return true;
  };

  runLegacyProductionLoad = ({
    persistedDesigns = null,
    exactSelectedPanels = null,
  } = {}) => {
    const { pid, API } = this.props;
    const requestSeq = this.productionRequestGuard.current();
    const isActive = () =>
      !this.isUnmounted && this.isLatestProductionRequest(requestSeq);
    const authoritativeSnapshot =
      this.getAuthoritativeLegacyProductionSnapshot(exactSelectedPanels);

    return this.loadLazyLegacyProductionSnapshot({
      isLegacyProposal: this.state.isLegacyProposal,
      proposalId: pid,
      authoritativeSnapshot,
      isActive,
      getSnapshot: () =>
        API.send(
          'GET',
          null,
          `${pid}/legacy-runtime-production-snapshot/`
        ),
      normalizeSnapshot: (snapshot) =>
        normalizeLegacyRuntimeProductionSnapshot({
          design: this.state.design,
          snapshot,
        }),
      hydrateSnapshot: (snapshot, source) =>
        this.hydrateLegacyProductionSnapshot(
          snapshot,
          source,
          isActive
        ),
      calculate: () =>
        this.hydrateLegacyProduction(
          persistedDesigns,
          exactSelectedPanels,
          isActive
        ),
      buildCallbackPayload: () =>
        this.emitProductionCallback({ deferEmit: true }),
      getSelectedPanels: () => this.state.selectedPanels,
      postSnapshot: (payload) =>
        API.send(
          'POST',
          payload,
          `${pid}/legacy-runtime-production-snapshot/`
        ),
      applyPersistedSnapshot: (snapshot) =>
        this.recordPersistedLegacyProductionSnapshot(snapshot, isActive),
      emit: (payload) => this.emitFinalProductionPayload(payload, isActive),
      onSnapshotReadError: this.handleLegacySnapshotReadError,
      onSnapshotPersistenceError:
        this.handleLegacySnapshotPersistenceError,
    });
  };

  getAuthoritativeLegacyProductionSnapshot = (selectedPanels) => {
    const selection = normalizeLegacySelectedPanels(
      this.state.design,
      selectedPanels
    );
    if (!selection) return null;

    const data = getVerifiedLegacyProductionSnapshot({
      design: this.state.design,
      designData: this.state.designData,
      selectedPanels: selection.selectedPanels,
    });
    if (!data) return null;

    return {
      data,
      selectedPanels: selection.selectedPanels,
      totalPanels: selection.totalPanels,
      snapshot: null,
    };
  };

  hydrateLegacyProductionSnapshot = async (
    snapshot,
    source = 'runtime',
    isActive = () => true
  ) => {
    const stateApplied = await this.setStateAsyncIfActive(
      isActive,
      (prevState) => ({
        selectedPanels: snapshot.selectedPanels,
        totalPanels: snapshot.totalPanels,
        systemSize: Number(snapshot.data.systemSize),
        solarProduction: Number(snapshot.data.yearlyProduction),
        monthlyProduction: snapshot.data.monthlyProduction.map(Number),
        productionPerPanelData: snapshot.data.productionPerPanel,
        offset: Number(snapshot.data.offset),
        ...(source === 'runtime'
          ? {
            legacyRuntimeProductionSnapshot: snapshot.snapshot,
            designData: mergeLegacyRuntimeProductionSnapshot({
              designData: prevState.designData,
              snapshot: snapshot.snapshot,
            }),
          }
          : {}),
        needsSolarProductionRecalc: false,
        productionIsCalculating: false,
        pendingLegacyProductionHydration: false,
      })
    );
    if (!stateApplied || !isActive()) return false;
    this.syncSelectedPanelFrom2D();
    await this.calcSavingsFromAPIProduction(isActive);
    return isActive();
  };

  recordPersistedLegacyProductionSnapshot = async (
    snapshot,
    isActive = () => true
  ) => {
    return this.setStateAsyncIfActive(isActive, (prevState) => ({
      legacyRuntimeProductionSnapshot: snapshot.snapshot,
      designData: mergeLegacyRuntimeProductionSnapshot({
        designData: prevState.designData,
        snapshot: snapshot.snapshot,
      }),
    }));
  };

  handleLegacySnapshotReadError = (error) => {
    if (error?.response?.status === 404) return;
    console.warn('Legacy production snapshot read failed', {
      proposalId: this.props.pid,
      status: error?.response?.status || null,
    });
  };

  handleLegacySnapshotPersistenceError = (error) => {
    console.warn('Legacy production snapshot persistence failed', {
      proposalId: this.props.pid,
      status: error?.response?.status || null,
    });
  };

  hydrateLegacyProduction = async (
    persistedDesigns = null,
    exactSelectedPanels = null,
    isActive = () => true
  ) => {
    if (!isActive()) return false;
    this.legacyProductionHydrationError = null;
    try {
      const { panel, annualUsage, design } = this.state;
      if (!panel || !panel.power || !Array.isArray(design) || design.length === 0) {
        this.legacyProductionHydrationError =
          'legacy_production_inputs_missing';
        return false;
      }

      const normalizedExactSelection = normalizeLegacySelectedPanels(
        design,
        exactSelectedPanels
      );

      // Source-blank legacy proposals never had saved selected panels; legacy
      // generated them after Canvas3D loaded, so iframe loads must wait here too.
      const canvas3D = await waitForCanvas3DReady(
        () => this.canvas3DElement,
        {
          timeoutMs: this.props.iframe ? 15000 : 800,
          pollIntervalMs: this.props.iframe ? 100 : 80,
          requiredMethods: ['getAnualPanelsShadings'],
        }
      );
      if (!isActive()) return false;
      if (!canvas3D) {
        this.legacyProductionHydrationError =
          LEGACY_CANVAS_UNAVAILABLE_REASON;
        return false;
      }

      let panelShadings = this.state.panelShadings;
      let monthlyPanelShadings = this.state.monthlyPanelShadings;
      if (!panelShadings || panelShadings.length === 0) {
        panelShadings = await canvas3D.getAnualPanelsShadings();
        if (!isActive()) return false;
        monthlyPanelShadings = panelShadings.pop();
      }
      panelShadings = normalizeLegacyPanelShadingOrder(panelShadings, {
        design,
        designData: this.state.designData,
      });

      let runtimeSelection = null;
      if (!normalizedExactSelection) {
        const hasPanelLevelShading =
          hasUsableLegacyPanelShading(panelShadings);
        const noShadingSelection = hasPanelLevelShading
          ? null
          : buildLegacyNoShadingRuntimeSelection({
            annualUsage,
            calculateNoShading: calculateSolarWithNoShading,
            design,
            panel,
            persistedDesigns,
          });

        if (noShadingSelection) {
          runtimeSelection = noShadingSelection;
        } else {
          runtimeSelection = calculateSolar(
            panel,
            Number(annualUsage || 0),
            design,
            panelShadings
          );
        }
      }
      const selectedPanels = normalizedExactSelection
        ? normalizedExactSelection.selectedPanels
        : runtimeSelection.selectedPanels;
      const systemSize = normalizedExactSelection
        ? normalizedExactSelection.totalPanels * (Number(panel.power) / 1000)
        : runtimeSelection.systemSize;

      const selectionStateApplied = await this.setStateAsyncIfActive(isActive, {
        selectedPanels,
        systemSize,
        panelShadings,
        monthlyPanelShadings,
      });
      if (!selectionStateApplied || !isActive()) return false;

      this.syncSelectedPanelFrom2D();

      // Match legacy iframe callbacks before falling back to hourly/PVWatts:
      // selected panel * panel kW * panel shading factor * legacy acMonthly.
      const transformedState = buildPersistedLegacyProductionState({
        design: this.state.design,
        panel,
        selectedPanels,
        persistedDesigns,
        annualUsage,
        fallbackOffset: this.state.offset,
        fallbackSystemSize: systemSize,
        panelShadings,
        designData: this.state.designData,
      });

      if (transformedState) {
        const productionStateApplied = await this.setStateAsyncIfActive(
          isActive,
          {
            ...transformedState,
            panelShadings,
            monthlyPanelShadings,
            needsSolarProductionRecalc: false,
            productionIsCalculating: false,
          }
        );
        if (!productionStateApplied || !isActive()) return false;
        await this.calcSavingsFromAPIProduction(isActive);
        return isActive();
      }

      await this.hourlyProductionLegacy(isActive);
      if (!isActive()) return false;

      if (
        (this.state.solarProduction || 0) <= 0 ||
        !Array.isArray(this.state.monthlyProduction) ||
        !this.state.monthlyProduction.some((v) => Number(v) > 0)
      )
        return false;
      await this.calcSavingsFromAPIProduction(isActive);
      return isActive();
    } catch (e) {
      if (!isActive()) return false;
      const reason = this.getLegacyProductionFailureReason(e);
      this.legacyProductionHydrationError = reason;
      console.error('Legacy production hydration failed', {
        proposalId: this.props.pid,
        reason,
        status: e?.response?.status || null,
      });
      return false;
    }
  };

  // utility function to close modals
  hideModal = () => this.setState({ modal: null });

  setProposalLosses = (nextLosses) => {
    const { pid, API } = this.props;
    const safeLosses =
      nextLosses && typeof nextLosses === 'object' ? nextLosses : {};
    this.markProposalDirty();
    this.setState(
      (prev) => ({
        designData: {
          ...(prev.designData || {}),
          losses: safeLosses,
        },
      }),
      () => {
        if (this.isIframeProposalAutoSaveEnabled()) {
          try {
            API.update(
              pid,
              this.withCurrentProposalVersion({
                design_data: {
                  ...(this.state.designData || {}),
                  losses: safeLosses,
                },
              }),
              true
            ).then(this.recordCurrentProposalVersion);
          } catch (e) { }
        }

        const totalPanels = Number(this.state.totalPanels || 0);
        if (totalPanels > 0) {
          this.handlePanelSelectionChange();
        } else {
          this.handleOptimalPanelSelection();
        }
      }
    );
  };

  // handlers for equipment selections
  solarPanelChangeHandler = (value) => {
    const { hardware, lead } = this.props;
    const { panel: currentPanel, proposalWithShadings } = this.state;
    const panel = hardware.panels.find(({ id }) => id === value);

    // change inverter if 'has_micro' attribute has changed
    let { inverter } = this.state;
    const statePackage = hardware.packages.filter(
      (item, index) => item.state === lead.address.state
    )[0];
    const defaultPackage = hardware.packages.filter(
      (item, index) => item.state === null
    )[0];
    const selectedPackage =
      statePackage !== undefined ? statePackage : defaultPackage;
    const defaultInverter = Object(
      selectedPackage.inverter || hardware.inverters[0]
    );
    // Removed panel.has_micro logic - let user select appropriate microinverter
    // if (panel.has_micro !== currentPanel.has_micro) {
    //   inverter = panel.has_micro ? Object() : defaultInverter;
    // }
    this.markProposalDirty();
    this.setState({ panel, inverter }, () => {
      const totalPanels = Number(this.state.totalPanels || 0);
      if (totalPanels > 0) {
        this.handlePanelSelectionChange();
      } else {
        this.handleOptimalPanelSelection();
      }
    });
  };

  invertersChangeHandler = (
    value,
    primaryQuantity = 1,
    additionalInvertersData = [],
    primaryInverterAutoQuantity = null,
    persistProposal = true
  ) => {
    const { hardware } = this.props;
    const { panel, selectedPanels } = this.state;
    const currentInverterId =
      typeof this.state?.inverter === 'string'
        ? this.state.inverter
        : this.state?.inverter?.id;
    const currentCompanyInverterId =
      typeof this.state?.companyInverter === 'string'
        ? this.state.companyInverter
        : this.state?.companyInverter?.id;
    const selectedInverterId =
      value || currentInverterId || currentCompanyInverterId || '';
    if (!selectedInverterId) {
      return;
    }
    const matchedInverter = hardware.inverters.find(
      ({ id }) => id === selectedInverterId
    );
    const stateInverterObject =
      this.state?.inverter && typeof this.state.inverter === 'object'
        ? this.state.inverter
        : null;
    const inverter = matchedInverter || stateInverterObject;
    if (!inverter) {
      return;
    }

    this.markProposalDirty();
    // Map additional inverters data to include full inverter details
    const additionalInvertersAll = (additionalInvertersData || [])
      .map((inv) => this.normalizeAdditionalInverter(inv))
      .filter(Boolean);

    const primaryGroup = getInverterGroup(inverter);
    const additionalInverters =
      primaryGroup === 'micro'
        ? []
        : (additionalInvertersAll || []).filter(
          (inv) => getInverterGroup(inv) === primaryGroup
        );
    const hasConfiguredAdditionalInverters = additionalInverters.some(
      (inv) => inv?.id
    );
    // Use the quantity that was calculated in real-time by the modal
    let inverterQuantity = primaryQuantity;

    const requestedPrimaryAuto =
      primaryInverterAutoQuantity === null
        ? !this.state.hasAutoCalculatedInverterQuantity
        : primaryInverterAutoQuantity;
    const nextPrimaryAuto = hasConfiguredAdditionalInverters
      ? false
      : requestedPrimaryAuto;
    const persistedAdditionalInverters = this.toPersistedAdditionalInverters(
      additionalInverters
    );

    this.setState(
      (prevState) =>
        buildReportInverterChangePatch(prevState, {
          inverter,
          inverterQuantity,
          additionalInverters,
          persistedAdditionalInverters,
          primaryInverterAutoQuantity: nextPrimaryAuto,
        }),
      async () => {
        this.calcCosts();

        // Recalculate production with new inverter configuration using solar-production API
        const { totalPanels } = this.state;
        if (totalPanels > 0) {
          // Panels already selected, recalculate production for selected panels
          await this.handlePanelSelectionChange();
        } else {
          // No panels selected, do optimal panel selection
          await this.handleOptimalPanelSelection();
        }

        if (persistProposal && this.isIframeProposalAutoSaveEnabled()) {
          try {
            await this.updateReport();
          } catch (error) {
          }
        }
      }
    );
  };

  mountingChangeHandler = (value) => {
    const { hardware } = this.props;
    const mounting = Object(hardware.mounting.find(({ id }) => id === value));
    this.markProposalDirty();
    this.setState({ mounting }, () => this.calcCosts());
  };

  monitoringChangeHandler = (value) => {
    const { hardware } = this.props;
    const monitoring = Object(
      hardware.monitoring.find(({ id }) => id === value)
    );
    this.markProposalDirty();
    this.setState({ monitoring }, () => this.calcCosts());
  };

  addersUpdateHandler = (adders) => {
    this.markProposalDirty();
    this.setState({ modal: null, adders }, () => this.calcCosts());
  };

  deleteAdder = (id) => {
    this.markProposalDirty();
    this.setState(
      ({ adders }) => {
        const addr = adders[id];
        addr.count = 0;
        return { adders: { [id]: addr, ...adders } };
      },
      () => this.calcCosts()
    );
  };

  // handlers for utility-related values
  utilityEscalationHandler = (value) => {
    // console.log('Escalation value received:', value);
    const escalation = value / 100;
    // console.log('Escalation converted to decimal:', escalation);
    this.markProposalDirty();
    this.setState({ escalation }, () => {
      // console.log('State updated with escalation:', this.state.escalation);
      this.calcSavings();
    });
  };

  // re-calculate annual usage and panel selection when bill is changed
  // monthlyBillHandler = (monthlyBill) => {
  //   const { lead: { utility: { fixed_fee: fixed, rate } } } = this.props;
  //   const annualUsage = ((monthlyBill - fixed) / rate) * 12;

  //   this.setState(
  //     { modal: null, monthlyBill, annualUsage },
  //     () => this.solarCalculations(),
  //   );
  // };

  loanChangeHandler = (loan) => {
    this.markProposalDirty();
    this.setState({ loan }, () => this.calcCosts());
  };

  setLoanObj = (loanObj) => {
    this.markProposalDirty();
    this.setState({ loanObj });
  };

  // handles financials for a loan payment
  loanCalculator = () => {
    const { loan, yearlySavings, totalCost, rebateAmount } = this.state;
    const data = calculateLoan(loan, yearlySavings, totalCost, rebateAmount);
    // if (this.updateCallback) this.updateCallback('Loan', data);
    this.setState({ ...data });
  };

  solarCalculations = async () => {
    // console.log('solar calc. -with- shading');
    if (this.processSolarCalculations) return;
    this.processSolarCalculations = true;

    const {
      panelShadings: statePanelShadings,
      monthlyPanelShadings: stateMonthlyPanelShadings,
    } = this.state;

    // Get panel shadings if not available
    let panelShadings = statePanelShadings;
    let monthlyPanelShadings = stateMonthlyPanelShadings;
    if (!panelShadings || panelShadings.length === 0) {
      panelShadings = await this.canvas3DElement.getAnualPanelsShadings();
      monthlyPanelShadings = panelShadings.pop();
    }

    this.setState(
      {
        panelShadings,
        monthlyPanelShadings,
      },
      async () => {
        // Use new solar-production API for optimal panel selection
        await this.handleOptimalPanelSelection();
        this.processSolarCalculations = false;
      }
    );
  };

  solarCalculationsWithOffset = async (offset) => {
    if (this.processSolarCalculations) return;
    this.processSolarCalculations = true;
    this.setState({ offset }, async () => {
      try {
        await this.handleOptimalPanelSelection();
      } finally {
        this.processSolarCalculations = false;
      }
    });
  };

  solarCalculationsWithNoShading = async () => {
    // console.log('solar calc. -without- shading');
    // Use new solar-production API for optimal panel selection
    await this.handleOptimalPanelSelection();
  };

  // ============================================================================
  // Solar Production Service Integration
  // ============================================================================

  /**
   * Build JSON payload for solar-production service
   * @param {boolean} selectedPanelsOnly - If true, only include selected panels
   */
  buildSharedProductionPayload = async (selectedPanelsOnly = false) => {
    const { lead, hardware } = this.props;
    const {
      panel,
      inverter,
      inverterQuantity,
      additionalInverters,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
      design,
      designData,
    } = this.state;
    let panelShadingData = [];
    if (this.canvas3DElement?.getAnualPanelsShadings) {
      try {
        panelShadingData =
          await this.canvas3DElement.getAnualPanelsShadings();
      } catch (error) {}
    }
    const selectedByVertex = this.canvas3DElement?.getSelectedPanelsByVertex
      ? this.canvas3DElement.getSelectedPanelsByVertex()
      : this.state?.selectedObjects?.panels || {};
    const normalizedAdditionalInverters = (additionalInverters || [])
      .map((item) => this.normalizeAdditionalInverter(item))
      .filter((item) => item?.id);
    const powerOptimizerConfig =
      this.props?.context?.powerOptimizer || designData?.powerOptimizer;
    const powerOptimizer =
      this.normalizePowerOptimizerPayload?.(powerOptimizerConfig) ||
      powerOptimizerConfig;
    const primaryAuto =
      primaryInverterAutoQuantity && !hasAutoCalculatedInverterQuantity;
    const result = buildSharedProductionJson({
      lat: lead.property?.coordinates?.[0],
      lon: lead.property?.coordinates?.[1],
      segments:
        Array.isArray(designData?.segments) && designData.segments.length
          ? designData.segments
          : design,
      panelDataByIndex: this.canvas3DElement?.panelData || [],
      selectedByVertex,
      panelShadingData,
      onlySelectedPanels: selectedPanelsOnly,
      panel,
      inverter,
      inverterQuantity,
      additionalInverters: normalizedAdditionalInverters,
      allInverters: hardware?.inverters || [],
      invertersUsed: this.state.invertersUsed,
      savedInverterHardware:
        designData?.production_payload?.inverter_hardware ||
        designData?.productionPayload?.inverter_hardware,
      primaryInverterAutoQuantity: primaryAuto,
      losses: designData?.losses || {},
      powerOptimizer,
      wiring: designData?.wiring || {},
      wiringConfigured: Boolean(designData?.wiringConfigured),
      includeDcacTarget: true,
      designData,
    });
    if (this.proposalDesignId) {
      result.productionJson.design_id = this.proposalDesignId;
    }
    this.setState({
      stringingSelectedPanelIndexMap: selectedPanelsOnly
        ? result.selectedPanelIndexMap
        : null,
      stringingRoofIndexMap: result.roofIndexMap,
    });
    this.lastSelectedPanelIndexMapForPayload = result.selectedPanelIndexMap;
    this.lastRoofIndexMapForPayload = result.roofIndexMap;
    return result.productionJson;
  };

  /**
   * Handle optimal panel selection using solar-production service
   * Called on initial load or when equipment changes with no panels selected
   */
  handleOptimalPanelSelection = async () => {
    const { solarProductionOptimalAPI } = this.props;
    const {
      annualUsage,
      offset,
      panel,
      design,
      designData,
      inverter,
      inverterQuantity,
      additionalInverters,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
    } = this.state;
    const inverterConfigSnapshot = {
      inverter,
      inverterQuantity,
      additionalInverters,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
    };

    this.setState({ productionIsCalculating: true });
    const requestSeq = this.bumpProductionRequestSeq();

    try {
      const payload = await this.buildSharedProductionPayload(false); // All panels
      payload.lead_uid = this.props.lead?.uid;
      payload.target_production = annualUsage * (offset / 100 || 1); // Target based on offset
      const requestedPanelCount = countProductionPayloadPanels(payload);
      const result = await solarProductionOptimalAPI.create(payload);

      if (!this.isLatestProductionRequest(requestSeq)) return;

      if (!result.success) {
        throw new Error(result.message || 'Optimal panel selection failed');
      }

      const panelIndexMap = Array.isArray(this.lastSelectedPanelIndexMapForPayload)
        ? this.lastSelectedPanelIndexMapForPayload
        : null;
      const roofIndexMap =
        this.lastRoofIndexMapForPayload || this.state.stringingRoofIndexMap;
      const mappedResult = mapReportSolarProductionResult({
        state: this.state,
        result,
        design,
        previousSelectedPanels: this.state.selectedPanels,
        selectedPanelsPerRoofFallback: {},
        panelData: this.canvas3DElement?.panelData,
        designPanels: designData?.panels,
        roofIndexMap,
        panelIndexMap,
        requestedPanelCount,
        panelPower: panel?.power,
        annualUsage,
        primaryInverterAutoQuantity,
        additionalInverters,
      });
      const {
        strings,
        selectedPanels: newSelectedPanels,
        selectedPanelsPerRoof: mappedSelectedPanelsPerRoof,
        productionPerPanelData,
        productionPerStringData,
        perRoofAnnualKwh,
        perRoofMonthlyAc,
        perRoofMonthlyPoa,
        perRoofClippingKwh,
        numTotalPanels,
        perRoofCounts,
        apiPanelCount,
        apiSystemSize,
        apiAnnualKwh,
        apiMonthlyKwh,
        apiOffset,
        limitReached: shouldWarnAboutFixedInverterCapacity,
        invertersUsed,
        mpptCountTotal,
        reportStatePatch,
      } = mappedResult;

      this.setStateIfLatest(
        requestSeq,
        reportStatePatch,
        async () => {
          if (
            !this.isLatestProductionRequest(requestSeq) ||
            this.isUnmounted
          ) {
            return;
          }
          this.hydrateSelectionFromStringing(
            strings,
            mappedSelectedPanelsPerRoof
          );
          // Calculate savings using API production values
          await this.calcSavingsFromAPIProduction();
          if (
            !this.isLatestProductionRequest(requestSeq) ||
            this.isUnmounted
          ) {
            return;
          }
          await this.persistSolarProductionResults({
            result,
            stringsOverride: strings,
            apiAnnualKwh,
            apiMonthlyKwh,
            apiSystemSize,
            apiPanelCount,
            apiOffset,
            stringingPanelIndexMode: 'all',
            productionPerPanelData,
            productionPerStringData,
            selectedPanelsSnapshot: newSelectedPanels,
            designSnapshot: design,
            designDataSnapshot: designData,
            inverterConfigSnapshot,
            limitReached: shouldWarnAboutFixedInverterCapacity,
            requestSeq,
          });
          if (
            !this.isLatestProductionRequest(requestSeq) ||
            this.isUnmounted
          ) {
            return;
          }

          if (this.updateCallback) {
            // NOTE: SolarProduction callback is disabled for now. Keep for future reference.
            /*
          this.updateCallback('SolarProduction', {
            annual_kwh: apiAnnualKwh,
            clipping_kwh: result.annual_clipping_kwh,
            dc_ac_ratio: result.dc_ac_ratio,
            strings_count: result.solar_design?.[0]?.strings?.length || 0,
          });
            */
            const productionPayload = buildReportProductionCallback({
              state: this.state,
              annualUsage,
              panelData: this.canvas3DElement?.panelData,
              overrides: {
                annualKwh: apiAnnualKwh,
                monthlyKwh: apiMonthlyKwh,
                systemSizeKw: apiSystemSize,
                numTotalPanels,
                selectedPanelCount: apiPanelCount,
                perRoofCounts,
                selectedPanelsPerRoof: mappedSelectedPanelsPerRoof,
                productionPerPanel: productionPerPanelData,
                productionPerString: productionPerStringData,
                strings,
                invertersUsed,
                monthlyClippingKwh:
                  result.monthly_clipping_kwh || Array(12).fill(0),
                clippingKwh: result.annual_clipping_kwh || 0,
                dcAcRatio: result.dc_ac_ratio,
                panelCount: apiPanelCount,
                mpptCountTotal,
                perRoofAnnualKwh,
                perRoofMonthlyAc,
                perRoofMonthlyPoa,
                perRoofClippingKwh,
                limitReached: shouldWarnAboutFixedInverterCapacity,
              },
            });
            this.emitProductionPayload(productionPayload);
            this.emitShadingValuesPerArray();
          }
        }
      );
    } catch (err) {
      if (this.isLatestProductionRequest(requestSeq) && !this.isUnmounted) {
        this.setState({ productionIsCalculating: false });
        this.emitProductionError(
          err?.message || 'Error in calculating proposal production'
        );
      }
    }
  };

  /**
   * Handle manual panel selection change (debounced)
   * Called when user manually toggles panels
   */
  handlePanelSelectionChange = async () => {
    const { solarProductionSimulateAPI, displaySnack } = this.props;
    const {
      selectedPanels,
      annualUsage,
      panel,
      design,
      designData,
      inverter,
      inverterQuantity,
      additionalInverters,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
    } = this.state;
    const inverterConfigSnapshot = {
      inverter,
      inverterQuantity,
      additionalInverters,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
    };
    const requestSeq = this.bumpProductionRequestSeq();

    const selectedByVertex =
      this.canvas3DElement?.getSelectedPanelsByVertex?.() ??
      this.state?.selectedObjects?.panels;
    const currentSelectedCount =
      selectedByVertex && typeof selectedByVertex === 'object'
        ? countSelectedPanelIndices(selectedByVertex)
        : countSelectedPanels(selectedPanels);

    if (currentSelectedCount === 0) {
      this.setStateIfLatest(
        requestSeq,
        buildEmptyReportProductionState(),
        () => {
          if (
            !this.isLatestProductionRequest(requestSeq) ||
            this.isUnmounted
          ) {
            return;
          }
          const emptyMonthly = Array(12).fill(0);
          this.emitProductionCallback({
            annualKwh: 0,
            monthlyKwh: emptyMonthly,
            systemSizeKw: 0,
            selectedPanelCount: 0,
            productionPerPanel: [],
            productionPerString: [],
            strings: null,
            invertersUsed: null,
            monthlyClippingKwh: emptyMonthly,
            clippingKwh: 0,
            dcAcRatio: null,
            panelCount: 0,
          });
        }
      );
      return;
    }

    this.setState({ productionIsCalculating: true });

    try {
      const payload = await this.buildSharedProductionPayload(true); // Selected panels only
      if (!this.isLatestProductionRequest(requestSeq)) return;
      payload.lead_uid = this.props.lead?.uid;
      payload.target_production = null; // No re-optimization
      payload.return_topology = true;
      const result = await solarProductionSimulateAPI.create(payload);

      if (!this.isLatestProductionRequest(requestSeq)) return;

      if (!result.success) {
        throw new Error(result.message || 'Production simulation failed');
      }

      const panelIndexMap = Array.isArray(this.lastSelectedPanelIndexMapForPayload)
        ? this.lastSelectedPanelIndexMapForPayload
        : null;
      const roofIndexMap =
        this.lastRoofIndexMapForPayload || this.state.stringingRoofIndexMap;
      const mappedResult = mapReportSolarProductionResult({
        state: this.state,
        result,
        design,
        previousSelectedPanels: selectedPanels,
        panelData: this.canvas3DElement?.panelData,
        designPanels: designData?.panels,
        roofIndexMap,
        panelIndexMap,
        requestedPanelCount: currentSelectedCount,
        panelPower: panel?.power,
        annualUsage,
        primaryInverterAutoQuantity,
        additionalInverters,
        synthesizeMissingStringing: true,
      });
      const {
        strings: resolvedStrings,
        selectedPanels: newSelectedPanels,
        selectedPanelsPerRoof: mappedSelectedPanelsPerRoof,
        productionPerPanelData,
        productionPerStringData,
        perRoofAnnualKwh,
        perRoofMonthlyAc,
        perRoofMonthlyPoa,
        perRoofClippingKwh,
        numTotalPanels,
        perRoofCounts,
        apiPanelCount,
        apiSystemSize,
        apiAnnualKwh,
        apiMonthlyKwh,
        apiOffset,
        limitReached: shouldWarnAboutFixedInverterCapacity,
        invertersUsed,
        mpptCountTotal,
        reportStatePatch,
      } = mappedResult;

      this.setStateIfLatest(
        requestSeq,
        reportStatePatch,
        async () => {
          if (
            !this.isLatestProductionRequest(requestSeq) ||
            this.isUnmounted
          ) {
            return;
          }
          this.hydrateSelectionFromStringing(
            resolvedStrings,
            mappedSelectedPanelsPerRoof
          );
          // Calculate savings using API production values
          await this.calcSavingsFromAPIProduction();
          if (
            !this.isLatestProductionRequest(requestSeq) ||
            this.isUnmounted
          ) {
            return;
          }
          if (
            !this.isLatestProductionRequest(requestSeq) ||
            this.isUnmounted
          ) {
            return;
          }
          await this.persistSolarProductionResults({
            result,
            stringsOverride: resolvedStrings,
            apiAnnualKwh,
            apiMonthlyKwh,
            apiSystemSize,
            apiPanelCount,
            apiOffset,
            stringingPanelIndexMode: 'all',
            productionPerPanelData,
            productionPerStringData,
            selectedPanelsSnapshot: newSelectedPanels,
            designSnapshot: design,
            designDataSnapshot: designData,
            inverterConfigSnapshot,
            limitReached: shouldWarnAboutFixedInverterCapacity,
            requestSeq,
          });
          if (
            !this.isLatestProductionRequest(requestSeq) ||
            this.isUnmounted
          ) {
            return;
          }

          if (this.updateCallback) {
            // NOTE: SolarProduction callback is disabled for now. Keep for future reference.
            /*
          this.updateCallback('SolarProduction', {
            annual_kwh: apiAnnualKwh,
            clipping_kwh: result.annual_clipping_kwh,
            dc_ac_ratio: result.dc_ac_ratio,
          });
            */
            const productionPayload = buildReportProductionCallback({
              state: this.state,
              annualUsage,
              panelData: this.canvas3DElement?.panelData,
              overrides: {
                annualKwh: apiAnnualKwh,
                monthlyKwh: apiMonthlyKwh,
                systemSizeKw: apiSystemSize,
                numTotalPanels,
                selectedPanelCount: apiPanelCount,
                perRoofCounts,
                selectedPanelsPerRoof: mappedSelectedPanelsPerRoof,
                productionPerPanel: productionPerPanelData,
                productionPerString: productionPerStringData,
                strings: resolvedStrings,
                invertersUsed,
                monthlyClippingKwh:
                  result.monthly_clipping_kwh || Array(12).fill(0),
                clippingKwh: result.annual_clipping_kwh || 0,
                dcAcRatio: result.dc_ac_ratio,
                panelCount: apiPanelCount,
                mpptCountTotal,
                perRoofAnnualKwh,
                perRoofMonthlyAc,
                perRoofMonthlyPoa,
                perRoofClippingKwh,
                limitReached: shouldWarnAboutFixedInverterCapacity,
              },
            });
            this.emitProductionPayload(productionPayload);
            this.emitShadingValuesPerArray();
          }

          if (
            shouldWarnAboutFixedInverterCapacity &&
            this.props.iframe &&
            this.updateCallback
          ) {
            this.updateCallback(
              FIXED_INVERTER_PANEL_CAPACITY_CALLBACK,
              buildFixedInverterPanelCapacityNotification()
            );
          } else if (shouldWarnAboutFixedInverterCapacity && displaySnack) {
            displaySnack({
              variant: 'warning',
              message: FIXED_INVERTER_PANEL_CAPACITY_MESSAGE,
            });
          }
        }
      );
    } catch (err) {
      if (this.isLatestProductionRequest(requestSeq) && !this.isUnmounted) {
        this.setState({ productionIsCalculating: false });
        this.emitProductionError(
          err?.message || 'Error in calculating proposal production'
        );
      }
    }
  };

  persistSolarProductionResults = async ({
    result,
    stringsOverride = null,
    apiAnnualKwh,
    apiMonthlyKwh,
    apiSystemSize,
    apiPanelCount,
    apiOffset,
    stringingPanelIndexMode = 'all',
    productionPerPanelData = null,
    productionPerStringData = null,
    selectedPanelsSnapshot = null,
    designSnapshot = null,
    designDataSnapshot = null,
    inverterConfigSnapshot = null,
    limitReached = false,
    requestSeq = null,
  }) => {
    const { pid, API } = this.props;
    const { designData, design, selectedPanels } = this.state;
    const resolvedDesignData = designDataSnapshot || designData;
    const resolvedDesign = designSnapshot || design;
    const resolvedSelectedPanels = selectedPanelsSnapshot || selectedPanels;

    if (
      this.isUnmounted ||
      (requestSeq !== null && !this.isLatestProductionRequest(requestSeq))
    ) {
      return;
    }

    if (!this.isIframeProposalAutoSaveEnabled()) {
      return;
    }

    try {
      const resolvedPrimaryInverter =
        inverterConfigSnapshot?.inverter ?? this.state?.inverter ?? null;
      const resolvedPrimaryAutoQuantity =
        typeof inverterConfigSnapshot?.primaryInverterAutoQuantity ===
          'boolean'
          ? inverterConfigSnapshot.primaryInverterAutoQuantity
          : typeof this.state?.primaryInverterAutoQuantity === 'boolean'
            ? this.state.primaryInverterAutoQuantity
            : resolvedDesignData?.primaryInverterAutoQuantity ?? true;
      const resolvedHasAutoCalculatedInverterQuantity =
        typeof inverterConfigSnapshot?.hasAutoCalculatedInverterQuantity ===
          'boolean'
          ? inverterConfigSnapshot.hasAutoCalculatedInverterQuantity
          : typeof this.state?.hasAutoCalculatedInverterQuantity === 'boolean'
            ? this.state.hasAutoCalculatedInverterQuantity
            : !resolvedPrimaryAutoQuantity;
      const resolvedInverterQuantity = Math.max(
        1,
        Number(
          inverterConfigSnapshot?.inverterQuantity ??
          this.state?.inverterQuantity ??
          resolvedDesignData?.inverterQuantity ??
          resolvedDesignData?.inverter_quantity ??
          1
        ) || 1
      );
      const productionInvertersUsed =
        result?.solar_design?.[0]?.inverters_used || null;
      const persistedInverterQuantity = resolveMicroInverterQuantity({
        inverter: resolvedPrimaryInverter,
        primaryInverterAutoQuantity: resolvedPrimaryAutoQuantity,
        selectedPanelCount: apiPanelCount,
        invertersUsed: productionInvertersUsed,
        fallbackQuantity: resolvedInverterQuantity,
      });
      const resolvedAdditionalInverters = Array.isArray(
        inverterConfigSnapshot?.additionalInverters
      )
        ? inverterConfigSnapshot.additionalInverters
        : Array.isArray(this.state?.additionalInverters)
          ? this.state.additionalInverters
          : [];
      const persistedAdditionalInverters = this.toPersistedAdditionalInverters(
        resolvedAdditionalInverters
      );

      const stringsAll =
        stringsOverride ?? (result?.solar_design?.[0]?.strings || []);
      const persistence = buildReportProductionPersistence({
        designData: resolvedDesignData,
        design: resolvedDesign,
        selectedPanels: resolvedSelectedPanels,
        strings: stringsAll,
        result,
        annualKwh: apiAnnualKwh,
        monthlyKwh: apiMonthlyKwh,
        systemSize: apiSystemSize,
        panelCount: apiPanelCount,
        offset: apiOffset,
        stringingPanelIndexMode,
        productionPerPanel: productionPerPanelData,
        productionPerString: productionPerStringData,
        inverter: resolvedPrimaryInverter,
        inverterQuantity: persistedInverterQuantity,
        primaryInverterAutoQuantity: resolvedPrimaryAutoQuantity,
        hasAutoCalculatedInverterQuantity:
          resolvedHasAutoCalculatedInverterQuantity,
        additionalInverters: persistedAdditionalInverters,
        limitReached,
      });

      const response = await API.update(
        pid,
        this.withCurrentProposalVersion(persistence),
        true
      );
      this.recordCurrentProposalVersion(response);
    } catch (e) {
      console.warn('Failed to persist solar-production results:', e);
    }
  };

  /**
   * Debounced version of handlePanelSelectionChange
   * Delays API call to consolidate rapid panel toggles
   */
  debouncedHandlePanelSelectionChange = () => {
    this.markProposalDirty();
    this.bumpProductionRequestSeq();
    const timer = this.schedulePanelSelectionChange();
    if (!this.isUnmounted) {
      this.setState({ productionDebounceTimer: timer });
    }
  };

  emitProductionPayload = (
    productionPayload,
    { emit = true, payloadIsFinal = false } = {}
  ) => {
    const { lead } = this.props;
    const callbackPayload = payloadIsFinal
      ? productionPayload
      : buildProposalProductionCallbackPayload({
        isLegacyProposal: this.state.isLegacyProposal,
        productionPayload,
        annualUsage: lead?.utility?.usage,
        fallbackOffset: this.state.offset,
      });

    if (emit && this.updateCallback) {
      this.updateCallback('Production', callbackPayload);
    }
    return callbackPayload;
  };

  emitFinalProductionPayload = (
    callbackPayload,
    isActive = () => true
  ) => {
    if (!isActive()) return null;
    this.emitProductionPayload(callbackPayload, { payloadIsFinal: true });
    if (isActive()) this.emitShadingValuesPerArray(isActive);
    return callbackPayload;
  };

  emitProductionCallback = ({
    deferEmit = false,
    annualKwh,
    monthlyKwh,
    systemSizeKw,
    selectedPanelCount,
    selectedPanelsPerRoof,
    productionPerPanel,
    productionPerString,
    strings,
    invertersUsed,
    monthlyClippingKwh,
    clippingKwh,
    dcAcRatio,
    panelCount,
    mpptCountTotal,
    perRoofAnnualKwh,
    perRoofMonthlyAc,
    perRoofMonthlyPoa,
    perRoofClippingKwh,
  } = {}) => {
    const { lead } = this.props;
    const { design, designData, selectedPanels } = this.state;
    const importedProduction = getVerifiedLegacyProductionSnapshot({
      design,
      designData,
      selectedPanels,
    });
    if (this.state.isLegacyProposal && importedProduction) {
      const callbackPayload = this.emitProductionPayload(importedProduction, {
        emit: !deferEmit,
      });
      if (!deferEmit) this.emitShadingValuesPerArray();
      return callbackPayload;
    }
    const productionPayload = buildReportProductionCallback({
      state: this.state,
      annualUsage: lead?.utility?.usage,
      panelData: this.canvas3DElement?.panelData,
      overrides: {
        annualKwh: annualKwh ?? undefined,
        monthlyKwh: monthlyKwh ?? undefined,
        systemSizeKw: systemSizeKw ?? undefined,
        selectedPanelCount: selectedPanelCount ?? undefined,
        selectedPanelsPerRoof: selectedPanelsPerRoof ?? undefined,
        productionPerPanel: productionPerPanel ?? undefined,
        productionPerString: productionPerString ?? undefined,
        strings: strings ?? undefined,
        invertersUsed: invertersUsed ?? undefined,
        monthlyClippingKwh: monthlyClippingKwh ?? undefined,
        clippingKwh: clippingKwh ?? undefined,
        dcAcRatio: dcAcRatio ?? undefined,
        panelCount: panelCount ?? undefined,
        mpptCountTotal: mpptCountTotal ?? undefined,
        perRoofAnnualKwh,
        perRoofMonthlyAc,
        perRoofMonthlyPoa,
        perRoofClippingKwh,
      },
    });

    const callbackPayload = this.emitProductionPayload(productionPayload, {
      emit: !deferEmit,
    });
    if (!deferEmit) this.emitShadingValuesPerArray();
    return callbackPayload;
  };

  getLegacyProductionFailureReason = (error) => {
    return (
      getLegacyProductionResponseReason(error) ||
      'legacy_production_recalculation_failed'
    );
  };

  emitProductionError = (
    message = 'Error in calculating production',
    reason = null
  ) => {
    if (this.props.iframe && this.updateCallback) {
      this.updateCallback('Error', {
        code: '51',
        message,
        ...(reason ? { reason } : {}),
      });
    }
  };

  calculateProduction = async ({ onlySelectedPanels } = {}) => {
    const selectedPanels = this.state.selectedPanels || {};
    const selectedPanelCount = Object.values(selectedPanels).reduce(
      (total, roofPanels) =>
        total +
        (Array.isArray(roofPanels)
          ? roofPanels.filter((panel) => Number(panel) === 1).length
          : 0),
      0
    );

    if (onlySelectedPanels !== false && selectedPanelCount > 0) {
      return this.handlePanelSelectionChange();
    }

    return this.handleOptimalPanelSelection();
  };

  shadingValues = () => {
    const { design } = this.state;
    if (this.updateCallback) {
      this.updateCallback('ShadingValues', buildShadingValuesPerRoof(design));
    }
  };

  pitchAndAzimuith = () => {
    const { design } = this.state;

    if (this.updateCallback) {
      this.updateCallback(
        'RoofOrientation',
        buildRoofOrientationValues(design)
      );
    }
  };

  panelOrientation = () => {
    const { design, designData, selectedPanels } = this.state;

    const { segments } = designData;

    let pos = design.map(({ id, horizontal, vertical, panels }, idx) => {
      let selectedCount = 0;
      let hc = 0;
      let vc = 0;
      selectedPanels[id].map((s, si) => {
        if (Number(s) === 1) {
          selectedCount += 1;
          if (panels[si].height < panels[si].width) {
            hc += 1;
          } else {
            vc += 1;
          }
        }
      });

      if (selectedCount > 0) {
        if ((hc > 0) & (vc > 0)) {
          return { idx, orientation: 'combination' };
        } else if ((hc > 0) & (vc === 0)) {
          return { idx, orientation: 'horizontal' };
        } else if ((hc === 0) & (vc > 0)) {
          return { idx, orientation: 'vertical' };
        }
      } else return { idx, orientation: 'N/A' };
    });

    if (this.updateCallback) {
      this.updateCallback('PanelOrientationPerArray', pos);
    }
    return pos;
  };

  productionPerPanel = async () => {
    const {
      proposalWithShadings,
      panel,
      design,
      selectedPanels,
      panelShadings: statePanelShadings,
      monthlyPanelShadings: stateMonthlyPanelShadings,
    } = this.state;

    if (!this.updateCallback) return;

    const totalSelected = Object.values(selectedPanels || {}).reduce(
      (sum, arr) => sum + (Array.isArray(arr) ? arr.filter((v) => v === 1).length : 0),
      0
    );
    const annualKwhTotal = Number(this.state.solarProduction || 0);
    const annualPerPanel = totalSelected > 0 ? annualKwhTotal / totalSelected : 0;

    const productionPerPanel = (design || []).map((seg, idx) => {
      const arr = selectedPanels?.[seg.id] || [];
      const roofPanels = Array.isArray(arr)
        ? arr.map((s) => (s === 1 ? annualPerPanel : 0))
        : [];
      return { idx, roofPanels };
    });

    this.updateCallback('ProductionPerPanel', productionPerPanel);
  };

  shadingValuesPerArray = async () => {
    await this.emitShadingValuesPerArray();
  };

  hourlyProduction = async () => {
    // Use new solar-production API instead of PVWattsHourlyAPI
    const { totalPanels } = this.state;

    if (totalPanels > 0) {
      // Panels are already selected, recalculate production for selected panels
      await this.handlePanelSelectionChange();
    } else {
      // No panels selected, do optimal panel selection
      await this.handleOptimalPanelSelection();
    }
  };

  // Rebuild source-blank legacy production with the legacy hourly API.
  hourlyProductionLegacy = async (isActive = () => true) => {
    if (!isActive()) return false;
    try {
      const started = await this.setStateAsyncIfActive(isActive, {
        hourlyCalcIsRunning: true,
      });
      if (!started || !isActive()) return false;
      const { pid, PVWattsHourlyAPI, hardware } = this.props;
      const {
        panel,
        inverter,
        additionalInverters,
        selectedPanels,
        design,
        designData,
      } = this.state;

      const panelkW = panel.power / 1000;
      const selectedPanelSegments = buildLegacyHourlyPanelTopology({
        design,
        designData,
        selectedPanels,
      });
      const panelsOn = selectedPanelSegments.map(
        ({ selected_panels: selectedPanelCount }) => selectedPanelCount
      );
      const totalPanels = panelsOn.reduce((a, b) => a + b, 0);
      const systemSize = totalPanels * panelkW;

      // Auto-update inverter quantity for micro inverters (must match panel count)
      if (
        inverter &&
        inverter.type === 'M' &&
        this.state.inverterQuantity !== totalPanels
      ) {
        const inverterStateApplied = await this.setStateAsyncIfActive(
          isActive,
          { inverterQuantity: totalPanels }
        );
        if (!inverterStateApplied || !isActive()) return false;
      }

      // Ensure we have required inverter data before making PVWatts call
      if (!inverter) {
        console.warn('PVWattsHourlyAPI: No inverter selected');
        return;
      }

      const raw = await PVWattsHourlyAPI.create({
        pid,
        panel,
        inverter,
        inverter_quantity: this.state.inverterQuantity || 1,
        ...(inverter?.type === 'M' || inverter?.type === 'Micro'
          ? {}
          : {
            additional_inverters: this.toPersistedAdditionalInverters(
              additionalInverters
            ),
          }),
        selected_panels: panelsOn,
        selected_panel_segments: selectedPanelSegments,
        system_size: systemSize,
      });
      if (!isActive()) return false;

      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const hourlyAC = parsed?.ac;
      const hourlyTemp = parsed?.tamb;
      if (!hourlyAC || !hourlyTemp) {
        await this.setStateAsyncIfActive(isActive, {
          hourlyCalcIsRunning: false,
        });
        return false;
      }

      const panelMetrics = await this.calcPanelMetrics();
      if (!isActive()) return false;
      const monthly = panelMetrics?.monthlyPanelMetrics;
      if (!monthly) {
        await this.setStateAsyncIfActive(isActive, {
          hourlyCalcIsRunning: false,
        });
        return false;
      }

      let minTemp = 0;
      let maxTemp = 0;

      Object.values(hourlyTemp).forEach((arr) => {
        const list = Array.isArray(arr) ? arr : [];
        if (list.length === 0) return;
        const min = Math.min(...list);
        const max = Math.max(...list);
        if (min < minTemp) minTemp = min;
        if (max > maxTemp) maxTemp = max;
      });

      const hourlyProductionPerArray = [];
      Object.values(hourlyAC).forEach((rawHourliesForSegment) => {
        const d = { idx: hourlyProductionPerArray.length, ac_hourly: {} };
        Object.values(rawHourliesForSegment || {}).forEach((acHoursInMonth, i) => {
          const monthHours = Array.isArray(acHoursInMonth) ? acHoursInMonth : [];
          const monthlyShadingFactor = monthly?.[i + 1]?.[d.idx]?.arraySA || 0;
          d.ac_hourly[i] = monthHours.map((rawHour) => rawHour * monthlyShadingFactor);
        });
        hourlyProductionPerArray.push(d);
      });

      const updatedDesign = Array.isArray(design) ? [...design] : [];
      hourlyProductionPerArray.forEach((el, i) => {
        const acMonthly = new Array(12).fill(0);
        Object.values(el.ac_hourly || {}).forEach((elh, j) => {
          const list = Array.isArray(elh) ? elh : [];
          acMonthly[j] = list.reduce((a, b) => a + b, 0) / 1000;
        });
        const newAcAnnual = acMonthly.reduce((a, b) => a + b, 0);
        if (updatedDesign[i]) {
          updatedDesign[i].acMonthly = acMonthly;
          if (newAcAnnual > 0) updatedDesign[i].acAnnual = newAcAnnual;
        }
      });

      const monthlyProduction = new Array(12).fill(0);
      updatedDesign.forEach((seg) => {
        const m = Array.isArray(seg?.acMonthly) ? seg.acMonthly : [];
        for (let i = 0; i < 12; i += 1) {
          monthlyProduction[i] += Number(m[i] || 0);
        }
      });
      const solarProduction = monthlyProduction.reduce(
        (a, b) => a + Number(b || 0),
        0
      );

      const productionStateApplied = await this.setStateAsyncIfActive(isActive, {
        design: updatedDesign,
        ambientTemp: { min: minTemp, max: maxTemp },
        hourlyCalcIsRunning: false,
        systemSize,
        totalPanels,
        solarProduction,
        monthlyProduction,
        needsSolarProductionRecalc: false,
        productionIsCalculating: false,
      });
      if (!productionStateApplied || !isActive()) return false;

      if (this.updateCallback && isActive()) {
        this.updateCallback('HourlyProduction', hourlyProductionPerArray);
      }
      return true;
    } catch (err) {
      if (!isActive()) return false;
      const reason = this.getLegacyProductionFailureReason(err);
      this.legacyProductionHydrationError = reason;
      console.error('Legacy hourly production failed', {
        proposalId: this.props.pid,
        reason,
        status: err?.response?.status || null,
      });
      await this.setStateAsyncIfActive(isActive, {
        hourlyCalcIsRunning: false,
      });
      throw err;
    }
  };

  autoString = async (type = 'nearest') => {
    try {
      const { pid, lead, currentUser, StringingAPI } = this.props;
      const { selectedPanels, design, panel, inverter, ambientTemp } =
        this.state;

      StringingAPI.create({
        pid: pid,
        selected_panels: selectedPanels,
        segments: design,
        // module: {
        //   Voc_STC: panel?.v_oc,
        //   dVoc_dT: panel?.temp_coeff_v_oc
        // },
        panel,
        // inverter: {
        //   Vdcmax: inverter?.max_dc_voltage
        // },
        inverter,
        ambient_temp: ambientTemp,
        type: type,
      }).then((data) => {
        const handleDownload = () => {
          const imageUrl = `/media/${currentUser.company.id}/${lead.uid}/string_plot.png`;
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = 'image.jpg'; // You can specify the file name here
          link.click();
        };
        if (currentUser?.company?.id === 'AERIAL') {
          console.log(data);
          handleDownload();
        }

        if (this.updateCallback) {
          this.updateCallback('proposal.autoString', data);
        }
      });
    } catch (err) {
      console.log(`error in suto stringing: ${err}`);
    }
  };

  calcSavings = async (calculateHourly = false) => {
    const hasProduction =
      (this.state.solarProduction || 0) > 0 &&
      Array.isArray(this.state.monthlyProduction) &&
      this.state.monthlyProduction.some((v) => Number(v) > 0);

    if (!hasProduction && calculateHourly) {
      await this.hourlyProduction();
      return;
    }

    await this.calcSavingsFromAPIProduction();
  };

  calcCosts = async () => {
    const {
      lead: { property: ppt, address: leadAddress, utility },
      currentUser,
      hardware,
      iframe,
    } = this.props;
    const {
      totalPanels,
      systemSize,
      yearlySavings,
      permitCost,
      discountAmount,
      loan,
      loanSwitch,
    } = this.state;
    // installation settings cost per watt
    const {
      designData,
      panel,
      inverter,
      additionalInverters,
      mounting,
      monitoring,
      adders,
      roofType,
    } = this.state;

    const data = await calculateCosts(
      ppt,
      leadAddress,
      currentUser,
      hardware,
      designData.selectedPackage,
      iframe,
      totalPanels,
      systemSize,
      yearlySavings,
      permitCost,
      discountAmount,
      loan,
      loanSwitch,
      panel,
      inverter,
      additionalInverters,
      mounting,
      monitoring,
      adders,
      roofType
    );

    if (!data) {
      return;
    }

    const { lifetimeSavings } = data;

    // Emit legacy cost summary callback for backwards compatibility with existing integrations.
    if (this.updateCallback) {
      this.updateCallback('ProposalCosts', {
        estimatedYearlySavings: yearlySavings[0],
        estimated25YearSavings: yearlySavings[24],
        systemSize,
        yearlySavings,
        totalPanels,
        ...data,
      });
    }

    this.setState({ ...data }, () => {
      this.loanCalculator();
    });
  };

  /**
   * Calculate savings using production values from solar-production API
   * This replaces the production calculation part of calcSavings but still
   * calculates financial savings (yearlySavings, lifetimeSavings, payback)
   */
  calcSavingsFromAPIProduction = async (isActive = () => true) => {
    if (!isActive()) return false;
    const {
      lead: { utility, property },
    } = this.props;
    const {
      systemSize,
      solarProduction,
      monthlyProduction,
      totalPanels,
      panel,
      escalation,
      totalCost,
    } = this.state;

    // Validate monthlyProduction and create fallback if needed
    let monthlyProductionToUse = monthlyProduction;
    const hasValidMonthlyProduction = Array.isArray(monthlyProduction) &&
      monthlyProduction.length === 12 &&
      monthlyProduction.some((v) => Number(v) > 0);

    if (!hasValidMonthlyProduction) {
      if (!solarProduction || solarProduction === 0) {
        // No production data at all - skip calculation
        return;
      }

      // Fallback: divide annual by 12
      console.error('[DEVELOPER] calcSavingsFromAPIProduction: monthlyProduction is invalid. Using equal monthly distribution (annual/12) as fallback. This may result in inaccurate net metering calculations.', {
        monthlyProduction,
        solarProduction,
        systemSize,
      });
      monthlyProductionToUse = new Array(12).fill(solarProduction / 12);
    }

    // Get monthly consumption from utility
    const monthlyConsumption = Array.isArray(utility.usage)
      ? utility.usage.map((usage) => Number(usage))
      : new Array(12).fill(Number(utility.totalUsage || 0) / 12);

    // Calculate savings based on billing type
    let savingsResults;
    let yearlySavingsToUse = [];
    let lifetimeSavingsToUse = [];

    try {
      const [providers, updatedUtility, rate] = await getRates(
        property,
        utility
      );
      if (!isActive()) return false;

      // Merge updatedUtility with original utility to ensure all required fields are available
      // getUtilityRate returns fixedCharge (camelCase) but calculation functions expect fixed_charge (snake_case)
      // Also preserve rate_amount from original utility as getUtilityRate doesn't set it
      const utilityToUse = updatedUtility
        ? {
          ...utility,  // Start with original utility (preserves fixed_charge, rate_amount, billing specs)
          ...updatedUtility,  // Override with updated utility (has rate structure, etc.)
          // Convert camelCase to snake_case for fixedCharge
          fixed_charge: updatedUtility.fixed_charge || updatedUtility.fixedCharge || utility.fixed_charge || utility.fixedCharge,
          // Preserve rate_amount from original (getUtilityRate doesn't set this)
          rate_amount: updatedUtility.rate_amount || utility.rate_amount || updatedUtility.effectiveRate || utility.effectiveRate,
        }
        : utility;

      if (!utility.billing_type) {
        // Legacy path: `null-billing-type-savings-calculation.md` — annual
        // degraded kWh, raw savings + calculateEscalation; LegacySavingsTable
        // mirrors the same numbers passed to calculateCosts.
        // Use the API-derived monthly production directly. If `solarProduction`
        // disagrees with the sum of `monthlyProductionToUse` (e.g. because we
        // fell back to `solarProduction / 12` above), scale the monthly array
        // to match annual so the calculator produces the same annual total a
        // direct annual computation would.
        const monthlyProductionSum = monthlyProductionToUse.reduce(
          (a, b) => a + Number(b),
          0
        );
        const annualProductionForLegacy =
          Number(solarProduction) > 0
            ? Number(solarProduction)
            : monthlyProductionSum;
        const monthlyProductionForLegacy =
          monthlyProductionSum > 0 &&
            Math.abs(monthlyProductionSum - annualProductionForLegacy) > 1
            ? monthlyProductionToUse.map(
              (v) => (Number(v) * annualProductionForLegacy) / monthlyProductionSum
            )
            : monthlyProductionToUse;

        savingsResults = calculateLegacySavingsV2({
          monthlyProduction: monthlyProductionForLegacy,
          monthlyConsumption,
          systemSize,
          utility: utilityToUse,
          panel,
          escalation,
        });

        yearlySavingsToUse = savingsResults.yearlyResults
          .slice(0, 30)
          .reduce((acc, year, index) => {
            const currentYearSavings = year.annualTotals.savings;
            acc[index] =
              index === 0
                ? currentYearSavings
                : acc[index - 1] + currentYearSavings;
            return acc;
          }, []);

        lifetimeSavingsToUse = yearlySavingsToUse.map(
          (value) => value - totalCost
        );

      } else if (utility.billing_type === 'net_billing') {
        // Use Net Billing calculations
        savingsResults = calculateNetBillingV2({
          monthlyProduction: monthlyProductionToUse,
          monthlyConsumption,
          systemSize,
          utility: utilityToUse,
          panel,
          escalation,
        });

        yearlySavingsToUse = savingsResults.yearlyResults
          .slice(0, 30)
          .reduce((acc, year, index) => {
            const currentYearSavings = year.annualTotals.savings;
            acc[index] =
              index === 0
                ? currentYearSavings
                : acc[index - 1] + currentYearSavings;
            return acc;
          }, []);

        lifetimeSavingsToUse = yearlySavingsToUse.map(
          (value) => value - totalCost
        );
      } else if (utility.net_metering_specs?.metering_period === 'monthly') {
        // Use Monthly Net Metering calculations
        savingsResults = calculateMonthlyNetMeteringV2({
          monthlyProduction: monthlyProductionToUse,
          monthlyConsumption,
          systemSize,
          utility: utilityToUse,
          panel,
          escalation,
        });

        yearlySavingsToUse = savingsResults.yearlyResults
          .slice(0, 30)
          .reduce((acc, year, index) => {
            const currentYearSavings = year.annualTotals.savings;
            acc[index] =
              index === 0
                ? currentYearSavings
                : acc[index - 1] + currentYearSavings;
            return acc;
          }, []);

        lifetimeSavingsToUse = yearlySavingsToUse.map(
          (value) => value - totalCost
        );
      } else {
        // Use Annual Net Metering calculations (default)
        savingsResults = calculateAnnualNetMeteringV2({
          monthlyProduction: monthlyProductionToUse,
          monthlyConsumption,
          systemSize,
          utility: utilityToUse,
          panel,
          escalation,
        });

        yearlySavingsToUse = savingsResults.yearlyResults
          .slice(0, 30)
          .reduce((acc, year, index) => {
            const currentYearSavings = year.annualTotals.savings;
            acc[index] =
              index === 0
                ? currentYearSavings
                : acc[index - 1] + currentYearSavings;
            return acc;
          }, []);

        lifetimeSavingsToUse = yearlySavingsToUse.map(
          (value) => value - totalCost
        );
      }
    } catch (err) {
      if (!isActive()) return false;
      console.error('[DEVELOPER] calcSavingsFromAPIProduction error:', err);
      // Fallback to simple estimation if calculation functions fail
      const annualSavings = solarProduction * (utility.rate_amount || 0.12);
      yearlySavingsToUse = Array(30)
        .fill(0)
        .map((_, i) => annualSavings * (i + 1));
      lifetimeSavingsToUse = yearlySavingsToUse.map(
        (value) => value - totalCost
      );
    }

    if (!isActive()) return false;
    // Update state with calculated savings
    this.setState(
      () =>
        isActive()
          ? {
            yearlySavings: yearlySavingsToUse,
            lifetimeSavings: lifetimeSavingsToUse,
            netMeteringCalculations: savingsResults,
          }
          : null,
      () => {
        if (isActive()) this.calcCosts();
      }
    );
    return true;
  };

  // calculate discount
  discountApplied = (amount) => {
    const discountAmount = amount;
    this.setState({ discountAmount }, () => this.calcCosts());
  };

  // set scaling
  setScale = (scale = SCALE) => this.setState({ scale });

  syncSelectedPanelFrom2D = () => {
    this.setState(syncReportSelectedPanelFrom2D(this.state));
  };

  syncSelectedPanelFrom3D = (opts = {}) => {
    const skipRecalc =
      Boolean(opts?.skipRecalc) ||
      Boolean(this.state.hydratingSelectionFromStringing);
    const patch = syncReportSelectedPanelFrom3D(this.state);

    this.setState(
      {
        ...patch,
        ...(skipRecalc ? {} : { productionIsCalculating: true }),
      },
      () => {
        if (skipRecalc) {
          this.maybeEmitInitialShadingValuesPerArray();
          return;
        }
        this.debouncedHandlePanelSelectionChange();
      }
    );
  };

  toggleStringingVisibility = () =>
    this.setState((s) => ({ showStringing: !s.showStringing }));

  addToSelectedObjects = (index, objectType, removeOld = false) => {
    if (objectType !== 'panels') return;
    const selectedObjects = addToSelection(
      this.state.selectedObjects,
      index,
      objectType,
      { removeOld }
    );

    if (this.state.hydratingSelectionFromStringing) {
      this.setState({ selectedObjects });
      return;
    }

    this.setState(
      {
        selectedObjects,
        productionIsCalculating: true,
      },
      () => {
        this.syncSelectedPanelFrom3D();
      }
    );
  };

  addToSelectedObjectsBulk = (indices, objectType, clearExisting = false) => {
    const items = Array.isArray(indices) ? indices : [indices];
    if (!items || items.length === 0) return;
    this.addToSelectedObjects(items, objectType, clearExisting);
  };

  removeFromSelectedObjects = (index, objectType) => {
    if (objectType !== 'panels') return;
    const selectedObjects = removeFromSelection(
      this.state.selectedObjects,
      index,
      objectType
    );

    if (this.state.hydratingSelectionFromStringing) {
      this.setState({ selectedObjects });
      return;
    }

    this.setState(
      {
        selectedObjects,
        productionIsCalculating: true,
      },
      () => {
        this.syncSelectedPanelFrom3D();
      }
    );
  };

  // handle toggling specific panels
  panelToggleHandler = (idx, jdx) => {
    const patch = toggleReportPanelSelection(this.state, idx, jdx);
    if (!patch) return;

    this.markProposalDirty();
    this.setState(
      patch,
      () => {
        this.syncSelectedPanelFrom2D();
        // Trigger debounced production recalculation using new API
        this.debouncedHandlePanelSelectionChange();
      }
    );
  };

  // deselect all panels or select optimal panels
  togglePanelSelection = () => {
    const {
      totalPanels,
      design,
      annualUsage,
    } = this.state;

    // Check if there are any panels available in the design
    const totalAvailablePanels =
      design?.reduce((sum, roof) => sum + (roof.panels?.length || 0), 0) || 0;

    if (totalAvailablePanels === 0) {
      // No panels available in design, nothing to toggle
      return;
    }

    this.markProposalDirty();
    if (totalPanels !== 0) {
      const patch = setAllReportPanelsSelection(this.state, false);
      this.setState(
        patch,
        () => {
          this.syncSelectedPanelFrom2D();
          this.calcCosts();
          if (this.updateCallback) {
            const productionPayload = buildReportProductionCallback({
              state: this.state,
              annualUsage,
              panelData: this.canvas3DElement?.panelData,
              overrides: {
                annualKwh: 0,
                monthlyKwh: Array(12).fill(0),
                systemSizeKw: 0,
                selectedPanelCount: 0,
                productionPerPanel: [],
                productionPerString: [],
                strings: null,
                invertersUsed: null,
                monthlyClippingKwh: Array(12).fill(0),
                clippingKwh: 0,
                dcAcRatio: null,
                panelCount: 0,
              },
            });
            this.emitProductionPayload(productionPayload);
            this.emitShadingValuesPerArray();
          }
        }
      );
    } else {
      // Currently no selected panels, select optimal panels using solar-production API
      this.handleOptimalPanelSelection();
    }
  };

  toggleAllPanels = () => {
    const {
      totalPanels,
      annualUsage,
    } = this.state;
    if (totalPanels !== 0) {
      const patch = setAllReportPanelsSelection(this.state, false);
      this.setState(
        patch,
        () => {
          this.syncSelectedPanelFrom2D();
          this.calcCosts();
          if (this.updateCallback) {
            this.updateCallback('proposal.ToggleAllPanels', '');
            const productionPayload = buildReportProductionCallback({
              state: this.state,
              annualUsage,
              panelData: this.canvas3DElement?.panelData,
              overrides: {
                annualKwh: 0,
                monthlyKwh: Array(12).fill(0),
                systemSizeKw: 0,
                selectedPanelCount: 0,
                productionPerPanel: [],
                productionPerString: [],
                strings: null,
                invertersUsed: null,
                monthlyClippingKwh: Array(12).fill(0),
                clippingKwh: 0,
                dcAcRatio: null,
                panelCount: 0,
              },
            });
            this.emitProductionPayload(productionPayload);
            this.emitShadingValuesPerArray();
          }
        }
      );
    } else {
      const patch = setAllReportPanelsSelection(this.state, true);
      this.setState(
        patch,
        () => {
          this.syncSelectedPanelFrom2D();
          // Use solar-production API to calculate for all selected panels
          this.handlePanelSelectionChange();
          if (this.updateCallback) {
            this.updateCallback('proposal.ToggleAllPanels', '');
          }
        }
      );
    }
  };

  toggleViewerMode = () => {
    const { viewerMode } = this.state;
    console.log(`proposal.ToggleViewerMode ${!viewerMode}`);
    this.setState(
      {
        viewerMode: !viewerMode,
      },
      () => {
        if (this.updateCallback) {
          this.updateCallback('proposal.ToggleViewerMode', '');
        }
      }
    );
  };

  handleDownload = async () => {
    try {
      await this.prepareProposalForExport();
      await this.setStateAsync({ visibility: false, showOverlay: false });
      await this.waitForNextPaint();
      await this.downloadPdf();
    } catch (error) {
      console.log(error);
      if (this.props.displaySnack) {
        this.props.displaySnack({
          variant: 'danger',
          message: 'Proposal export failed. Please try again.',
        });
      }
    }
  };

  calcPanelMetrics = async () => {
    const data = {};

    const {
      panelShadings: statePanelShadings,
      monthlyPanelShadings: stateMonthlyPanelShadings,
      design,
      designData,
      selectedPanels,
      panel,
      inverterQuantity,
      additionalInverters,
    } = this.state;

    const { segments } = designData;

    let panelShadings = statePanelShadings;
    let monthlyPanelShadings = stateMonthlyPanelShadings;
    if (!panelShadings || panelShadings.length === 0) {
      ({ panelShadings, monthlyPanelShadings } =
        await this.getPanelShadingData());
    }

    // Check if design is valid before calling map
    if (!Array.isArray(design)) {
      return {}; // Return early to prevent crash
    }

    const lstPanelShadings = design.map((d, di) =>
      panelShadings.filter(([ri]) => ri === di)
    );

    const avgPanelMetrics = design.map(({ id, acMonthly }, idx) => {
      let segID = id.split('-')[id.split('-').length - 1];
      let segment = segments[idx];
      let sumSA = 0;
      let sumTOF = 0;
      let sumTSRF = 0;
      let count = 0;
      let selectedCount = 0;

      if (!selectedPanels[id]) {
        return null; // Prevent crash
      }

      if (!Array.isArray(selectedPanels[id])) {
        return null; // Prevent crash
      }

      try {
        selectedPanels[id].map((s, si) => {
          count += 1;
          if (Number(s) === 1) {
            selectedCount += 1;
          }
          if (lstPanelShadings[idx][si]) {
            sumSA += s * lstPanelShadings[idx][si][6];
            sumTOF += s * lstPanelShadings[idx][si][7];
            sumTSRF += s * lstPanelShadings[idx][si][8];
          }
        });
      } catch (error) {
        return null; // Prevent crash
      }
      count = count - 1;
      if (selectedCount > 0) {
        let arraySA = sumSA / selectedCount;
        let arrayTOF = sumTOF / selectedCount;
        let arrayTSRF = sumTSRF / selectedCount;
        let arrayProduction =
          arraySA *
          selectedCount *
          (panel?.power / 1000) *
          acMonthly.reduce((a, b) => a + b, 0);
        arraySA = arraySA.toFixed(2);
        arrayTOF = arrayTOF.toFixed(2);
        arrayTSRF = arrayTSRF.toFixed(2);
        return {
          idx,
          selected: selectedCount,
          production: arrayProduction,
          pitch: segment ? Number(segment?.pitch).toFixed(2) : 'NaN',
          azimuth: segment ? Number(segment?.azimuth).toFixed(2) : 'NaN',
          arraySA,
          arrayTOF,
          arrayTSRF,
        };
      } else {
        let arraySA = 0;
        let arrayTOF = 0;
        let arrayTSRF = 0;
        return {
          idx,
          selected: selectedCount,
          pitch: segment ? Number(segment?.pitch).toFixed(2) : 'NaN',
          azimuth: segment ? Number(segment?.azimuth).toFixed(2) : 'NaN',
          arraySA,
          arrayTOF,
          arrayTSRF,
        };
      }
    });

    const avgMonthlyPanelMetrics = {};
    for (let i = 1; i <= 12; i += 1) {
      const monthlyShadingsForMonth = Array.isArray(monthlyPanelShadings?.[i])
        ? monthlyPanelShadings[i]
        : [];
      const lstMonthlyPanelShadings = design.map((d, di) =>
        monthlyShadingsForMonth.filter(([ri]) => ri === di)
      );
      const avgMPanelMetrics = design.map(({ id, acMonthly }, idx) => {
        let segID = id.split('-')[id.split('-').length - 1];
        let segment = segments[idx];
        let sumSA = 0;
        let sumTOF = 0;
        let sumTSRF = 0;
        let count = 0;
        let selectedCount = 0;
        if (!Array.isArray(selectedPanels?.[id])) {
          return null;
        }

        selectedPanels[id].map((s, si) => {
          count += 1;
          if (Number(s) === 1) {
            selectedCount += 1;
          }
          if (lstMonthlyPanelShadings[idx][si]) {
            sumSA += s * lstMonthlyPanelShadings[idx][si][6];
            sumTOF += s * lstMonthlyPanelShadings[idx][si][7];
            sumTSRF += s * lstMonthlyPanelShadings[idx][si][8];
          }
        });
        count = count - 1;
        if (selectedCount > 0) {
          let arraySA = sumSA / selectedCount;
          let arrayTOF = sumTOF / selectedCount;
          let arrayTSRF = sumTSRF / selectedCount;
          let arrayProduction =
            arraySA * selectedCount * (panel?.power / 1000) * acMonthly[i - 1];
          arraySA = arraySA.toFixed(2);
          arrayTOF = arrayTOF.toFixed(2);
          arrayTSRF = arrayTSRF.toFixed(2);
          return {
            idx,
            selected: selectedCount,
            production: arrayProduction,
            pitch: segment ? Number(segment?.pitch).toFixed(2) : 'NaN',
            azimuth: segment ? Number(segment?.azimuth).toFixed(2) : 'NaN',
            arraySA,
            arrayTOF,
            arrayTSRF,
          };
        } else {
          let arraySA = 0;
          let arrayTOF = 0;
          let arrayTSRF = 0;
          let arrayProduction = 0;
          return {
            idx,
            selected: selectedCount,
            production: arrayProduction,
            pitch: segment ? Number(segment?.pitch).toFixed(2) : 'NaN',
            azimuth: segment ? Number(segment?.azimuth).toFixed(2) : 'NaN',
            arraySA,
            arrayTOF,
            arrayTSRF,
          };
        }
      });
      avgMonthlyPanelMetrics[i] = avgMPanelMetrics;
    }

    data['panelMetrics'] = avgPanelMetrics;
    data['monthlyPanelMetrics'] = avgMonthlyPanelMetrics;
    return data;
  };

  handleShadingReportDownload = async (params = null, showModal = true) => {
    const shadingReportData = {};

    const {
      panelShadings: statePanelShadings,
      monthlyPanelShadings: stateMonthlyPanelShadings,
      design,
      designData,
      selectedPanels,
      panel,
    } = this.state;

    const { segments } = designData;

    let panelShadings = statePanelShadings;
    let monthlyPanelShadings = stateMonthlyPanelShadings;
    if (!panelShadings || panelShadings.length === 0) {
      ({ panelShadings, monthlyPanelShadings } =
        await this.getPanelShadingData({
          waitForTextures: !this.props?.iframe,
        }));
    }

    const lstPanelShadings = design.map((d, di) =>
      panelShadings.filter(([ri]) => ri === di)
    );

    const avgPanelMetrics = design.map(({ id, acMonthly }, idx) => {
      let segID = id.split('-')[id.split('-').length - 1];
      let segment = segments[idx];
      let sumSA = 0;
      let sumTOF = 0;
      let sumTSRF = 0;
      let count = 0;
      let selectedCount = 0;

      // Check if selectedPanels[id] exists before calling map
      if (!selectedPanels[id]) {
        return null; // Return early to prevent crash
      }

      if (!Array.isArray(selectedPanels[id])) {
        return null; // Return early to prevent crash
      }

      const roofPanels = selectedPanels[id].map((s, si) => {
        count += 1;
        if (Number(s) === 1) {
          selectedCount += 1;
        }
        if (lstPanelShadings[idx][si]) {
          sumSA += s * lstPanelShadings[idx][si][6];
          sumTOF += s * lstPanelShadings[idx][si][7];
          sumTSRF += s * lstPanelShadings[idx][si][8];
        }
      });
      count = count - 1;
      if (selectedCount > 0) {
        let arraySA = sumSA / selectedCount;
        let arrayTOF = sumTOF / selectedCount;
        let arrayTSRF = sumTSRF / selectedCount;
        let arrayProduction =
          arraySA *
          selectedCount *
          (panel?.power / 1000) *
          acMonthly.reduce((a, b) => a + b, 0);
        arraySA = arraySA.toFixed(2);
        arrayTOF = arrayTOF.toFixed(2);
        arrayTSRF = arrayTSRF.toFixed(2);
        return {
          id,
          panelCount: count,
          selected: selectedCount,
          production: arrayProduction,
          pitch: segment ? Number(segment?.pitch).toFixed(2) : 'NaN',
          azimuth: segment ? Number(segment?.azimuth).toFixed(2) : 'NaN',
          arraySA,
          arrayTOF,
          arrayTSRF,
        };
      } else {
        let arraySA = 0;
        let arrayTOF = 0;
        let arrayTSRF = 0;
        let arrayProduction = 0;
        return {
          id,
          panelCount: count,
          selected: selectedCount,
          production: arrayProduction,
          pitch: segment ? Number(segment?.pitch).toFixed(2) : 'NaN',
          azimuth: segment ? Number(segment?.azimuth).toFixed(2) : 'NaN',
          arraySA,
          arrayTOF,
          arrayTSRF,
        };
      }
    });

    const avgMonthlyPanelMetrics = {};
    for (let i = 1; i <= 12; i += 1) {
      const monthlyShadingsForMonth = Array.isArray(monthlyPanelShadings?.[i])
        ? monthlyPanelShadings[i]
        : [];
      const lstMonthlyPanelShadings = design.map((d, di) =>
        monthlyShadingsForMonth.filter(([ri]) => ri === di)
      );
      const avgMPanelMetrics = design.map(({ id, acMonthly }, idx) => {
        let segID = id.split('-')[id.split('-').length - 1];
        let segment = segments[idx];
        let sumSA = 0;
        let sumTOF = 0;
        let sumTSRF = 0;
        let count = 0;
        let selectedCount = 0;
        if (!Array.isArray(selectedPanels?.[id])) {
          return null;
        }

        selectedPanels[id].map((s, si) => {
          count += 1;
          if (Number(s) === 1) {
            selectedCount += 1;
          }
          if (lstMonthlyPanelShadings[idx][si]) {
            sumSA += s * lstMonthlyPanelShadings[idx][si][6];
            sumTOF += s * lstMonthlyPanelShadings[idx][si][7];
            sumTSRF += s * lstMonthlyPanelShadings[idx][si][8];
          }
        });
        count = count - 1;
        if (selectedCount > 0) {
          let arraySA = sumSA / selectedCount;
          let arrayTOF = sumTOF / selectedCount;
          let arrayTSRF = sumTSRF / selectedCount;
          let arrayProduction =
            arraySA * selectedCount * (panel?.power / 1000) * acMonthly[i - 1];
          arraySA = arraySA.toFixed(2);
          arrayTOF = arrayTOF.toFixed(2);
          arrayTSRF = arrayTSRF.toFixed(2);
          return {
            id,
            panelCount: count,
            selected: selectedCount,
            production: arrayProduction,
            pitch: segment ? Number(segment?.pitch).toFixed(2) : 'NaN',
            azimuth: segment ? Number(segment?.azimuth).toFixed(2) : 'NaN',
            arraySA,
            arrayTOF,
            arrayTSRF,
          };
        } else {
          let arraySA = 0;
          let arrayTOF = 0;
          let arrayTSRF = 0;
          let arrayProduction = 0;
          return {
            id,
            panelCount: count,
            selected: selectedCount,
            production: arrayProduction,
            pitch: segment ? Number(segment?.pitch).toFixed(2) : 'NaN',
            azimuth: segment ? Number(segment?.azimuth).toFixed(2) : 'NaN',
            arraySA,
            arrayTOF,
            arrayTSRF,
          };
        }
      });
      avgMonthlyPanelMetrics[i] = avgMPanelMetrics;
    }

    shadingReportData['panelMetrics'] = avgPanelMetrics;
    shadingReportData['monthlyPanelMetrics'] = avgMonthlyPanelMetrics;

    this.setState({ visibility: false });
    this.canvas3DElement.toggleView('shading', false, true);
    await new Promise((r) => setTimeout(r, 500));
    const {
      url: canvas2DJPG,
      width: canvas3DJPGWidth,
      height: canvas3DJPGHeight,
    } = await this.canvas3DElement.exportToJPG();
    await new Promise((r) => setTimeout(r, 500));
    this.canvas3DElement.toggleView('shading', true, false);
    await new Promise((r) => setTimeout(r, 500));
    const {
      url: canvas3DJPG1,
      width: canvas3DJPGWidth1,
      height: canvas3DJPGHeight1,
    } = await this.canvas3DElement.exportToJPG(2, [70, 0, 60]);
    const {
      url: canvas3DJPG2,
      width: canvas3DJPGWidth2,
      height: canvas3DJPGHeight2,
    } = await this.canvas3DElement.exportToJPG(2, [-70, 0, 60]);

    shadingReportData['canvas2DJPG'] = canvas2DJPG;
    shadingReportData['canvas3DJPG1'] = canvas3DJPG1;
    shadingReportData['canvas3DJPG2'] = canvas3DJPG2;

    if (showModal === false) {
      [
        shadingReportData['canvas2DJPG'],
        shadingReportData['canvas3DJPG1'],
        shadingReportData['canvas3DJPG2'],
      ] = await Promise.all(
        [canvas2DJPG, canvas3DJPG1, canvas3DJPG2].map(
          this.toPersistentImageUrl
        )
      );
    }

    const {
      iframe,
      lead: { homeowners, address, property },
      currentUser: advisor,
      fullAddress,
      hardware,
    } = this.props;

    // if (!iframe) {
    //   const contact = homeowners[0];
    //   const customerName = `${contact?.first_name} ${contact?.last_name}`;
    //   const addressTop = address?.street;
    //   const addressBottom = `${address?.city}, ${address?.state}`;

    //   // solar advisor
    //   const company = advisor.company.name;
    //   const advisorName = `${advisor.first_name} ${advisor.last_name}`;

    //   shadingReportData['homeowner'] = customerName;
    //   shadingReportData['coordinates'] = property?.coordinates;
    //   shadingReportData['address'] = [addressTop, addressBottom];
    //   shadingReportData['client'] = company;
    //   shadingReportData['designer'] = advisorName;

    //   let options = { year: 'numeric', month: 'long', day: 'numeric' };
    //   let today  = new Date();

    //   shadingReportData['date'] = today.toLocaleDateString("en-US", options)
    // } else {
    //   // pass
    // }
    const contact = homeowners[0];
    const customerName = `${contact?.first_name} ${contact?.last_name}`;
    const addressTop = address?.street;
    const addressBottom = `${address?.city}, ${address?.state}`;

    // solar advisor
    const company = advisor.company.name;
    const advisorName = `${advisor.first_name} ${advisor.last_name}`;

    shadingReportData['homeowner'] = customerName;
    shadingReportData['coordinates'] = property?.coordinates;
    shadingReportData['address'] = `${addressTop}, ${addressBottom}`;
    shadingReportData['client'] = company;
    // shadingReportData['designer'] = advisorName;

    if (iframe && params && params?.homeowner) {
      shadingReportData['homeowner'] = params?.homeowner;
      shadingReportData['coordinates'] = [
        params?.coordinates.split(',')[0].trim(),
        params?.coordinates.split(',')[1].trim(),
      ];
      shadingReportData['address'] = params?.address;
      shadingReportData['client'] = params?.client;
    }

    let options = { year: 'numeric', month: 'long', day: 'numeric' };
    let today = new Date();

    shadingReportData['date'] = today.toLocaleDateString('en-US', options);

    // Add panel information to shading report data
    shadingReportData['totalPanels'] = this.state.totalPanels || 0;
    shadingReportData['systemSize'] = this.state.systemSize || 0;
    shadingReportData['dcAcRatio'] = this.state.dcAcRatio || null;
    shadingReportData['proposalStateSignature'] =
      createProposalShadingReportSignature(this.state);

    let modal = 'shading-report';
    if (showModal === false) modal = null;

    await this.setStateAsync({
      shadingReportData,
      modal,
      visibility: true,
    });
    return shadingReportData;
  };

  dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    // eslint-disable-next-line no-plusplus
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  blobToDataURL = (blob, callback) => {
    const a = new FileReader();
    a.onload = (e) => {
      callback(e.target.result);
    };
    a.readAsDataURL(blob);
  };

  blobToDataURLAsync = (blob) =>
    new Promise((resolve, reject) => {
      try {
        this.blobToDataURL(blob, resolve);
      } catch (error) {
        reject(error);
      }
    });

  toPersistentImageUrl = async (url) => {
    if (!url || typeof url !== 'string' || url.startsWith('data:')) {
      return url;
    }

    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const dataUrl = await this.blobToDataURLAsync(res.data);
      if (
        url.startsWith('blob:') &&
        typeof URL !== 'undefined' &&
        typeof URL.revokeObjectURL === 'function'
      ) {
        URL.revokeObjectURL(url);
      }
      return dataUrl;
    } catch (error) {
      console.log(error);
      return url;
    }
  };

  waitForElement = (selector, timeoutMs = 4000) =>
    new Promise((resolve) => {
      const existingElement = document.querySelector(selector);
      if (existingElement) {
        resolve(existingElement);
        return;
      }

      const rootElement = document.body || document.documentElement;
      if (!rootElement) {
        resolve(null);
        return;
      }

      let timeoutId;
      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (!element) return;

        clearTimeout(timeoutId);
        observer.disconnect();
        resolve(element);
      });

      timeoutId = setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, timeoutMs);

      observer.observe(rootElement, { childList: true, subtree: true });
    });


  waitForCaptureCanvas = async (requiredMethods) => {
    const canvas3D = await waitForCanvas3DReady(() => this.canvas3DElement, {
      requiredMethods,
    });

    if (!canvas3D) {
      throw new Error('Canvas3D is not ready for capture.');
    }

    return canvas3D;
  };

  captureProposalDesignImage = async () => {
    const canvas3D = await this.waitForCaptureCanvas(['exportToJPG']);
    const blobClient = axios.create();

    return captureRequiredCanvasExportImage(canvas3D, {
      assetName: 'rgbImage',
      fetchBlob: async (url) => {
        const response = await blobClient.get(url, {
          responseType: 'blob',
        });
        return response.data;
      },
      blobToDataURL: this.blobToDataURLAsync,
    });
  };

  captureProposalImages = async () => {
    const [chartImages, rgbImage] = await Promise.all([
      getImages(['paybackChart', 'solarGraphChart']),
      this.captureProposalDesignImage(),
    ]);

    return { ...chartImages, rgbImage };
  };

  captureThumbnail = async () => {
    try {
      const canvas3D = await this.waitForCaptureCanvas(['exportThumbnail']);

      // Step 1: Get the canvas image in base64 format
      const { base64 } = await canvas3D.exportThumbnail();

      if (!base64) {
        throw new Error(
          'Failed to retrieve canvas image from exportThumbnail.'
        );
      }

      // Step 2: Access the parent container of the canvas
      const parentContainer = document.getElementsByClassName('container3d')[0];

      if (!parentContainer) {
        throw new Error('Parent container not found for the canvas element.');
      }

      // Step 3: Capture the annotations (HTML overlay) using html2canvas
      const annotationsCanvas = await html2canvas(parentContainer, {
        useCORS: true,
        ignoreElements: (element) => {
          console.log(`element.tagName: ${element.tagName}`);
          return element.tagName === 'CANVAS'; // Skip the WebGL canvas
        },
        logging: false,
        scale: 1,
        backgroundColor: null,
      });
      const annotationsDataUrl = annotationsCanvas.toDataURL('image/png');

      // Step 4: Merge the canvas (from base64) and annotations
      const combinedCanvas = document.createElement('canvas');
      const context = combinedCanvas.getContext('2d');

      // Set dimensions for the combined canvas
      combinedCanvas.width = annotationsCanvas.width;
      combinedCanvas.height = annotationsCanvas.height;

      const canvasImage = new Image();
      const annotationsImage = new Image();

      canvasImage.src = base64; // Use the base64 from exportThumbnail
      annotationsImage.src = annotationsDataUrl;

      // Wait for both images to load and then draw them sequentially
      await Promise.all([
        new Promise((resolve) => (canvasImage.onload = resolve)),
        new Promise((resolve) => (annotationsImage.onload = resolve)),
      ]);

      // Draw the canvas image first
      context.drawImage(
        canvasImage,
        0,
        0,
        combinedCanvas.width,
        combinedCanvas.height
      );

      // Draw the annotations on top
      context.drawImage(
        annotationsImage,
        0,
        0,
        combinedCanvas.width,
        combinedCanvas.height
      );

      // Step 5: Export the combined image as a data URL
      const finalDataUrl = combinedCanvas.toDataURL('image/png');

      // Remove the "data:image/png;base64," prefix
      const base64Data = finalDataUrl.replace(/^data:image\/png;base64,/, '');

      // Step 6: Send the base64 data to the update callback
      if (this.updateCallback) {
        this.updateCallback('proposal.CaptureThumbnail', base64Data);
      }

      return base64Data;
    } catch (err) {
      console.error('Error capturing thumbnail:', err);
      if (this.updateCallback) {
        this.updateCallback('Error', {
          code: 86,
          message: 'Error in proposal.CaptureThumbnail',
        });
      }
    }
  };

  captureImage = async () => {
    try {
      if (this.updateCallback) {
        this.updateCallback('proposal.CaptureImage', 'initiated');
      }

      const canvas3D = await this.waitForCaptureCanvas(['exportToJPG']);
      const imageData = await canvas3D.exportToJPG();

      if (!imageData?.url) {
        throw new Error('Canvas3D export did not return an image URL.');
      }

      const blobClient = axios.create();
      const res = await blobClient.get(imageData.url, {
        responseType: 'blob',
      });

      const dataUrl = await this.blobToDataURLAsync(res.data);
      if (this.updateCallback) {
        this.updateCallback('proposal.CaptureImage', dataUrl);
      }

      return imageData;
    } catch (err) {
      console.log(err);
      if (this.updateCallback) {
        this.updateCallback('Error', {
          code: 86,
          message: 'Error in proposal.CaptureImage',
        });
      }

      return null;
    }
  };

  downloadShadingReport = async (params = null) => {
    const { iframe, callback } = this.props;
    const { shadingReportData } = this.state;
    await this.setStateAsync({
      exporting: true,
    });

    try {
      if (shadingReportData === null || iframe) {
        await this.handleShadingReportDownload(params, true);
      }

      const htmlElement = await this.waitForElement('#shading-report');
      if (!htmlElement) {
        throw new Error('Shading report content did not render.');
      }

      const opt = {
        margin: [24, 24, 24, 24],
        filename: 'shading-report.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, allowTaint: false, letterRendering: true },
        pagebreak: { mode: 'avoid-all', before: '#page2el' },
        jsPDF: { unit: 'pt', format: 'a4', orientation: 'landscape' },
      };

      if (iframe && this.updateCallback) {
        if (params?.format === 'pdf') {
          await html2pdf().set(opt).from(htmlElement).save();
          callback('proposal.ShadingReportPDF', '');
        } else if (params?.format === 'base64') {
          const pdf = await html2pdf().set(opt).from(htmlElement).outputPdf();
          callback('proposal.ShadingReportBase64', btoa(pdf));
        } else {
          await html2pdf().set(opt).from(htmlElement).save();
          callback('proposal.ShadingReportPDF', '');
        }
      } else {
        await html2pdf().set(opt).from(htmlElement).save();
      }
    } catch (err) {
      console.log(err);
      if (iframe && this.updateCallback) {
        this.updateCallback('Error', {
          code: '87',
          message: 'Error in proposal.ShadingReport',
        });
      }
    } finally {
      this.setState({
        exporting: false,
        modal: '',
      });
    }
  };

  downloadPdf = async () => {
    const primaryColor = '#333F50';
    const black = '#000000';
    const white = '#FFFFFF';
    const lightGrey = '#F4F5F8';

    const dateLong = { year: 'numeric', month: 'long', day: 'numeric' };
    const date = new Date().toLocaleString('en-US', dateLong);

    const pdf = new JsPDF('p');
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();

    // TODO figure out specifics of adding logo to PDF
    // let logoUrl = '/logo.png';
    const pageOneContentWidth = width / 2.2 + 15;
    // let adjustHeight = 20;
    let aspectRatio = this.state.logoWidth / this.state.logoHeight;
    let imgWidth = width - pageOneContentWidth - 60;
    let imgHeight = imgWidth / aspectRatio;

    // customer data
    const {
      lead: { homeowners, address, property },
      currentUser: advisor,
      fullAddress,
      hardware,
    } = this.props;
    const contact = homeowners[0];
    const customerName = `${contact.first_name} ${contact.last_name}`;
    const addressTop = address.street;
    const addressBottom = `${address.city}, ${address.state}`;
    console.log('advisor: ', advisor);

    // solar advisor
    const company = advisor.company.name;
    let logoUrl = advisor.company?.logo;

    console.log('logoUrl: ', logoUrl);

    const advisorName = `${advisor.first_name} ${advisor.last_name}`;

    // Title Page
    pdf.addImage('/pdfHomePage.png', 10, 10, width / 2.2, height - 20);
    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(50);
    pdf.setTextColor(primaryColor);
    pdf.text('Custom', pageOneContentWidth, 23);
    pdf.text('Solar', pageOneContentWidth, 43);
    pdf.text('Design', pageOneContentWidth, 63);

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(12);
    pdf.setTextColor(black);
    pdf.text(date, pageOneContentWidth, 75);

    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(black);
    pdf.text('PREPARED FOR', pageOneContentWidth, 110);

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(12);
    pdf.setTextColor(black);
    pdf.text(customerName, pageOneContentWidth, 115);
    pdf.text(addressTop, pageOneContentWidth, 120);
    pdf.text(addressBottom, pageOneContentWidth, 125);

    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(black);
    pdf.text('PREPARED BY', pageOneContentWidth, 150);

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(12);
    pdf.setTextColor(black);
    pdf.text(advisorName, pageOneContentWidth, 155);
    pdf.text(company, pageOneContentWidth, 160);
    pdf.text(advisor.email, pageOneContentWidth, 165);
    pdf.text(advisor.phone || '', pageOneContentWidth, 170);

    const pdfLogo = await loadPdfImage(logoUrl);
    if (pdfLogo) {
      pdf.addImage(
        pdfLogo.image,
        pdfLogo.format,
        pageOneContentWidth,
        /* height-20 */ 185,
        imgWidth,
        imgHeight
      );
    }

    // const pageOneHeight = 60

    /* pdf.setFont("Helvetica","bold")
      pdf.setFontSize(80)
      pdf.setTextColor("#22919E")
      pdf.text("AERIALYTIC", 45,300) */

    pdf.addPage();
    pdf.setFillColor(lightGrey);
    pdf.rect(0, 0, width, height, 'F');

    // hardware
    const { panel, inverter, mounting, monitoring, loanSwitch } = this.state;
    let panelName = panel.manufacturer
      ? `${panel.manufacturer.name} ${panel.model}`
      : '';
    panelName = pdf.splitTextToSize(panelName, 30);
    // let solarPanelModelpdf = this.state.panelsSelected.model;

    // financial info
    // TODO: clean this up? what values can we pre-format
    const solarProduction = `${this.state.solarProduction.toFixed(2)} kWh`;
    const annualUsage = `${this.state.annualUsage.toFixed(2)} kWh`;
    const offset = `${this.state.offset} %`;
    const systemSize = `${this.state.systemSize.toFixed(2)} kW`;
    const totalCost = formatCurrency(this.state.totalCost);
    const yearlySavings = formatCurrency(this.state.yearlySavings[0]);
    const payback = (
      this.state.paybackYear +
      this.state.paybackMonth / 12
    ).toFixed(1);

    const lifetimeSavings = loanSwitch
      ? formatCurrency(this.state.loan.lifetimeSavings[24])
      : formatCurrency(this.state.lifetimeSavings[24]);

    // loan related finances
    // const { amount, interestRate, years} = this.state.loan;
    const monthlyPayments = formatCurrency(this.state.loan.monthlyPayments);

    // TODO: write this to PDF somewhere?
    // const loanSummary = pdf.splitTextToSize(
    //   `For a ${formatCurrency(amount)} loan at ${interestRate}% interest with a ${years} year amortization, your monthly payments would be ${monthlyPayments}`,
    //   100,
    // );

    const houseImage = document.querySelector('.canvas3d').children[0];
    const solarGraphChart =
      document.querySelector('#solarGraphChart').children[1];
    const paybackChart = document.querySelector('#paybackChart').children[1];
    const footNote =
      'The savings, production and estimated payback period on this Custom Solar Design is an estimate only.';

    // pdf.addImage('/logo.png', 'PNG', width-imgWidth-5, 10, imgWidth, imgHeight)
    pdf.setFillColor(primaryColor);
    pdf.roundedRect(5, 10, width - 10, 25, 1, 1, 'F');

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(12);
    pdf.setTextColor(white);
    pdf.text('Here is your custom solar design for', 10, 20);

    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(white);
    pdf.text(fullAddress, 10, 28);

    const {
      url: canvas3DJPG,
      width: canvas3DJPGWidth,
      height: canvas3DJPGHeight,
    } = await this.canvas3DElement.exportToJPG();
    pdf.addImage(canvas3DJPG, 'JPEG', 25, 38, width - 50, 138);

    const savingsHeight = height / 2.5 + 60;
    pdf.setFillColor(primaryColor);
    pdf.roundedRect(5, savingsHeight, width - 10, 25, 1, 1, 'F');

    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(25);
    pdf.setTextColor(white);
    pdf.text(yearlySavings, 10, savingsHeight + 12);
    pdf.text(lifetimeSavings, width / 2 - 20, savingsHeight + 12);
    pdf.text(
      loanSwitch ? monthlyPayments : payback,
      width - 50,
      savingsHeight + 12
    );

    // Add "months" as payback period if on Cash tab
    pdf.setFontSize(12);
    if (!loanSwitch) pdf.text('months', width - 36, savingsHeight + 12);

    pdf.setFont('Helvetica', 'normal');
    pdf.setTextColor(white);
    pdf.text('First Year Savings', 10, savingsHeight + 18);
    pdf.text('25 Year Savings', width / 2 - 15, savingsHeight + 18);
    pdf.text(
      loanSwitch ? 'Monthly Payment' : 'Payback Period',
      width - 50,
      savingsHeight + 18
    );

    // overview & equipment sections
    const overviewHeight = savingsHeight + 28;
    pdf.setFillColor(white);
    pdf.setDrawColor('#ECECEC');
    pdf.roundedRect(5, overviewHeight, width / 2 - 5, 82, 1, 1, 'DF');
    pdf.roundedRect(
      width / 2 + 5,
      overviewHeight,
      width / 2 - 10,
      82,
      1,
      1,
      'DF'
    );

    pdf.setFont('Helvetica', 'bold');
    pdf.setTextColor(black);
    pdf.setFontSize(12);
    pdf.text('Overview', 10, overviewHeight + 6);

    pdf.setFont('Helvetica', 'normal');
    pdf.text('Offset', 10, height / 2.5 + 90 + 13);
    pdf.text('System Size', 10, height / 2.5 + 105 + 13);
    pdf.text('Total Cost', 10, height / 2.5 + 120 + 13);
    pdf.text('Electricity Usage', 10, height / 2.5 + 135 + 13);
    pdf.text('Solar Production', 10, height / 2.5 + 150 + 13);

    pdf.setFont('Helvetica', 'bold');
    pdf.text(offset, width / 2 - 40, height / 2.5 + 90 + 13);
    pdf.text(systemSize, width / 2 - 40, height / 2.5 + 105 + 13);
    pdf.text(totalCost, width / 2 - 40, height / 2.5 + 120 + 13);
    pdf.text(annualUsage, width / 2 - 40, height / 2.5 + 135 + 13);
    pdf.text(solarProduction, width / 2 - 40, height / 2.5 + 150 + 13);

    pdf.setFont('Helvetica', 'bold');
    pdf.setTextColor(black);
    pdf.setFontSize(12);
    pdf.text('Equipment', width / 2 + 10, overviewHeight + 6);

    pdf.setFont('Helvetica', 'normal');
    pdf.text('Solar Panel', width / 2 + 10, height / 2.5 + 90 + 13);
    pdf.text('Inverter', width / 2 + 10, height / 2.5 + 110 + 13);
    pdf.text('Mounting', width / 2 + 10, height / 2.5 + 130 + 13);
    pdf.text('Monitoring', width / 2 + 10, height / 2.5 + 150 + 13);

    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text(panelName, width - 50, height / 2.5 + 90 + 13);
    // pdf.text(solarPanelModelpdf, width - 50, height / 2.5 + 95 + 13);
    pdf.text(inverter.model || '', width - 50, height / 2.5 + 110 + 13);
    pdf.text(mounting.model || '', width - 50, height / 2.5 + 130 + 13);
    pdf.text(monitoring.model || '', width - 50, height / 2.5 + 150 + 13);

    pdf.setFont('Helvetica', 'italic');
    pdf.setTextColor(black);
    pdf.setFontSize(8);
    pdf.text('Page 2', width - 15, height - 3);
    pdf.text(footNote, 5, height - 3);

    // adding third page
    pdf.addPage();
    pdf.setFillColor(lightGrey);
    pdf.rect(0, 0, width, height, 'F');

    pdf.setFont('Helvetica', 'italic');
    pdf.setTextColor(black);
    pdf.setFontSize(8);
    pdf.text('Page 3', width - 15, height - 3);
    pdf.text(footNote, 5, height - 3);

    // chart sections white squares
    pdf.setFillColor(white);
    pdf.setDrawColor('#ECECEC');
    pdf.roundedRect(5, 10, width / 2 - 5, 80, 1, 1, 'DF');
    pdf.roundedRect(width / 2 + 5, 10, width / 2 - 10, 80, 1, 1, 'DF');

    // pdf.addImage(logoUrl, 'PNG', width-imgWidth-5, 10, imgWidth, imgHeight)

    // electricity usage vs production
    const solarGraphDataUrl = await captureRequiredExportPng(
      solarGraphChart,
      toPng,
      { assetName: 'solar production chart' }
    );
    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('Average Electricity Usage', 7, 18);
    pdf.addImage(
      solarGraphDataUrl,
      'PNG',
      6,
      24,
      width / 2 - 7,
      height / 4.5
    );

    // 25 year savings chart
    const paybackChartDataUrl = await captureRequiredExportPng(
      paybackChart,
      toPng,
      { assetName: 'payback chart' }
    );
    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('25 Years Savings', width / 2 + 7, 18);
    pdf.addImage(
      paybackChartDataUrl,
      width / 2 + 5,
      24,
      width / 2 - 14,
      height / 4.5
    );

    const breakdownStart = height / 4.5 + 30;

    const adders = [];
    Object.entries(this.state.adders).forEach(
      ([_, { count, name, totalCost: total }]) => {
        if (count) {
          const adderName = count === 1 ? name : `${name} (x${count})`;
          adders.push({ name: adderName, total });
        }
      }
    );

    const { credits, systemCost, permitCost } = this.state;
    let breakdownHeight = 32;
    if (permitCost) breakdownHeight += 13;
    if (adders.length > 0) breakdownHeight += 8 + adders.length * 5;
    if (credits.length > 0) breakdownHeight += 8 + credits.length * 5;

    pdf.setFillColor(white);
    pdf.setDrawColor('#ECECEC');
    pdf.roundedRect(5, breakdownStart, width - 10, breakdownHeight, 1, 1, 'DF');

    // write content
    pdf.text('Financial Breakdown', 10, breakdownStart + 8);
    pdf.setFont('Helvetica', 'normal');
    pdf.setDrawColor('#808080');

    // system costs
    let currentHeight = breakdownStart + 18;
    pdf.text('System Cost', 40, currentHeight);
    pdf.text('+', 150, currentHeight);
    pdf.text(formatCurrency(systemCost), 155, currentHeight);
    currentHeight += 5;

    // permit costs
    const permitCity = `${property.city}, ${property.state}`;

    if (permitCost) {
      pdf.text('Permits', 40, currentHeight + 2);
      pdf.line(30, currentHeight + 3, width - 30, currentHeight + 3);
      pdf.text(permitCity, 40, currentHeight + 8);
      pdf.text('+', 150, currentHeight + 8);
      pdf.text(formatCurrency(permitCost), 155, currentHeight + 8);
      currentHeight += 13;
    }

    if (adders.length > 0) {
      pdf.text('Adders', 40, currentHeight + 2);
      pdf.line(30, currentHeight + 3, width - 30, currentHeight + 3);
      currentHeight += 8;
    }
    adders.forEach(({ name, total }) => {
      pdf.text(name, 40, currentHeight);
      pdf.text('+', 150, currentHeight);
      pdf.text(formatCurrency(total), 155, currentHeight);
      currentHeight += 5;
    });

    if (credits.length > 0) {
      pdf.text('Credits', 40, currentHeight + 2);
      pdf.line(30, currentHeight + 3, width - 30, currentHeight + 3);
      currentHeight += 8;
    }
    credits.forEach(({ name, amount }) => {
      pdf.text(name, 40, currentHeight);
      pdf.text('-', 150, currentHeight);
      pdf.text(formatCurrency(amount), 155, currentHeight);
      currentHeight += 5;
    });

    pdf.setDrawColor('#404040');
    pdf.setFont('Helvetica', 'bold');
    pdf.line(30, currentHeight - 2, width - 30, currentHeight - 2);
    pdf.text('Total', 40, currentHeight + 3);
    pdf.text(totalCost, 155, currentHeight + 3);

    // add warranty section
    currentHeight = breakdownStart + breakdownHeight + 6;
    const { warranty } = hardware;
    if (warranty) {
      // split into lines to fit page width
      const splitWarranty = pdf.splitTextToSize(warranty, width - 10);

      pdf.setFillColor(white);
      pdf.setDrawColor('#ECECEC');
      pdf.roundedRect(
        5,
        currentHeight,
        width - 10,
        4 * splitWarranty.length + 16,
        1,
        1,
        'DF'
      );

      pdf.setFont('Helvetica', 'bold');
      pdf.setTextColor(black);
      pdf.setFontSize(12);
      pdf.text('Warranty', 10, currentHeight + 8);

      // warranty details
      pdf.setFont('Helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(splitWarranty, 10, currentHeight + 14);
    }

    // const reportName = `${company}_${customerName}_${systemSize}kW_${date}.pdf`;
    // pdf.output('save', reportName);
    window.open(pdf.output('bloburl'));
  };

  updateSelectedPanels = async () => {
    const { pid, API } = this.props;

    const { design, selectedPanels } = this.state;

    // update selected panels
    const designs = buildDesignsPayloadWithStringing({
      design,
      selectedPanels,
      designData: this.state.designData,
    }).map(({ id, vertical_selected, horizontal_selected, panel_axis }) => ({
      id,
      vertical_selected,
      horizontal_selected,
      panel_axis,
    }));

    const data = this.withCurrentProposalVersion({ designs });
    const response = await API.update(pid, data, true); // partial update
    this.recordCurrentProposalVersion(response);
  };

  // update report in backend
  updateReport = async (options = {}) => {
    const { pid, API, iframe, hardware, lead } = this.props;

    const {
      offset,
      systemSize,
      yearlySavings,
      lifetimeSavings,
      totalCost,
      escalation,
      panel,
      currentPanel,
      inverter,
      inverterQuantity,
      additionalInverters,
      monitoring,
      mounting,
      adders,
      design,
      selectedPanels,
      solarProduction,
      annualUsage,
      paybackYear,
      paybackMonth,
      systemCost,
      adderCost,
      adderNRCost,
      hiddenAdderCost,
      visibleAdderCost,
      preRebateCost,
      discountSystemCost,
      discountAmount,
      loan,
      credits,
      permitCost,
      loanObj,
      designData,
      panelShadings: statePanelShadings,
      shadingReportData,
      companyPanel,
      companyInverter,
      loanSwitch,
      totalPanels,
      monthlyProduction,
      primaryInverterAutoQuantity,
      hasAutoCalculatedInverterQuantity,
    } = this.state;

    const currentPanelId =
      typeof panel === 'string' ? panel : panel?.id || null;
    const currentCompanyPanelId =
      typeof companyPanel === 'string'
        ? companyPanel
        : companyPanel?.id || null;
    const resolvedCompanyPanelId =
      currentCompanyPanelId || (isUuid(currentPanelId) ? currentPanelId : null);
    const resolvedCompanyPanel = resolvedCompanyPanelId
      ? { id: resolvedCompanyPanelId }
      : null;

    const currentInverterId =
      typeof inverter === 'string' ? inverter : inverter?.id || null;
    const currentCompanyInverterId =
      typeof companyInverter === 'string'
        ? companyInverter
        : companyInverter?.id || null;
    const resolvedCompanyInverterId =
      currentCompanyInverterId ||
      (isUuid(currentInverterId) ? currentInverterId : null);
    const resolvedCompanyInverter = resolvedCompanyInverterId
      ? { id: resolvedCompanyInverterId }
      : null;

    let panelShadings = statePanelShadings;

    let selectedInverter = inverter;
    const statePackage = hardware.packages.filter(
      (item, index) => item.state === lead.address.state
    )[0];
    const defaultPackage = hardware.packages.filter(
      (item, index) => item.state === null
    )[0];
    const selectedPackage =
      statePackage !== undefined ? statePackage : defaultPackage;
    const defaultInverter = Object(
      selectedPackage.inverter || hardware.inverters[0]
    );
    // Removed panel.has_micro logic - let user select appropriate microinverter
    // if (panel?.has_micro !== currentPanel?.has_micro) {
    //   selectedInverter = panel.has_micro ? Object() : defaultInverter;
    // }

    // await this.hourlyProduction();

    const panelMetrics = await this.calcPanelMetrics();

    // save only panel positions in this case
    if (!yearlySavings[0] || !lifetimeSavings[24]) {
      // update selected panels
      const liveStrings = this.state.stringingData;
      const designMap = {};
      design.forEach(({ id }, index) => {
        designMap[id] = designData.panels[index].id;
      });
      const designs = buildDesignsPayloadWithStringing({
        design,
        selectedPanels,
        strings: liveStrings,
        designData,
      });

      const size = systemSize.toFixed(3);
      const summary = [
        size,
        solarProduction,
        Math.floor(offset),
        annualUsage.toFixed(0),
        paybackYear,
        paybackMonth,
        systemCost,
        selectedPackage,
        selectedInverter,
      ].join(',');

      try {
        if (!panelShadings || panelShadings.length === 0) {
          panelShadings = await this.canvas3DElement.getAnualPanelsShadings();
        }
      } catch (err) {
        console.log(err);
      }

      let data = {
        summary,
        designs,
        escalation: (escalation * 100).toFixed(1),
        monitoring: monitoring?.id || null,
        mounting: mounting?.id || null,
        adders: Object.entries(adders)
          .map(([id, adder]) => ({ ...adder, id, proposal: pid }))
          .filter(({ count }) => count > 0),
        design_data: buildDesignDataWithLiveProduction({
          baseDesignData: {
            ...designData,
            design_map: designMap,
            selectedPackage,
            panel,
            inverter,
            inverterQuantity,
            primaryInverterAutoQuantity,
            hasAutoCalculatedInverterQuantity: !primaryInverterAutoQuantity,
            inverter_quantity: inverterQuantity,
            ...((inverter?.type === 'M' || inverter?.type === 'Micro')
              ? {}
              : {
                additional_inverters: this.toPersistedAdditionalInverters(
                  additionalInverters
                ),
              }),
            monitoring,
            mounting,
            monthlyProduction,
            adders: Object.entries(adders)
              .map(([id, adder]) => ({ ...adder, id, proposal: pid }))
              .filter(({ count }) => count > 0),
            roof_type: lead?.property?.roof_type,
            selected_panels: totalPanels,
            summary: {
              system_size: size,
              solar_production: solarProduction,
              offset: Math.floor(offset),
              annual_usage: annualUsage.toFixed(0),
              payback_year: paybackYear,
              payback_month: paybackMonth,
              system_cost: systemCost,
              adder_cost: adderCost,
              adder_nrcost: adderNRCost,
              hidden_adder_cost: hiddenAdderCost,
              visible_adder_cost: visibleAdderCost,
              pre_rebate_cost: preRebateCost,
              discount_system_cost: discountSystemCost,
              discount_amount: discountAmount,
              total_panels: totalPanels,
            },
            credits,
            permit_cost: permitCost,
            panel_shadings: panelShadings,
            panel_metrics: panelMetrics,
            shading_report: shadingReportData,
          },
          production: this.state,
          strings: liveStrings,
        }),
      };
      const persistedHardwareId = (value) => {
        const id = typeof value === 'string' ? value : value?.id || null;
        return isUuid(id) ? id : null;
      };
      const companyPanelId = persistedHardwareId(resolvedCompanyPanel);
      const companyInverterId = persistedHardwareId(resolvedCompanyInverter);
      const hardwarePayload = {
        panel: companyPanelId ? null : persistedHardwareId(panel),
        company_panel: companyPanelId,
        inverter: companyInverterId ? null : persistedHardwareId(inverter),
        company_inverter: companyInverterId,
      };
      data = {
        ...data,
        ...hardwarePayload,
      };

      const proposalBody = this.withCurrentProposalVersion(data);
      if (options?.collectOnly) return proposalBody;
      const saveSeq = this.proposalDirtySeq || 0;
      const response = await API.update(
        pid,
        proposalBody,
        true
      ); // partial update
      if ((this.proposalDirtySeq || 0) === saveSeq) this.markProposalClean();
      this.recordCurrentProposalVersion(response);

      if (this.updateCallback) {
        this.updateCallback(
          'proposal.Save',
          'Selected panels are saved on proposal.'
        );
        this.emitShadingValuesPerArray();
        // try {
        //   if (['AERIAL', 'ENERFL', 'ENERF2'].includes(lead?.company)) {
        //     let panelMetrics = await this.calcPanelMetrics();
        //     this.updateCallback('proposal.ShadingValuesPerArray', {
        //       yearly: panelMetrics['panelMetrics'],
        //       monthly: panelMetrics['monthlyPanelMetrics'],
        //     });
        //   }
        // } catch (err) {
        //   console.log(`error in calcPanelMetrics: ${err}`);
        // }
      }

      return;
    }

    // if (!yearlySavings[0] || !lifetimeSavings[24]) return;
    this.setState({
      portalLoading: true,
    });
    try {
      const images = await this.captureProposalImages();

      const size = systemSize.toFixed(3);
      const yearly = yearlySavings[0].toFixed(2);
      const savings = lifetimeSavings[24].toFixed(2);
      const total = totalCost.toFixed(2);
      const summary = [
        size,
        yearly,
        savings,
        total,
        solarProduction,
        Math.floor(offset),
        annualUsage.toFixed(0),
        paybackYear,
        paybackMonth,
        systemCost,
      ].join(',');

      const designMap = {};
      const liveStrings = this.state.stringingData;

      // update selected panels
      design.forEach(({ id }, index) => {
        designMap[id] = designData.panels[index].id;
      });
      const designs = buildDesignsPayloadWithStringing({
        design,
        selectedPanels,
        strings: liveStrings,
        designData,
      });

      try {
        if (!panelShadings || panelShadings.length === 0) {
          panelShadings = await this.canvas3DElement.getAnualPanelsShadings();
        }
      } catch (err) {
        console.log(err);
      }

      let data = {
        summary,
        loan: loanObj || null,
        loan_switch: loanSwitch,
        designs,
        images,
        escalation: (escalation * 100).toFixed(1),
        monitoring: monitoring?.id || null,
        mounting: mounting?.id || null,
        adders: Object.entries(adders)
          .map(([id, adder]) => ({ ...adder, id, proposal: pid }))
          .filter(({ count }) => count > 0),
        design_data: buildDesignDataWithLiveProduction({
          baseDesignData: {
            ...designData,
            design_map: designMap,
            selectedPackage,
            panel,
            inverter,
            inverterQuantity,
            primaryInverterAutoQuantity,
            hasAutoCalculatedInverterQuantity: !primaryInverterAutoQuantity,
            inverter_quantity: inverterQuantity,
            additional_inverters: this.toPersistedAdditionalInverters(
              additionalInverters
            ),
            monitoring,
            mounting,
            monthlyProduction,
            adders: Object.entries(adders)
              .map(([id, adder]) => ({ ...adder, id, proposal: pid }))
              .filter(({ count }) => count > 0),
            roof_type: lead?.property?.roof_type,
            selected_panels: totalPanels,
            summary: {
              system_size: size,
              yearly_savings: yearly,
              yearly_savings_array: yearlySavings,
              lifetime_savings: savings,
              lifetime_savings_array: lifetimeSavings,
              total_cost: total,
              solar_production: solarProduction,
              offset: Math.floor(offset),
              annual_usage: annualUsage.toFixed(0),
              payback_year: paybackYear,
              payback_month: paybackMonth,
              system_cost: systemCost,
              adder_cost: adderCost,
              adder_nrcost: adderNRCost,
              hidden_adder_cost: hiddenAdderCost,
              visible_adder_cost: visibleAdderCost,
              pre_rebate_cost: preRebateCost,
              discount_system_cost: discountSystemCost,
              discount_amount: discountAmount,
              total_panels: totalPanels,
            },
            credits,
            permit_cost: permitCost,
            panel_shadings: panelShadings,
            panel_metrics: panelMetrics,
            shading_report: shadingReportData,
          },
          production: this.state,
          strings: liveStrings,
        }),
      };
      const persistedHardwareId = (value) => {
        const id = typeof value === 'string' ? value : value?.id || null;
        return isUuid(id) ? id : null;
      };
      const companyPanelId = persistedHardwareId(resolvedCompanyPanel);
      const companyInverterId = persistedHardwareId(resolvedCompanyInverter);
      const hardwarePayload = {
        panel: companyPanelId ? null : persistedHardwareId(panel),
        company_panel: companyPanelId,
        inverter: companyInverterId ? null : persistedHardwareId(inverter),
        company_inverter: companyInverterId,
      };
      data = {
        ...data,
        ...hardwarePayload,
      };

      const proposalBody = this.withCurrentProposalVersion(data);
      if (options?.collectOnly) return proposalBody;
      const saveSeq = this.proposalDirtySeq || 0;
      const response = await API.update(
        pid,
        proposalBody,
        true
      ); // partial update
      if ((this.proposalDirtySeq || 0) === saveSeq) this.markProposalClean();
      this.recordCurrentProposalVersion(response);

      if (this.updateCallback) {
        this.updateCallback('proposal.Save', 'Proposal is saved.');
        this.emitShadingValuesPerArray();
        // try {
        //   if (['AERIAL', 'ENERFL', 'ENERF2'].includes(lead?.company)) {
        //     let panelMetrics = await this.calcPanelMetrics();
        //     this.updateCallback('proposal.ShadingValuesPerArray', {
        //       yearly: panelMetrics['panelMetrics'],
        //       monthly: panelMetrics['monthlyPanelMetrics'],
        //     });
        //   }
        // } catch (err) {
        //   console.log(`error in calcPanelMetrics: ${err}`);
        // }
      }
    } finally {
      this.setState({
        portalLoading: false,
      });
    }
  };

  switchView = (viewMode) => {
    this.setState({ viewMode });
    this.canvas3DElement.setViewMode(viewMode);
  };

  mapsLink = () => {
    const { lead } = this.props;
    const [lat, lng] = lead.property.coordinates;
    return `https://www.google.com/maps/@${lat},${lng},61m/data=!3m1!1e3`;
  };

  toggleOverlay = () => {
    const { showOverlay } = this.state;
    this.canvas3DElement.toggleView('shading', showOverlay, !showOverlay);
    this.setState({ showOverlay: !showOverlay });
  };

  toggleVisibility = () => {
    const { visibility } = this.state;
    this.setState({ visibility: !visibility });
  };

  setShadingTexture = (texture, month) => {
    this.setState({
      showOverlay: true,
      shadingMonth: month,
      shadingTexture: texture,
    });
  };

  switchAnnotationMode = () => {
    if (this.updateCallback) {
      this.canvas3DElement.switchAnnotation();
    }
  };

  newAnnotation = (position, type = 'custom', params = {}) => {
    const { annotations } = this.state;
    const typeName = type.toLowerCase();
    const typeCaption = typeName == 'custom' ? 'Annotation' : type;
    const typeLength =
      annotations.filter((a) => a?.type?.toLowerCase() === typeName).length ||
      0;
    const newAnnotations = [...annotations];
    newAnnotations.push({
      position: { ...position },
      text: `${typeCaption}_${typeLength + 1}`,
      type,
      ...params,
    });
    this.persistAnnotation(newAnnotations);
    if (this.updateCallback)
      this.updateCallback('proposal.new.annotation', {
        position,
        text: `${typeCaption}_${typeLength + 1}`,
        type: 'custom',
      });
    // this.getAnnotations();
  };

  persistAnnotation = (annotations) => {
    const { pid, API } = this.props;
    const data = this.withCurrentProposalVersion({ annotations });
    API.update(pid, data, true).then(this.recordCurrentProposalVersion);
    this.setState({ annotations });
  };

  updateAnnotations = (annotations) => {
    if (this.updateCallback && annotations) {
      this.canvas3DElement.setAnnotations(annotations);
      this.getAnnotations();
      return true;
    }
    return false;
  };

  getAnnotations = () => {
    if (this.updateCallback) {
      const annotations = this.canvas3DElement.getAnnotations();
      this.updateCallback('proposal.annotations', annotations);
    }
  };

  removeAnnotation = (index) => {
    const { annotations } = this.state;
    const newAnnotations = [...annotations];
    newAnnotations.splice(index, 1);
    this.persistAnnotation(newAnnotations);
    // this.getAnnotations();
  };

  handleDownload3DJSON = () => {
    this.canvas3DElement.downloadJSON();
  };

  // Add this method to calculate net metering
  calculateNetMetering = async (params = null) => {
    const { lead } = this.props;
    const { monthlyProduction, systemSize } = params || this.state;

    console.log('calculateNetMetering', this.props);
    console.log('calculateNetMetering', this.state);

    console.log('Calculating net metering with:', {
      monthlyProduction,
      systemSize,
      utility: lead.utility,
    });

    if (!monthlyProduction || !systemSize || !lead.utility) {
      console.error('Missing required data for net metering calculations:', {
        hasMonthlyProduction: !!monthlyProduction,
        hasSystemSize: !!systemSize,
        hasUtility: !!lead.utility,
      });
      return;
    }

    try {
      const netMeteringResults = calculateNetMetering({
        monthlyProduction,
        systemSize,
        utility: lead.utility,
      });

      console.log('Net metering results:', netMeteringResults);

      const billingResults = calculateBilling(netMeteringResults, lead.utility);
      console.log('Billing results:', billingResults);

      this.setState({
        netMeteringCalculations: {
          ...netMeteringResults,
          billing: billingResults,
        },
      });
    } catch (error) {
      console.error('Error calculating net metering:', error);
    }
  };

  render() {
    const {
      design,
      panel,
      inverter,
      inverterQuantity,
      hasAutoCalculatedInverterQuantity,
      primaryInverterAutoQuantity,
      additionalInverters,
      invertersUsed,
      monitoring,
      mounting,
      loanSwitch,
      loadError,
    } = this.state;
    const { validUtility, pid, displaySnack, callback, currentUser } =
      this.props;

    if (loadError) {
      return (
        <Alert className="w-100 my-3" variant="warning">
          <InfoCircle size={20} className="mr-2" />
          {loadError}
        </Alert>
      );
    }

    if (design === undefined) return <Loading />;

    // destructure rendered props/state
    const {
      lead,
      hardware,
      iframe,
      fullAddress,
      companyName,
      context: iframeContext = {},
    } = this.props;

    let snowLoss = '0.0';
    try {
      snowLoss = getSnowLoss(convertStateToAbbr(lead?.property?.state, 'abbr'));
    } catch (err) {
      console.log(`in getting snow loss: ${err}`);
    }

    const invertersUsedCountById = Array.isArray(invertersUsed)
      ? invertersUsed.reduce((acc, item) => {
        const id = item?.hardware_id;
        if (!id) return acc;
        acc[String(id)] = Number(item?.count || 0);
        return acc;
      }, {})
      : {};

    const {
      modal,
      scale,
      visibility,
      showOverlay,
      overlaySrc,
      offset,
      annualUsage,
      escalation,
      loan,
      rebateAmount,
      totalPanels,
      selectedPanels,
      adders,
      credits,
      systemCost,
      adderCost,
      adderNRCost,
      hiddenAdderCost,
      visibleAdderCost,
      preRebateCost,
      discountSystemCost,
      permitCost,
      totalCost,
      systemSize,
      discountAmount,
      discountCap,
      monthlyProduction,
      solarProduction,
      yearlySavings,
      viewMode,
      shadingTexture,
      selectedObjects,
      shadingReportData,
      exporting,
      shadingReportLoading,
      viewerMode,
      portalLoading,
      showNetMeteringVerification,
      netMeteringCalculations,
      annotations,
      hourlyCalcIsRunning,
      isLegacyProposal,
    } = this.state;

    const { warranty } = hardware;
    const { paybackYear, paybackMonth } = this.state;

    // set yearlySavings and annualSavings based on state of Cash vs. Loan
    const lifetimeSavings = loanSwitch
      ? loan.lifetimeSavings
      : this.state.lifetimeSavings;
    const totalSavings = yearlySavings[24] || 0;

    // **** props for 3D
    const { designData, proposalShadings } = this.state;
    const {
      segments = [],
      obstacles,
      trees,
      panels: designPanels,
      losses,
    } = designData;
    const canvasStringingViewProps = getLegacyCanvasStringingViewProps({
      isLegacyProposal,
      stringingData: this.state.stringingData,
      showStringing: this.state.showStringing,
      forceShowStringing: true,
    });
    const { coordinates, resolution: propertyResolution } = lead.property;
    const proposalCanvasImagery = this.state.proposalCanvasImagery;
    const rgbSrc = this.proposalDesignId
      ? (proposalCanvasImagery?.rgb ?? null)
      : designData?.rgb || lead.property?.rgb;
    const dsmSrc = this.proposalDesignId
      ? (proposalCanvasImagery?.dsm ?? null)
      : designData?.dsm || lead.property?.dsm;
    const resolution =
      proposalCanvasImagery?.resolution ||
      designData?.resolution ||
      propertyResolution;

    const show2D = viewMode === '2D';

    let filteredIframeContext = {};
    if (iframe) {
      filteredIframeContext = filterDictByKeys(iframeContext, [
        'buffer',
        'setback',
        'panelType',
        'defaultBtnView',
      ]);
    }

    const isIos = ios();
    const activeProposals = Array.isArray(this.props.proposalList)
      ? this.props.proposalList.filter(
        (proposal) => !proposal?.archived_at && !proposal?.is_archive && !proposal?.archived
      )
      : [];

    // dummy method to fix "automaticAzimuthMode is not a function" error
    const automaticAzimuthMode = (mode) => {
      return;
    };

    const setDSMParams = (DSMParams) => {
      // const { updateLead, lead: { property } } = this.context;
      // updateLead({ property: { ...property, dsm_params: DSMParams } });
    };

    // TODO: Remove after migrating old data from /media to /imagery
    function parsePropertyDataURL(property, type) {
      try {
        if (!property) return null;
        const imagery = property[type];
        if (imagery && imagery.startsWith('pd_')) {
          if (type == 'rgb') {
            return `${process.env.REACT_APP_PROPERTY_DATA_RGB_PUBLIC_URL}/${imagery}/rgb.tiff`;
          } else if (type == 'dsm') {
            return `${process.env.REACT_APP_PROPERTY_DATA_DSM_PUBLIC_URL}/${imagery}/dsm.tiff`;
          }
        } else {
          return imagery.startsWith('http') ? imagery : `/media/${imagery}`;
        }
      } catch (err) {
        console.log(`Error parsing property data URL: ${err}`);
        return null;
      }
    }

    const property = {
      ...lead.property,
      rgb: rgbSrc,
      dsm: dsmSrc,
    };

    let rgbImage = undefined;
    let dsmImage = undefined;
    if (property) {
      rgbImage = parsePropertyDataURL(property, 'rgb');
      dsmImage = parsePropertyDataURL(property, 'dsm');
    }

    const viewer = (
      <DesignContext.Provider
        value={{
          coordinates,
          resolution,
          selectedObjects,
          panelVisibility: visibility,
          viewMode,
          iframe,
          dsmParams:
            proposalCanvasImagery?.dsm_params ||
            designData?.dsm_params ||
            designData?.dsmParams ||
            lead.property.dsm_params,
          ios: isIos,
          mode: 'select',
          viewerMode,
          annotations,
          hourlyCalcIsRunning,
          addToSelectedObjects: this.addToSelectedObjects,
          addToSelectedObjectsBulk: this.addToSelectedObjectsBulk,
          removeFromSelectedObjects: this.removeFromSelectedObjects,
          clearSelection: this.clearSelection,
          setShadingTexture: this.setShadingTexture,
          setViewMode: this.switchView,
          mapsLink: this.mapsLink,
          togglePanelSelection: this.togglePanelSelection,
          toggleVisibility: this.toggleVisibility,
          toggleStringingVisibility: this.toggleStringingVisibility,
          showStringing: this.state.showStringing,
          automaticAzimuthMode,
          setDSMParams,
          newAnnotation: this.newAnnotation,
          setAnnotations: this.persistAnnotation,
          ...filteredIframeContext,
        }}
      >
        <Canvas3D
          ref={(canvas3D) => {
            this.canvas3DElement = canvas3D;
          }}
          onLoaded={this.handleCanvas3DLoaded}
          // visible={!show2D}
          visible={exporting || shadingReportLoading ? false : true}
          imgSrc={rgbImage}
          dsmSrc={dsmImage}
          segments={segments}
          obstacles={obstacles}
          trees={trees}
          panels={designPanels}
          annotations={annotations || []}
          proposalShadings={proposalShadings}
          iframeCallback={callback}
          // initViewMode={iframe ? '2D' : '3D'}
          initViewMode={'2D'}
          readonly
          stringingData={canvasStringingViewProps.stringingData}
          showStringing={canvasStringingViewProps.showStringing}
          forceShowStringing={canvasStringingViewProps.forceShowStringing}
          stringingPanelIndexMode={this.state.stringingPanelIndexMode || 'all'}
          stringingSelectedPanelIndexMap={
            this.state.stringingSelectedPanelIndexMap || null
          }
          // LeadContext props
          lead={this.props.lead}
          user={this.props.currentUser}
          displaySnack={this.props.displaySnack}
          saveShadingsAPI={this.props.saveShadingsAPI}
          runShadingsAPI={this.props.runShadingsAPI}
          runShadingRequiredClientDataAPI={
            this.props.runShadingRequiredClientDataAPI
          }
          exportDesignAPI={this.props.exportDesignAPI}
        />
        {(exporting || shadingReportLoading) && (
          <Loading title="Generating Shading Report" />
        )}
        {/* <TogglePanels
          imgSrc={lead.property.rgb}
          overlaySrc={overlaySrc}
          scale={scale}
          visibility={visibility}
          showOverlay={showOverlay}
          panels={design}
          selected={selectedPanels}
          handlePanelToggle={this.panelToggleHandler}
          shadingTexture={shadingTexture}
          visible={show2D}
        /> */}
        {companyName !== 'Astrawatt' && companyName !== 'demand-iq' && (
          <IFrameLogo textColor="light" />
        )}
      </DesignContext.Provider>
    );

    if (iframe) {
      return (
        <>
          {viewer}
          <ShadingReportModal
            show={modal === 'shading-report'}
            onHide={this.hideModal}
            shadingReportData={shadingReportData}
            exportPDFShadingReport={this.downloadShadingReport}
            monthlyProduction={monthlyProduction}
            losses={losses}
            snowLoss={snowLoss}
            inverter={inverter}
            exporting={exporting}
            iframe={iframe}
          />
        </>
      );
    }

    return (
      <Col className="p-0 m-0">
        <Card className="shadow w-100 mt-3 bg-primary text-white" body>
          <div className="float-right text-right">
            <div className="mb-2" style={{ fontSize: '0.95rem', lineHeight: 1.35 }}>
              {[this.state.proposalDesignName, this.currentProposalName()]
                .map((name) => String(name || '').trim())
                .filter(Boolean)
                .join(' · ')}
            </div>
          <ValidatedForm
            className="d-flex align-items-center justify-content-end"
            onSave={this.handleManualSave}
          >
            <Dropdown className="mr-2 d-inline-block">
              <Dropdown.Toggle
                size="sm"
                variant="outline-light"
                id="proposal-switcher"
              >
                {this.currentProposalName() || 'Select proposal'}
                <style>{`@keyframes canvasDirtyPulse { 0% { opacity: 1; } 50% { opacity: 0.25; } 100% { opacity: 1; } }`}</style>
                <span
                  className="ml-2 d-inline-block rounded-circle"
                  title={this.state.proposalDirty ? 'Unsaved changes' : 'All changes saved'}
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: this.state.proposalDirty ? '#ff9800' : '#4caf50',
                    animation: this.state.proposalDirty ? 'canvasDirtyPulse 1s infinite' : 'none',
                  }}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ maxWidth: 420, minWidth: 300 }}>
                {activeProposals.map((proposal) => (
                  <Dropdown.Item
                    key={proposal.id}
                    active={String(proposal.id) === String(pid)}
                    style={
                      String(proposal.id) === String(pid)
                        ? { backgroundColor: '#e9ecef', color: '#212529' }
                        : undefined
                    }
                    onClick={() => this.handleProposalSelect(proposal.id)}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="text-truncate">
                        {proposal.name || `Proposal ${proposal.id}`}
                      </span>
                      <span className="ml-2 d-flex">
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 mr-2"
                          title={`Rename ${proposal.name || proposal.id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            this.handleRenameProposal(proposal.id);
                          }}
                        >
                          Rename
                        </Button>
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 text-danger"
                          title={`Archive ${proposal.name || proposal.id}`}
                          disabled={activeProposals.length < 2}
                          onClick={(event) => {
                            event.stopPropagation();
                            this.handleArchiveProposal(proposal.id);
                          }}
                        >
                          Archive
                        </Button>
                      </span>
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Button
              size="sm"
              variant="outline-light"
              className="mr-2"
              type="submit"
              disabled={!this.state.proposalDirty}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outline-light"
              className="mr-2 text-nowrap"
              style={{ flexShrink: 0 }}
              type="button"
              disabled={!this.state.proposalDirty}
              onClick={this.handleSaveAsProposal}
            >
              Save as
            </Button>
          </ValidatedForm>
          </div>
          Here is your proposal for
          <h3 className="mt-2">{fullAddress}</h3>
        </Card>
        {isLegacyProposal && (
          <Alert className="w-100 mb-3" variant="warning">
            <InfoCircle size={20} className="mr-2" />
            This proposal was created with an older proposal format.
          </Alert>
        )}
        <Row className="d-flex justify-content-between" style={{ paddingTop: 5 }}>
          <Col>
            <Button
              className="float-left"
              variant={loanSwitch ? 'outline-primary' : 'primary'}
              onClick={() =>
                this.setState({ loanSwitch: false }, () => {
                  this.setLoanObj(null);
                  this.calcCosts();
                })
              }
            >
              Cash
            </Button>
            {/* <Button
              className="ml-2 px-3"
              variant={!loanSwitch ? 'outline-primary' : 'primary'}
              onClick={() => this.setState({ loanSwitch: true }, () => {
                this.calcCosts();
              })}
            >
              Loan
            </Button> */}
          </Col>
          <Col className="text-right">
            <ValidatedForm onSave={this.saveCurrentProposal}>
              <PortalRedirect
                style={{ paddingRight: 10 }}
                leadId={lead.uid}
                proposalId={pid}
                loading={portalLoading || shadingReportLoading}
                handleSaveProposal={this.prepareProposalForExport}
                canOpenPortal={this.hasPortalFinancials()}
                onBlockedOpen={this.handleBlockedPortalOpen}
                onSaveError={this.handlePortalSaveError}
              />
              <Button
                className="float-right mr-0"
                onClick={this.handleDownload}
                disabled={portalLoading || shadingReportLoading || exporting}
              >
                <Download size={20} />
              </Button>
              <Button
                className="float-right mr-2"
                onClick={() =>
                  this.ensureShadingReportReadyForExport({
                    force: true,
                    showModal: true,
                  })
                }
                disabled={portalLoading || shadingReportLoading || exporting}
              >
                <CloudSun size={20} />
              </Button>
            </ValidatedForm>
          </Col>
        </Row>
        {/* === DESIGN & SUMMARY === */}
        <Row noGutters>
          <Col xl={8} lg={12} md={12} s={12} xs={12} className="mt-3">
            {/* <Row>
              <Col>
                <Controls
                  resetScale={() => this.setScale(SCALE)}
                  scaleUp={() => this.setScale(scale + 0.1)}
                  scaleDown={() => this.setScale(scale - 0.1)}
                  hasPanels={totalPanels !== 0}
                  togglePanelSelection={this.togglePanelSelection}
                  visibility={visibility}
                  showOverlay={showOverlay}
                  toggleVisibility={this.toggleVisibility}
                  toggleOverlay={this.toggleOverlay}
                  viewMode={viewMode}
                  switchView={this.switchView}
                />
              </Col>
            </Row> */}
            <Row className="m-0 pr-2">
              <Col
                className="m-0 p-0 border rounded border-primary overflow-auto"
                style={{ height: 507 }}
              >
                {viewer}
              </Col>
            </Row>
            <Row className="m-1">
              <SystemLosses
                setPVwattsLosses={this.setProposalLosses}
                initialLosses={losses}
              />
            </Row>
          </Col>
          <Col xl={4} lg={12} md={12} s={12} xs={12} className="h-100">
            <Row className="mt-3" noGutters>
              <Col>
                <Card className="shadow-sm p-3 text-white h-100 bg-primary-600">
                  <h4 className="font-weight-bold">
                    {yearlySavings[0] ? (
                      formatCurrency(yearlySavings[0])
                    ) : (
                      <Spinner animation="border" size="sm" />
                    )}
                  </h4>
                  <small>Estimated Yearly Savings</small>
                  <hr className="font-weight-bold border border-white" />
                  <h4 className="font-weight-bold">
                    {totalSavings ? (
                      formatCurrency(totalSavings)
                    ) : (
                      <Spinner animation="border" size="sm" />
                    )}
                  </h4>
                  <small>Estimated 25 Year Savings</small>
                  <hr className="font-weight-bold border border-white" />
                  <h4 className="font-weight-bold">
                    {totalCost ? (
                      formatCurrency(totalCost)
                    ) : (
                      <Spinner animation="border" size="sm" />
                    )}
                  </h4>
                  <small>Estimated Total Cost </small>
                </Card>
              </Col>
              <Col>
                <Card
                  className="shadow-sm p-3 bg-primary-300"
                  onClick={() => {
                    if (!portalLoading) {
                      this.setState({ modal: 'discount' });
                    } else {
                      displaySnack({
                        variant: 'warning',
                        message:
                          'Please wait until proposal is sucessfully saved.',
                      });
                    }
                  }}
                  style={{ height: 175 }}
                >
                  <CircularProgressbar
                    className="font-weight-bold"
                    strokeWidth={14}
                    value={offset}
                    text={`${offset}%`}
                    styles={buildStyles({
                      pathColor: 'darkgreen',
                      textColor: '#333333',
                      textSize: '24px',
                      trailColor: 'white',
                    })}
                  />
                  <div className="mt-2 text-center font-weight-bold">
                    Offset
                  </div>
                </Card>
                <Card className="shadow-sm p-4 text-center">
                  <h4 className="font-weight-bold">
                    {`${systemSize.toFixed(3)} kW`}
                  </h4>
                  <small>Total System Size</small>
                </Card>
              </Col>
            </Row>
            <Row className="" noGutters>
              <Col>
                <Card className="shadow-sm p-5">
                  <table>
                    <tbody>
                      <tr>
                        <td className="text-center">
                          {this.state.productionIsCalculating ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="mb-2"
                              />
                              <br />
                              <small>Calculating...</small>
                            </>
                          ) : (
                            <>
                              <h4 className="font-weight-bold">
                                {`${solarProduction.toFixed(0)} kWh`}
                              </h4>
                              <small>Solar Production</small>
                              <div
                                className="mt-2"
                                style={{
                                  fontSize: '0.75em',
                                  color: '#666',
                                  lineHeight: '1.6',
                                }}
                              >
                                <div>
                                  {this.state.clippingKwh > 0 ? (
                                    <span style={{ color: '#FF5349' }}>
                                      Clipping:{' '}
                                      {this.state.clippingKwh.toFixed(0)} kWh (
                                      {(
                                        (this.state.clippingKwh /
                                          (solarProduction +
                                            this.state.clippingKwh)) *
                                        100
                                      ).toFixed(1)}
                                      %)
                                    </span>
                                  ) : (
                                    <span style={{ color: '#52c41a' }}>
                                      No clipping
                                    </span>
                                  )}
                                </div>
                                {this.state.dcAcRatio && (
                                  <div>
                                    DC/AC Ratio:{' '}
                                    {this.state.dcAcRatio.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </td>
                        <td className="text-center">
                          <h4 className="font-weight-bold">
                            {`${annualUsage.toFixed(0)} kWh`}
                          </h4>
                          <small>Electricity Usage</small>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card>
              </Col>
            </Row>
            <Row className="" noGutters>
              <Col>
                <Card className="shadow-sm p-3">
                  {loanSwitch ? (
                    <p className="small m-0">
                      Your monthly loan payments will be
                      <span className="h4 font-weight-bold">
                        &nbsp;
                        {formatCurrency(loan.monthlyPayments)}
                      </span>
                      .
                    </p>
                  ) : (
                    <p className="small m-0">
                      Your estimated payback period will be
                      <span className="h4 font-weight-bold">{` ${paybackYear} `}</span>
                      {Pluralize('year', paybackYear)},
                      <span className="h4 font-weight-bold">{` ${paybackMonth} `}</span>
                      {Pluralize('month', paybackMonth)}.
                    </p>
                  )}
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
        {/* === EQUIPMENT AND ADDERS  === */}
        <Row className="mt-3" noGutters>
          <Col md={7}>
            <Card className="shadow-sm h-100 mr-1" body>
              <h5 className="font-weight-bold">Equipment</h5>
              <div className="mb-3 p-2 border rounded bg-light">
                <div className="font-weight-bold mb-1">Selected Hardware</div>
                <div className="small">
                  <div>
                    <strong>Panel:</strong>{' '}
                    {panel?.model
                      ? `${panel.model} (${panel.power || 0}W)`
                      : 'Not selected'}
                  </div>
                  <div className="mt-1">
                    <strong>Inverters:</strong>
                    {inverter?.model ? (
                      <ul className="mb-0 ps-3" style={{ fontSize: '0.95em' }}>
                        <li>
                          {inverter.model} ({inverter.capacity || 0}W) ×{' '}
                          <span style={{ color: '#0d6efd', fontWeight: 700 }}>
                            {primaryInverterAutoQuantity
                              ? 'Auto'
                              : inverterQuantity || 1}
                          </span>
                          {invertersUsedCountById[inverter?.id] !==
                            undefined && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  color: '#6c757d',
                                  fontSize: '0.9em',
                                }}
                              >
                                Used: {invertersUsedCountById[inverter.id]}
                              </span>
                            )}
                        </li>
                        {(additionalInverters || []).map((inv, idx) => {
                          const normalizedInv = this.normalizeAdditionalInverter(inv);
                          const additionalInverterId =
                            getAdditionalInverterId(normalizedInv || inv);
                          const additionalInverterModel =
                            normalizedInv?.model || inv?.model || 'Unknown';
                          const additionalInverterCapacity =
                            Number(normalizedInv?.capacity || 0) || 0;
                          return (
                            <li key={`${additionalInverterId || 'inv'}-${idx}`}>
                              {additionalInverterModel} ({additionalInverterCapacity}W) ×{' '}
                              <span style={{ color: '#0d6efd', fontWeight: 700 }}>
                                {inv?.autoQuantity !== false
                                  ? 'Auto'
                                  : inv?.quantity || 1}
                              </span>
                              {additionalInverterId &&
                                invertersUsedCountById[String(additionalInverterId)] !==
                                undefined && (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      color: '#6c757d',
                                      fontSize: '0.9em',
                                    }}
                                  >
                                    Used:{' '}
                                    {
                                      invertersUsedCountById[
                                      String(additionalInverterId)
                                      ]
                                    }
                                  </span>
                                )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <span> Not selected</span>
                    )}
                  </div>
                </div>
              </div>
              <Row className="mt-1">
                <Col
                  className="d-flex flex-column rounded shadow-sm m-2 px-2 pt-2 pointer border border-secondary"
                  onClick={() => this.setState({ modal: 'panel' })}
                >
                  {panel.manufacturer && (
                    <h5 className="mt-auto">
                      {`${panel.manufacturer.name
                        ? panel.manufacturer.name
                        : panel.manufacturer
                        } - ${panel.model}`}
                    </h5>
                  )}
                  Solar Panel
                </Col>
                <Col
                  className="d-flex flex-column rounded shadow-sm m-2 px-2 pt-2 pointer border border-secondary"
                  onClick={() => this.setState({ modal: 'mounting' })}
                >
                  {mounting && <h5 className="mt-auto">{mounting.model}</h5>}
                  Mounting Hardware
                </Col>
              </Row>
              <Row>
                <Col
                  className="d-flex flex-column rounded shadow-sm m-2 px-2 pt-2 pointer border border-secondary"
                  onClick={() => this.setState({ modal: 'inverter' })}
                >
                  {additionalInverters && additionalInverters.length > 0 ? (
                    <>
                      {panel && (
                        <h6 className="mt-auto">
                          {inverter &&
                            `${inverter.model}${(additionalInverters || [])
                              .map(
                                (inv) =>
                                  ` + ${inv?.quantity || 1}x ${inv?.model || 'Unknown'
                                  }`
                              )
                              .join('')}`}
                        </h6>
                      )}
                    </>
                  ) : (
                    <>
                      {panel && (
                        <h5 className="mt-auto">
                          {inverter && `${inverter.model}`}
                        </h5>
                      )}
                    </>
                  )}
                  Inverter
                </Col>
                <Col
                  className="d-flex flex-column rounded shadow-sm m-2 px-2 pt-2 pointer border border-secondary"
                  onClick={() => this.setState({ modal: 'monitoring' })}
                >
                  {monitoring && (
                    <h5 className="mt-auto">{monitoring.model}</h5>
                  )}
                  Monitoring System
                </Col>
              </Row>
            </Card>
          </Col>
          <Col md={5} className="mt-4 mt-lg-0">
            <Card className="shadow-sm h-100" body>
              <h5 className="font-weight-bold">Adders</h5>
              <Row xs={1} md={2} className="mt-1">
                {Object.entries(adders)
                  .filter(([_, { count }]) => count)
                  .map(([id, { name, count }]) => (
                    <Col
                      key={id}
                      className="ml-1 font-weight-bold h6 pointer"
                      onClick={() => this.deleteAdder(id)}
                    >
                      {count === 1 ? name : `${name} x ${count}`}
                    </Col>
                  ))}
              </Row>

              <Row className="mt-1">
                <Col>
                  <Button onClick={() => this.setState({ modal: 'adder' })}>
                    + Adders
                  </Button>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* === PAYBACK AND ELECTRICITY USAGE GRAPHS === */}
        <Row className="mt-1" noGutters>
          <Col lg={6}>
            <Card className="shadow-sm h-100 mr-1" body>
              <Row>
                <Col className="d-flex justify-content-between">
                  <h5 className="font-weight-bold">25 Years Savings</h5>
                  <PencilSquare
                    className="pointer"
                    onClick={() => this.setState({ modal: 'escalation' })}
                    size={24}
                  />
                </Col>
              </Row>
              <p className="small font-italic">
                {`Estimate is based on an annual ${escalation * 100
                  }% utility rate increase`}
              </p>
              <PaybackChart graphData={lifetimeSavings} />
            </Card>
          </Col>
          <Col lg={6} className="mt-4 mt-lg-0">
            <Card
              className="shadow-sm h-100"
              body
              id="card-average-electricity-usage"
            >
              <Row>
                <Col className="d-flex justify-content-between">
                  <h5 className="font-weight-bold">
                    Average Electricity Usage
                  </h5>
                  {/* <PencilSquare
                    className="pointer"
                    onClick={() => this.setState({ modal: 'utility' })}
                    size={24}
                  /> */}
                </Col>
              </Row>
              <SolarPowerChart
                annualProduction={monthlyProduction}
                annualUsage={lead.utility.usage}
              />
            </Card>
          </Col>
        </Row>

        {/* === LOAN CALCULATOR === */}
        {/* {loanSwitch && (
          <LoanReport
            loan={loan}
            rebates={rebateAmount}
            loanChangeHandler={this.loanChangeHandler}
            setLoanObj={this.setLoanObj}
          />
        )} */}

        {/* === FINANCES FOR YOUR SOLAR SYSTEM === */}
        <Row className="mt-1" noGutters>
          <Col md={7}>
            <Card className="shadow-sm h-100 mr-1" body>
              <h5 className="font-weight-bold">Financial Breakdown</h5>
              <Row>
                <Col sm={5} className="h-100">
                  <Card className="shadow-sm p-3 text-white bg-primary-600">
                    <h4 className="font-weight-bold">
                      {formatCurrency(yearlySavings[0])}
                    </h4>
                    <small>Estimated First Year Savings</small>
                    <hr className="font-weight-bold border border-white" />
                    <h4 className="font-weight-bold">
                      {formatCurrency(totalSavings)}
                    </h4>
                    <small>Estimated 25-Year Savings</small>
                    {!loanSwitch && (
                      <>
                        <hr className="font-weight-bold border border-white" />
                        <h4 className="font-weight-bold">
                          {Pluralize('year', paybackYear, true)}
                          ,
                          <br />
                          {Pluralize('month', paybackMonth, true)}.
                        </h4>
                        <small>Payback Period</small>
                      </>
                    )}
                  </Card>
                </Col>
                <Col sm={7} className="h-100">
                  <Row>
                    {/* <Col>
                      <Row className="px-4 d-flex justify-content-between">
                        System Cost
                        <strong>{formatCurrency(systemCost)}</strong>
                      </Row>

                      {permitCost ? (
                        <Row className="px-4 d-flex justify-content-between">
                          Permits
                          <strong>{`+ ${formatCurrency(permitCost)}`}</strong>
                        </Row>
                      ) : null}

                      {Object.entries(adders)
                        .filter(([_, { count }]) => count)
                        .map(([id, { name, count, totalCost: total }]) => (
                          <Row
                            key={id}
                            className="px-4 d-flex justify-content-between"
                          >
                            {count === 1 ? name : `${name} (x${count})`}
                            <strong>{`+ ${formatCurrency(total)}`}</strong>
                          </Row>
                        ))}

                      {credits.map(({ name, appliedAmount }) => (
                        <Row
                          key={name}
                          className="px-4 d-flex justify-content-between"
                        >
                          {name}
                          <strong>{`- ${formatCurrency(appliedAmount)}`}</strong>
                        </Row>
                      ))}

                      <Row className="px-4 d-flex justify-content-between">
                        Discount
                        <strong>- {formatCurrency(discountAmount)}</strong>
                      </Row>

                      <hr />

                      <Row className="px-4 d-flex justify-content-between">
                        Total Cost
                        <strong>{formatCurrency(totalCost)}</strong>
                      </Row>
                    </Col> */}
                    <Col>
                      {discountAmount > 0 ? (
                        <>
                          <Row className="px-4 d-flex justify-content-between">
                            System Cost
                            <strong>
                              {formatCurrency(
                                discountSystemCost - visibleAdderCost
                              )}
                            </strong>
                          </Row>

                          {Object.entries(adders)
                            .filter(([_, { count }]) => count)
                            .map(
                              ([id, el]) =>
                                !el?.hidden && (
                                  <Row className="px-4 d-flex justify-content-between">
                                    {el?.count === 1
                                      ? `${el?.name} ${el?.dq ? '(Rebate Exemption)' : ''
                                      }`
                                      : `${el?.name} x ${el?.count} ${el?.dq ? '(Rebate Exemption)' : ''
                                      }`}
                                    {el?.flat_cost ? (
                                      <strong>{`+ ${formatCurrency(
                                        Number(el?.count) * Number(el?.cost)
                                      )}`}</strong>
                                    ) : (
                                      <strong>{`+ ${formatCurrency(
                                        Number(el?.count) *
                                        Number(el?.cost) *
                                        Number(systemSize) *
                                        1000
                                      )}`}</strong>
                                    )}
                                  </Row>
                                )
                            )}

                          <Row className="px-4 d-flex justify-content-between">
                            Discount
                            <strong>{`- ${formatCurrency(
                              discountAmount
                            )}`}</strong>
                          </Row>

                          <Row className="px-4 d-flex justify-content-between">
                            Total Cost
                            <strong>
                              {formatCurrency(
                                discountSystemCost - discountAmount
                              )}
                            </strong>
                          </Row>

                          {credits.map(({ name, appliedAmount }) => (
                            <Row
                              key={name}
                              className="px-4 d-flex justify-content-between"
                            >
                              {name}
                              <strong>{`- ${formatCurrency(
                                appliedAmount
                              )}`}</strong>
                            </Row>
                          ))}

                          <hr />

                          <Row className="px-4 d-flex justify-content-between">
                            Net Cost
                            <strong>{formatCurrency(totalCost)}</strong>
                          </Row>
                        </>
                      ) : (
                        <>
                          <Row className="px-4 d-flex justify-content-between">
                            System Cost
                            <strong>
                              {formatCurrency(
                                discountSystemCost - visibleAdderCost
                              )}
                            </strong>
                          </Row>

                          {Object.entries(adders)
                            .filter(([_, { count }]) => count)
                            .map(
                              ([id, el]) =>
                                !el?.hidden && (
                                  <Row className="px-4 d-flex justify-content-between">
                                    {el?.count === 1
                                      ? `${el?.name} ${el?.dq ? '(Rebate Exemption)' : ''
                                      }`
                                      : `${el?.name} x ${el?.count} ${el?.dq ? '(Rebate Exemption)' : ''
                                      }`}
                                    {el?.flat_cost ? (
                                      <strong>{`+ ${formatCurrency(
                                        Number(el?.count) * Number(el?.cost)
                                      )}`}</strong>
                                    ) : (
                                      <strong>{`+ ${formatCurrency(
                                        Number(el?.count) *
                                        Number(el?.cost) *
                                        Number(systemSize) *
                                        1000
                                      )}`}</strong>
                                    )}
                                  </Row>
                                )
                            )}

                          <Row className="px-4 d-flex justify-content-between">
                            Total Cost
                            <strong>
                              {formatCurrency(discountSystemCost)}
                            </strong>
                          </Row>

                          {credits.map(({ name, appliedAmount }) => (
                            <Row
                              key={name}
                              className="px-4 d-flex justify-content-between"
                            >
                              {name}
                              <strong>{`- ${formatCurrency(
                                appliedAmount
                              )}`}</strong>
                            </Row>
                          ))}

                          <hr />

                          <Row className="px-4 d-flex justify-content-between">
                            Net Cost
                            <strong>{formatCurrency(totalCost)}</strong>
                          </Row>
                        </>
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col md={5} className="mt-4 mt-lg-0">
            <Card className="shadow-sm h-100" body>
              <h5 className="font-weight-bold">Warranty</h5>
              <p className="mt-1">{warranty}</p>
            </Card>
          </Col>
        </Row>

        {/* === SHADE REPORT === */}
        {/* this.state.design && this.state.panel && (
          <ShadeReport
            wattage={this.state.panel.power}
            panels={this.state.design}
          />
        ) */}

        {/* === DISCOUNT COST MODAL === */}
        <DiscountModal
          show={modal === 'discount'}
          onHide={this.hideModal}
          pid={pid}
          amount={discountAmount}
          applyDiscount={this.discountApplied}
          isPreparedToCalc={
            Number(designData?.summary?.discount_system_cost) > 0 ||
            Number(discountSystemCost) > 0
          }
        />

        {/* === SOLAR PANELS MODAL === */}
        <SelectModal
          title="Solar Panel"
          show={modal === 'panel'}
          selectValue={panel?.id || ''}
          selectOptions={hardware.panels
            .filter(
              ({ width, length }) =>
                width === panel.width && length === panel.length
            )
            .map(({ id, manufacturer, model }) => ({
              id,
              label: `${manufacturer.name}-${model}`,
            }))}
          onHide={this.hideModal}
          onSave={this.solarPanelChangeHandler}
        />

        {/* === MOUNTING HARDWARE MODAL === */}
        <SelectModal
          title="Mounting Hardware"
          show={modal === 'mounting'}
          selectValue={mounting?.id || ''}
          selectOptions={hardware.mounting.map(({ id, model }) => ({
            id,
            label: model,
          }))}
          onHide={this.hideModal}
          onSave={this.mountingChangeHandler}
        />

        {/* === INVERTER MODAL === */}
        <SelectModal
          title="Inverter"
          show={modal === 'inverter'}
          selectValue={inverter?.id || ''}
          selectQuantity={inverterQuantity || 1}
          selectedPanels={selectedPanels}
          panel={panel}
          additionalInverters={(additionalInverters || [])
            .map((inv) => this.normalizeAdditionalInverter(inv))
            .filter(Boolean)
            .map((inv) => ({
              inverter_id: getAdditionalInverterId(inv),
              quantity: inv?.quantity || 1,
              autoQuantity: inv?.autoQuantity !== false,
            }))}
          selectOptions={hardware.inverters.map(
            ({ id, model, type, capacity }) => ({
              id,
              label: model,
              type,
              capacity,
            })
          )}
          nullOption={null}
          disabled={false}
          hasAutoCalculatedInverterQuantity={hasAutoCalculatedInverterQuantity}
          onQuantityManuallyChanged={() =>
            this.setState(buildReportInverterAutoModePatch(false))
          }
          onResetToAutoMode={() =>
            this.setState(buildReportInverterAutoModePatch(true))
          }
          onHide={this.hideModal}
          onSave={this.invertersChangeHandler}
        />

        {/* === MONITORING SYSTEM MODAL === */}
        <SelectModal
          title="Monitoring System"
          show={modal === 'monitoring'}
          selectValue={monitoring?.id || ''}
          selectOptions={hardware.monitoring.map(({ id, model }) => ({
            id,
            label: model,
          }))}
          nullOption="No Monitoring System"
          onHide={this.hideModal}
          onSave={this.monitoringChangeHandler}
        />

        {/* === ADDERS MODAL === */}
        <AddersModal
          show={modal === 'adder'}
          onHide={this.hideModal}
          current={adders}
          options={hardware.adders}
          onSave={this.addersUpdateHandler}
        />

        {/* === UTILITY ESCALATION MODAL === */}
        <EscalationModal
          show={modal === 'escalation'}
          escalation={escalation * 100}
          onHide={this.hideModal}
          onSave={this.utilityEscalationHandler}
        />

        {/* === UTILITY RATE MODAL === */}
        {/* <UtilityModal
          show={modal === 'utility'}
          onHide={this.hideModal}
          utility={lead.utility}
          // monthlyBill={this.state.monthlyBill}
          // monthlyBillHandler={this.monthlyBillHandler}
        /> */}

        <ShadingReportModal
          show={modal === 'shading-report'}
          onHide={this.hideModal}
          shadingReportData={shadingReportData}
          exportPDFShadingReport={this.downloadShadingReport}
          monthlyProduction={monthlyProduction}
          losses={losses}
          snowLoss={snowLoss}
          inverter={inverter}
          exporting={exporting}
          iframe={iframe}
        />

        <p id="footnote" className="mt-5 font-italic">
          * The savings, production and payback period in this Custom Solar
          Design is an estimate only.
        </p>

        {(this.props.lead.utility.billing_type === 'net_billing' ||
          this.props.lead.utility.billing_type === 'net_metering' ||
          (!this.props.lead.utility.billing_type &&
            SHOW_LEGACY_SAVINGS_DEBUG_TABLE)) && (
            <div className="mt-4">
              <Card className="shadow-sm">
                <Card.Header>
                  <h5 className="mb-0">
                    {this.props.lead.utility.billing_type === 'net_billing'
                      ? 'Net Billing Analysis'
                      : this.props.lead.utility.billing_type === 'net_metering'
                        ? this.props.lead.utility.net_metering_specs
                          ?.metering_period === 'monthly'
                          ? 'Monthly Net Metering Analysis'
                          : 'Annual Net Metering Analysis'
                        : 'Effective-Rate Savings Analysis (no billing type)'}
                  </h5>
                </Card.Header>
                <Card.Body>
                  {monthlyProduction && systemSize ? (
                    this.props.lead.utility.billing_type === 'net_billing' ? (
                      <NetBillingTable
                        calculationResults={calculateNetBillingV2({
                          monthlyProduction,
                          monthlyConsumption: Array.isArray(
                            this.props.lead.utility.usage
                          )
                            ? this.props.lead.utility.usage
                            : new Array(12).fill(
                              this.props.lead.utility.totalUsage / 12
                            ),
                          systemSize,
                          utility: this.props.lead.utility,
                          panel: this.state.panel,
                          escalation: this.state.escalation,
                        })}
                      />
                    ) : this.props.lead.utility.billing_type ===
                      'net_metering' ? (
                      this.props.lead.utility.net_metering_specs
                        ?.metering_period === 'monthly' ? (
                        <MonthlyNetMeteringTable
                          calculationResults={calculateMonthlyNetMeteringV2({
                            monthlyProduction,
                            monthlyConsumption: Array.isArray(
                              this.props.lead.utility.usage
                            )
                              ? this.props.lead.utility.usage
                              : new Array(12).fill(
                                this.props.lead.utility.totalUsage / 12
                              ),
                            systemSize,
                            utility: this.props.lead.utility,
                            panel: this.state.panel,
                            escalation: this.state.escalation,
                          })}
                        />
                      ) : this.props.lead.utility.net_metering_specs
                        ?.metering_period === 'annual' ? (
                        <AnnualNetMeteringTable
                          calculationResults={calculateAnnualNetMeteringV2({
                            monthlyProduction,
                            monthlyConsumption: Array.isArray(
                              this.props.lead.utility.usage
                            )
                              ? this.props.lead.utility.usage
                              : new Array(12).fill(
                                this.props.lead.utility.totalUsage / 12
                              ),
                            systemSize,
                            utility: this.props.lead.utility,
                            panel: this.state.panel,
                            escalation: this.state.escalation,
                          })}
                        />
                      ) : null
                    ) : (
                      <LegacySavingsTable
                        calculationResults={
                          this.state.netMeteringCalculations ||
                          calculateLegacySavingsV2({
                            monthlyProduction,
                            monthlyConsumption: Array.isArray(
                              this.props.lead.utility.usage
                            )
                              ? this.props.lead.utility.usage
                              : new Array(12).fill(
                                this.props.lead.utility.totalUsage / 12
                              ),
                            systemSize,
                            utility: this.props.lead.utility,
                            panel: this.state.panel,
                            escalation: this.state.escalation,
                          })
                        }
                      />
                    )
                  ) : (
                    <div className="alert alert-info">
                      {this.state.needsSolarProductionRecalc
                        ? 'Production data is not available for this proposal. Recalculate production to generate results.'
                        : 'Loading production data...'}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          )}
        <Modal
          show={Boolean(this.state.saveAsOpen)}
          onHide={this.closeSaveAsProposal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Save as</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Control
              value={this.state.saveAsName || ''}
              autoFocus
              placeholder="Proposal name"
              onChange={(event) => this.setState({ saveAsName: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  this.submitSaveAsProposal();
                }
              }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={this.closeSaveAsProposal}
              disabled={this.state.proposalActionBusy}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={this.submitSaveAsProposal}
              disabled={
                this.state.proposalActionBusy ||
                !String(this.state.saveAsName || '').trim()
              }
            >
              Save as
            </Button>
          </Modal.Footer>
        </Modal>
        <Modal
          show={Boolean(this.state.renameOpen)}
          onHide={this.closeRenameProposal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Rename</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Control
              value={this.state.renameName || ''}
              autoFocus
              onChange={(event) => this.setState({ renameName: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  this.submitRenameProposal();
                }
              }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={this.closeRenameProposal}
              disabled={this.state.proposalActionBusy}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={this.submitRenameProposal}
              disabled={
                this.state.proposalActionBusy ||
                !String(this.state.renameName || '').trim()
              }
            >
              Save
            </Button>
          </Modal.Footer>
        </Modal>
        <Modal
          show={Boolean(this.state.archiveOpen)}
          onHide={this.closeArchiveProposal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Archive</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Archive {this.proposalNameById(this.proposalTargetId()) || 'this item'}?
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={this.closeArchiveProposal}
              disabled={this.state.proposalActionBusy}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={this.submitArchiveProposal}
              disabled={this.state.proposalActionBusy}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>
        <Modal
          show={Boolean(this.state.switchConfirm)}
          onHide={() => this.setState({ switchConfirm: null })}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Unsaved changes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-0">{UNSAVED_CHANGES_GUARD_MESSAGE}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => this.setState({ switchConfirm: null })}
            >
              Stay
            </Button>
            <Button variant="warning" onClick={this.confirmProposalSwitch}>
              Discard and switch
            </Button>
          </Modal.Footer>
        </Modal>
      </Col>
    );
  }
}

Report.defaultProps = {
  callback: null,
};

Report.propTypes = {
  pid: PropTypes.string.isRequired,
  API: PropTypes.instanceOf(Object).isRequired,
  hardware: PropTypes.shape({
    adders: PropTypes.instanceOf(Array),
    credits: PropTypes.instanceOf(Array),
    inverters: PropTypes.instanceOf(Array),
    monitoring: PropTypes.instanceOf(Array),
    mounting: PropTypes.instanceOf(Array),
    packages: PropTypes.instanceOf(Array),
    panels: PropTypes.instanceOf(Array),
    permits: PropTypes.instanceOf(Array),
    roof_types: PropTypes.instanceOf(Array),
    warranty: PropTypes.string,
  }).isRequired,
  currentUser: PropTypes.shape({
    commission: PropTypes.string,
    discount: PropTypes.string,
  }).isRequired,
  fullAddress: PropTypes.string.isRequired,
  lead: PropTypes.shape({
    property: PropTypes.instanceOf(Object),
    utility: PropTypes.instanceOf(Object),
    contact: PropTypes.instanceOf(Object),
    address: PropTypes.instanceOf(Object),
  }).isRequired,
  validUtility: PropTypes.bool.isRequired,
  callback: PropTypes.func,
  proposalList: PropTypes.instanceOf(Array),
  onNavigateProposal: PropTypes.func,
  onDuplicateProposal: PropTypes.func,
  onCloneProposal: PropTypes.func,
  onRenameProposal: PropTypes.func,
  onArchiveProposal: PropTypes.func,
  displaySnack: PropTypes.func,
};

export default Report;
